import {meta as classesMeta} from "@/wab/shared/model/classes-metas";
import {Field, ModelInst} from "@/wab/shared/model/model-meta";
import {assert, ensure, mkUnexpectedTypeMsg} from "@/wab/shared/common";
import type { ReactElement } from "react";

// The accessors use a module-local binding of meta, because bundlers compile
// an imported binding to a getter call on every access
const meta = classesMeta;

type KnownType = KnownPrimitiveType | KnownImg | KnownComponentInstance | KnownPlumeInstance | KnownRenderableType | KnownHrefType | KnownTargetType | KnownRenderFuncType | KnownQueryData | KnownDateString | KnownDateRangeStrings | KnownFunctionType | KnownArgType | KnownStylePropType;
export function isKnownType<T>(x: [Extract<T, Type>] extends [never] ? never : T): x is [Extract<T, Type>] extends [never]
  ? never
  : Type extends T // Needed when T is any
  ? Type
  : Extract<T, Type> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseType;
}

    export function ensureKnownType<T>(x: [Extract<T, Type>] extends [never] ? never : T): Type {
      assert(isKnownType(x), () => mkUnexpectedTypeMsg([Type], x));
      return x;
    }
    export function ensureMaybeKnownType<T>(x: [Extract<T, Type>] extends [never] ? never : T): Type | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownType(x as any);
    }
    
export type Type = KnownType;
export interface TypeParams {
  
}

abstract class BaseType extends ModelInst {
  
    static isKnown(x: any): x is Type { return isKnownType(x); }
    static getType(): Type { throw new Error(); }
    static modelTypeName = "Type";
    
  static readonly $fields = meta.allFields(meta.clsByName["Type"]);
  
  
}

export const Type = BaseType;

type KnownPrimitiveType = KnownScalar | KnownAnyType | KnownChoice | KnownMultiChoice;
export function isKnownPrimitiveType<T>(x: [Extract<T, PrimitiveType>] extends [never] ? never : T): x is [Extract<T, PrimitiveType>] extends [never]
  ? never
  : PrimitiveType extends T // Needed when T is any
  ? PrimitiveType
  : Extract<T, PrimitiveType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePrimitiveType;
}

    export function ensureKnownPrimitiveType<T>(x: [Extract<T, PrimitiveType>] extends [never] ? never : T): PrimitiveType {
      assert(isKnownPrimitiveType(x), () => mkUnexpectedTypeMsg([PrimitiveType], x));
      return x;
    }
    export function ensureMaybeKnownPrimitiveType<T>(x: [Extract<T, PrimitiveType>] extends [never] ? never : T): PrimitiveType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPrimitiveType(x as any);
    }
    
export type PrimitiveType = KnownPrimitiveType;
export interface PrimitiveTypeParams {
  
}

abstract class BasePrimitiveType extends BaseType {
  
    static isKnown(x: any): x is PrimitiveType { return isKnownPrimitiveType(x); }
    static getType(): PrimitiveType { throw new Error(); }
    static modelTypeName = "PrimitiveType";
    
  static readonly $fields = meta.allFields(meta.clsByName["PrimitiveType"]);
  
  
}

export const PrimitiveType = BasePrimitiveType;

type KnownScalar = KnownNum | KnownText | KnownBoolType;
export function isKnownScalar<T>(x: [Extract<T, Scalar>] extends [never] ? never : T): x is [Extract<T, Scalar>] extends [never]
  ? never
  : Scalar extends T // Needed when T is any
  ? Scalar
  : Extract<T, Scalar> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseScalar;
}

    export function ensureKnownScalar<T>(x: [Extract<T, Scalar>] extends [never] ? never : T): Scalar {
      assert(isKnownScalar(x), () => mkUnexpectedTypeMsg([Scalar], x));
      return x;
    }
    export function ensureMaybeKnownScalar<T>(x: [Extract<T, Scalar>] extends [never] ? never : T): Scalar | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownScalar(x as any);
    }
    
export type Scalar = KnownScalar;
export interface ScalarParams {
  
}

abstract class BaseScalar extends BasePrimitiveType {
  
    static isKnown(x: any): x is Scalar { return isKnownScalar(x); }
    static getType(): Scalar { throw new Error(); }
    static modelTypeName = "Scalar";
    
  static readonly $fields = meta.allFields(meta.clsByName["Scalar"]);
  
  
}

export const Scalar = BaseScalar;

type KnownNum = ClsNum;
export function isKnownNum<T>(x: [Extract<T, Num>] extends [never] ? never : T): x is [Extract<T, Num>] extends [never]
  ? never
  : Num extends T // Needed when T is any
  ? Num
  : Extract<T, Num> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseNum;
}

    export function ensureKnownNum<T>(x: [Extract<T, Num>] extends [never] ? never : T): Num {
      assert(isKnownNum(x), () => mkUnexpectedTypeMsg([Num], x));
      return x;
    }
    export function ensureMaybeKnownNum<T>(x: [Extract<T, Num>] extends [never] ? never : T): Num | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownNum(x as any);
    }
    
export type Num = KnownNum;
export interface NumParams {
  readonly name: "num" /* Const */;
}

abstract class BaseNum extends BaseScalar {
  
    static isKnown(x: any): x is Num { return isKnownNum(x); }
    static getType(): Num { throw new Error(); }
    static modelTypeName = "Num";
    
  static readonly $fields = meta.allFields(meta.clsByName["Num"]);
  static readonly $field_name: Field = ensure(
    BaseNum.$fields.find((f) => f.name === "name"),
    "Num has no field name",
  );
  /* Const */
get name(): "num" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "num") { meta.writeField(this, BaseNum.$field_name, value); }
}
class ClsNum extends BaseNum {
  constructor(args: NumParams) {
    super();
    meta.initializers.Num(this, args);
  }
  get typeTag(): "Num" {
        return "Num";
      }
}
export const Num = ClsNum;

type KnownText = ClsText;
export function isKnownText<T>(x: [Extract<T, Text>] extends [never] ? never : T): x is [Extract<T, Text>] extends [never]
  ? never
  : Text extends T // Needed when T is any
  ? Text
  : Extract<T, Text> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseText;
}

    export function ensureKnownText<T>(x: [Extract<T, Text>] extends [never] ? never : T): Text {
      assert(isKnownText(x), () => mkUnexpectedTypeMsg([Text], x));
      return x;
    }
    export function ensureMaybeKnownText<T>(x: [Extract<T, Text>] extends [never] ? never : T): Text | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownText(x as any);
    }
    
export type Text = KnownText;
export interface TextParams {
  readonly name: "text" /* Const */;
}

abstract class BaseText extends BaseScalar {
  
    static isKnown(x: any): x is Text { return isKnownText(x); }
    static getType(): Text { throw new Error(); }
    static modelTypeName = "Text";
    
  static readonly $fields = meta.allFields(meta.clsByName["Text"]);
  static readonly $field_name: Field = ensure(
    BaseText.$fields.find((f) => f.name === "name"),
    "Text has no field name",
  );
  /* Const */
get name(): "text" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "text") { meta.writeField(this, BaseText.$field_name, value); }
}
class ClsText extends BaseText {
  constructor(args: TextParams) {
    super();
    meta.initializers.Text(this, args);
  }
  get typeTag(): "Text" {
        return "Text";
      }
}
export const Text = ClsText;

type KnownBoolType = ClsBoolType;
export function isKnownBoolType<T>(x: [Extract<T, BoolType>] extends [never] ? never : T): x is [Extract<T, BoolType>] extends [never]
  ? never
  : BoolType extends T // Needed when T is any
  ? BoolType
  : Extract<T, BoolType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseBoolType;
}

    export function ensureKnownBoolType<T>(x: [Extract<T, BoolType>] extends [never] ? never : T): BoolType {
      assert(isKnownBoolType(x), () => mkUnexpectedTypeMsg([BoolType], x));
      return x;
    }
    export function ensureMaybeKnownBoolType<T>(x: [Extract<T, BoolType>] extends [never] ? never : T): BoolType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownBoolType(x as any);
    }
    
export type BoolType = KnownBoolType;
export interface BoolTypeParams {
  readonly name: "bool" /* Const */;
}

abstract class BaseBoolType extends BaseScalar {
  
    static isKnown(x: any): x is BoolType { return isKnownBoolType(x); }
    static getType(): BoolType { throw new Error(); }
    static modelTypeName = "BoolType";
    
  static readonly $fields = meta.allFields(meta.clsByName["BoolType"]);
  static readonly $field_name: Field = ensure(
    BaseBoolType.$fields.find((f) => f.name === "name"),
    "BoolType has no field name",
  );
  /* Const */
get name(): "bool" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "bool") { meta.writeField(this, BaseBoolType.$field_name, value); }
}
class ClsBoolType extends BaseBoolType {
  constructor(args: BoolTypeParams) {
    super();
    meta.initializers.BoolType(this, args);
  }
  get typeTag(): "BoolType" {
        return "BoolType";
      }
}
export const BoolType = ClsBoolType;

type KnownAnyType = ClsAnyType;
export function isKnownAnyType<T>(x: [Extract<T, AnyType>] extends [never] ? never : T): x is [Extract<T, AnyType>] extends [never]
  ? never
  : AnyType extends T // Needed when T is any
  ? AnyType
  : Extract<T, AnyType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseAnyType;
}

    export function ensureKnownAnyType<T>(x: [Extract<T, AnyType>] extends [never] ? never : T): AnyType {
      assert(isKnownAnyType(x), () => mkUnexpectedTypeMsg([AnyType], x));
      return x;
    }
    export function ensureMaybeKnownAnyType<T>(x: [Extract<T, AnyType>] extends [never] ? never : T): AnyType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownAnyType(x as any);
    }
    
export type AnyType = KnownAnyType;
export interface AnyTypeParams {
  readonly name: "any" /* Const */;
}

abstract class BaseAnyType extends BasePrimitiveType {
  
    static isKnown(x: any): x is AnyType { return isKnownAnyType(x); }
    static getType(): AnyType { throw new Error(); }
    static modelTypeName = "AnyType";
    
  static readonly $fields = meta.allFields(meta.clsByName["AnyType"]);
  static readonly $field_name: Field = ensure(
    BaseAnyType.$fields.find((f) => f.name === "name"),
    "AnyType has no field name",
  );
  /* Const */
get name(): "any" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "any") { meta.writeField(this, BaseAnyType.$field_name, value); }
}
class ClsAnyType extends BaseAnyType {
  constructor(args: AnyTypeParams) {
    super();
    meta.initializers.AnyType(this, args);
  }
  get typeTag(): "AnyType" {
        return "AnyType";
      }
}
export const AnyType = ClsAnyType;

type KnownChoice = ClsChoice;
export function isKnownChoice<T>(x: [Extract<T, Choice>] extends [never] ? never : T): x is [Extract<T, Choice>] extends [never]
  ? never
  : Choice extends T // Needed when T is any
  ? Choice
  : Extract<T, Choice> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseChoice;
}

    export function ensureKnownChoice<T>(x: [Extract<T, Choice>] extends [never] ? never : T): Choice {
      assert(isKnownChoice(x), () => mkUnexpectedTypeMsg([Choice], x));
      return x;
    }
    export function ensureMaybeKnownChoice<T>(x: [Extract<T, Choice>] extends [never] ? never : T): Choice | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownChoice(x as any);
    }
    
export type Choice = KnownChoice;
export interface ChoiceParams {
  readonly name: "choice" /* Const */;
options: ReadonlyArray<string | number | boolean> | ReadonlyArray<Readonly<Record<string, string | number | boolean>>> /*  */;
}

abstract class BaseChoice extends BasePrimitiveType {
  
    static isKnown(x: any): x is Choice { return isKnownChoice(x); }
    static getType(): Choice { throw new Error(); }
    static modelTypeName = "Choice";
    
  static readonly $fields = meta.allFields(meta.clsByName["Choice"]);
  static readonly $field_name: Field = ensure(
    BaseChoice.$fields.find((f) => f.name === "name"),
    "Choice has no field name",
  );
static readonly $field_options: Field = ensure(
    BaseChoice.$fields.find((f) => f.name === "options"),
    "Choice has no field options",
  );
  /* Const */
get name(): "choice" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "choice") { meta.writeField(this, BaseChoice.$field_name, value); }
get options(): ReadonlyArray<string | number | boolean> | ReadonlyArray<Readonly<Record<string, string | number | boolean>>> { meta.reportRead(this, "options"); return (this as any).$v.options; }
set options(value: ReadonlyArray<string | number | boolean> | ReadonlyArray<Readonly<Record<string, string | number | boolean>>>) { meta.writeField(this, BaseChoice.$field_options, value); }
}
class ClsChoice extends BaseChoice {
  constructor(args: ChoiceParams) {
    super();
    meta.initializers.Choice(this, args);
  }
  get typeTag(): "Choice" {
        return "Choice";
      }
}
export const Choice = ClsChoice;

type KnownMultiChoice = ClsMultiChoice;
export function isKnownMultiChoice<T>(x: [Extract<T, MultiChoice>] extends [never] ? never : T): x is [Extract<T, MultiChoice>] extends [never]
  ? never
  : MultiChoice extends T // Needed when T is any
  ? MultiChoice
  : Extract<T, MultiChoice> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseMultiChoice;
}

    export function ensureKnownMultiChoice<T>(x: [Extract<T, MultiChoice>] extends [never] ? never : T): MultiChoice {
      assert(isKnownMultiChoice(x), () => mkUnexpectedTypeMsg([MultiChoice], x));
      return x;
    }
    export function ensureMaybeKnownMultiChoice<T>(x: [Extract<T, MultiChoice>] extends [never] ? never : T): MultiChoice | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownMultiChoice(x as any);
    }
    
export type MultiChoice = KnownMultiChoice;
export interface MultiChoiceParams {
  readonly name: "multiChoice" /* Const */;
options: ReadonlyArray<string | number | boolean> | ReadonlyArray<Readonly<Record<string, string | number | boolean>>> /*  */;
}

abstract class BaseMultiChoice extends BasePrimitiveType {
  
    static isKnown(x: any): x is MultiChoice { return isKnownMultiChoice(x); }
    static getType(): MultiChoice { throw new Error(); }
    static modelTypeName = "MultiChoice";
    
  static readonly $fields = meta.allFields(meta.clsByName["MultiChoice"]);
  static readonly $field_name: Field = ensure(
    BaseMultiChoice.$fields.find((f) => f.name === "name"),
    "MultiChoice has no field name",
  );
static readonly $field_options: Field = ensure(
    BaseMultiChoice.$fields.find((f) => f.name === "options"),
    "MultiChoice has no field options",
  );
  /* Const */
get name(): "multiChoice" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "multiChoice") { meta.writeField(this, BaseMultiChoice.$field_name, value); }
get options(): ReadonlyArray<string | number | boolean> | ReadonlyArray<Readonly<Record<string, string | number | boolean>>> { meta.reportRead(this, "options"); return (this as any).$v.options; }
set options(value: ReadonlyArray<string | number | boolean> | ReadonlyArray<Readonly<Record<string, string | number | boolean>>>) { meta.writeField(this, BaseMultiChoice.$field_options, value); }
}
class ClsMultiChoice extends BaseMultiChoice {
  constructor(args: MultiChoiceParams) {
    super();
    meta.initializers.MultiChoice(this, args);
  }
  get typeTag(): "MultiChoice" {
        return "MultiChoice";
      }
}
export const MultiChoice = ClsMultiChoice;

type KnownImg = ClsImg;
export function isKnownImg<T>(x: [Extract<T, Img>] extends [never] ? never : T): x is [Extract<T, Img>] extends [never]
  ? never
  : Img extends T // Needed when T is any
  ? Img
  : Extract<T, Img> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseImg;
}

    export function ensureKnownImg<T>(x: [Extract<T, Img>] extends [never] ? never : T): Img {
      assert(isKnownImg(x), () => mkUnexpectedTypeMsg([Img], x));
      return x;
    }
    export function ensureMaybeKnownImg<T>(x: [Extract<T, Img>] extends [never] ? never : T): Img | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownImg(x as any);
    }
    
export type Img = KnownImg;
export interface ImgParams {
  readonly name: "img" /* Const */;
}

abstract class BaseImg extends BaseType {
  
    static isKnown(x: any): x is Img { return isKnownImg(x); }
    static getType(): Img { throw new Error(); }
    static modelTypeName = "Img";
    
  static readonly $fields = meta.allFields(meta.clsByName["Img"]);
  static readonly $field_name: Field = ensure(
    BaseImg.$fields.find((f) => f.name === "name"),
    "Img has no field name",
  );
  /* Const */
get name(): "img" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "img") { meta.writeField(this, BaseImg.$field_name, value); }
}
class ClsImg extends BaseImg {
  constructor(args: ImgParams) {
    super();
    meta.initializers.Img(this, args);
  }
  get typeTag(): "Img" {
        return "Img";
      }
}
export const Img = ClsImg;

type KnownComponentInstance = ClsComponentInstance;
export function isKnownComponentInstance<T>(x: [Extract<T, ComponentInstance>] extends [never] ? never : T): x is [Extract<T, ComponentInstance>] extends [never]
  ? never
  : ComponentInstance extends T // Needed when T is any
  ? ComponentInstance
  : Extract<T, ComponentInstance> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentInstance;
}

    export function ensureKnownComponentInstance<T>(x: [Extract<T, ComponentInstance>] extends [never] ? never : T): ComponentInstance {
      assert(isKnownComponentInstance(x), () => mkUnexpectedTypeMsg([ComponentInstance], x));
      return x;
    }
    export function ensureMaybeKnownComponentInstance<T>(x: [Extract<T, ComponentInstance>] extends [never] ? never : T): ComponentInstance | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentInstance(x as any);
    }
    
export type ComponentInstance = KnownComponentInstance;
export interface ComponentInstanceParams {
  readonly name: "instance" /* Const */;
component: Component /* WeakRef */;
}

abstract class BaseComponentInstance extends BaseType {
  
    static isKnown(x: any): x is ComponentInstance { return isKnownComponentInstance(x); }
    static getType(): ComponentInstance { throw new Error(); }
    static modelTypeName = "ComponentInstance";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentInstance"]);
  static readonly $field_name: Field = ensure(
    BaseComponentInstance.$fields.find((f) => f.name === "name"),
    "ComponentInstance has no field name",
  );
static readonly $field_component: Field = ensure(
    BaseComponentInstance.$fields.find((f) => f.name === "component"),
    "ComponentInstance has no field component",
  );
  /* Const */
get name(): "instance" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "instance") { meta.writeField(this, BaseComponentInstance.$field_name, value); }
/* WeakRef */
get component(): Component { meta.reportRead(this, "component"); return (this as any).$v.component; }
set component(value: Component) { meta.writeField(this, BaseComponentInstance.$field_component, value); }
}
class ClsComponentInstance extends BaseComponentInstance {
  constructor(args: ComponentInstanceParams) {
    super();
    meta.initializers.ComponentInstance(this, args);
  }
  get typeTag(): "ComponentInstance" {
        return "ComponentInstance";
      }
}
export const ComponentInstance = ClsComponentInstance;

type KnownPlumeInstance = ClsPlumeInstance;
export function isKnownPlumeInstance<T>(x: [Extract<T, PlumeInstance>] extends [never] ? never : T): x is [Extract<T, PlumeInstance>] extends [never]
  ? never
  : PlumeInstance extends T // Needed when T is any
  ? PlumeInstance
  : Extract<T, PlumeInstance> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePlumeInstance;
}

    export function ensureKnownPlumeInstance<T>(x: [Extract<T, PlumeInstance>] extends [never] ? never : T): PlumeInstance {
      assert(isKnownPlumeInstance(x), () => mkUnexpectedTypeMsg([PlumeInstance], x));
      return x;
    }
    export function ensureMaybeKnownPlumeInstance<T>(x: [Extract<T, PlumeInstance>] extends [never] ? never : T): PlumeInstance | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPlumeInstance(x as any);
    }
    
export type PlumeInstance = KnownPlumeInstance;
export interface PlumeInstanceParams {
  readonly name: "plumeInstance" /* Const */;
plumeType: string /*  */;
}

abstract class BasePlumeInstance extends BaseType {
  
    static isKnown(x: any): x is PlumeInstance { return isKnownPlumeInstance(x); }
    static getType(): PlumeInstance { throw new Error(); }
    static modelTypeName = "PlumeInstance";
    
  static readonly $fields = meta.allFields(meta.clsByName["PlumeInstance"]);
  static readonly $field_name: Field = ensure(
    BasePlumeInstance.$fields.find((f) => f.name === "name"),
    "PlumeInstance has no field name",
  );
static readonly $field_plumeType: Field = ensure(
    BasePlumeInstance.$fields.find((f) => f.name === "plumeType"),
    "PlumeInstance has no field plumeType",
  );
  /* Const */
get name(): "plumeInstance" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "plumeInstance") { meta.writeField(this, BasePlumeInstance.$field_name, value); }
get plumeType(): string { meta.reportRead(this, "plumeType"); return (this as any).$v.plumeType; }
set plumeType(value: string) { meta.writeField(this, BasePlumeInstance.$field_plumeType, value); }
}
class ClsPlumeInstance extends BasePlumeInstance {
  constructor(args: PlumeInstanceParams) {
    super();
    meta.initializers.PlumeInstance(this, args);
  }
  get typeTag(): "PlumeInstance" {
        return "PlumeInstance";
      }
}
export const PlumeInstance = ClsPlumeInstance;

type KnownRenderableType = ClsRenderableType;
export function isKnownRenderableType<T>(x: [Extract<T, RenderableType>] extends [never] ? never : T): x is [Extract<T, RenderableType>] extends [never]
  ? never
  : RenderableType extends T // Needed when T is any
  ? RenderableType
  : Extract<T, RenderableType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRenderableType;
}

    export function ensureKnownRenderableType<T>(x: [Extract<T, RenderableType>] extends [never] ? never : T): RenderableType {
      assert(isKnownRenderableType(x), () => mkUnexpectedTypeMsg([RenderableType], x));
      return x;
    }
    export function ensureMaybeKnownRenderableType<T>(x: [Extract<T, RenderableType>] extends [never] ? never : T): RenderableType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRenderableType(x as any);
    }
    
export type RenderableType = KnownRenderableType;
export interface RenderableTypeParams {
  readonly name: "renderable" /* Const */;
params: ReadonlyArray<PlumeInstance | ComponentInstance> /*  */;
allowRootWrapper: boolean | null | undefined /*  */;
}

abstract class BaseRenderableType extends BaseType {
  
    static isKnown(x: any): x is RenderableType { return isKnownRenderableType(x); }
    static getType(): RenderableType { throw new Error(); }
    static modelTypeName = "RenderableType";
    
  static readonly $fields = meta.allFields(meta.clsByName["RenderableType"]);
  static readonly $field_name: Field = ensure(
    BaseRenderableType.$fields.find((f) => f.name === "name"),
    "RenderableType has no field name",
  );
static readonly $field_params: Field = ensure(
    BaseRenderableType.$fields.find((f) => f.name === "params"),
    "RenderableType has no field params",
  );
static readonly $field_allowRootWrapper: Field = ensure(
    BaseRenderableType.$fields.find((f) => f.name === "allowRootWrapper"),
    "RenderableType has no field allowRootWrapper",
  );
  /* Const */
get name(): "renderable" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "renderable") { meta.writeField(this, BaseRenderableType.$field_name, value); }
get params(): ReadonlyArray<PlumeInstance | ComponentInstance> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: ReadonlyArray<PlumeInstance | ComponentInstance>) { meta.writeField(this, BaseRenderableType.$field_params, value); }
get allowRootWrapper(): boolean | null | undefined { meta.reportRead(this, "allowRootWrapper"); return (this as any).$v.allowRootWrapper; }
set allowRootWrapper(value: boolean | null | undefined) { meta.writeField(this, BaseRenderableType.$field_allowRootWrapper, value); }
}
class ClsRenderableType extends BaseRenderableType {
  constructor(args: RenderableTypeParams) {
    super();
    meta.initializers.RenderableType(this, args);
  }
  get typeTag(): "RenderableType" {
        return "RenderableType";
      }
}
export const RenderableType = ClsRenderableType;

type KnownHrefType = ClsHrefType;
export function isKnownHrefType<T>(x: [Extract<T, HrefType>] extends [never] ? never : T): x is [Extract<T, HrefType>] extends [never]
  ? never
  : HrefType extends T // Needed when T is any
  ? HrefType
  : Extract<T, HrefType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseHrefType;
}

    export function ensureKnownHrefType<T>(x: [Extract<T, HrefType>] extends [never] ? never : T): HrefType {
      assert(isKnownHrefType(x), () => mkUnexpectedTypeMsg([HrefType], x));
      return x;
    }
    export function ensureMaybeKnownHrefType<T>(x: [Extract<T, HrefType>] extends [never] ? never : T): HrefType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownHrefType(x as any);
    }
    
export type HrefType = KnownHrefType;
export interface HrefTypeParams {
  readonly name: "href" /* Const */;
}

abstract class BaseHrefType extends BaseType {
  
    static isKnown(x: any): x is HrefType { return isKnownHrefType(x); }
    static getType(): HrefType { throw new Error(); }
    static modelTypeName = "HrefType";
    
  static readonly $fields = meta.allFields(meta.clsByName["HrefType"]);
  static readonly $field_name: Field = ensure(
    BaseHrefType.$fields.find((f) => f.name === "name"),
    "HrefType has no field name",
  );
  /* Const */
get name(): "href" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "href") { meta.writeField(this, BaseHrefType.$field_name, value); }
}
class ClsHrefType extends BaseHrefType {
  constructor(args: HrefTypeParams) {
    super();
    meta.initializers.HrefType(this, args);
  }
  get typeTag(): "HrefType" {
        return "HrefType";
      }
}
export const HrefType = ClsHrefType;

type KnownTargetType = ClsTargetType;
export function isKnownTargetType<T>(x: [Extract<T, TargetType>] extends [never] ? never : T): x is [Extract<T, TargetType>] extends [never]
  ? never
  : TargetType extends T // Needed when T is any
  ? TargetType
  : Extract<T, TargetType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTargetType;
}

    export function ensureKnownTargetType<T>(x: [Extract<T, TargetType>] extends [never] ? never : T): TargetType {
      assert(isKnownTargetType(x), () => mkUnexpectedTypeMsg([TargetType], x));
      return x;
    }
    export function ensureMaybeKnownTargetType<T>(x: [Extract<T, TargetType>] extends [never] ? never : T): TargetType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTargetType(x as any);
    }
    
export type TargetType = KnownTargetType;
export interface TargetTypeParams {
  readonly name: "target" /* Const */;
}

abstract class BaseTargetType extends BaseType {
  
    static isKnown(x: any): x is TargetType { return isKnownTargetType(x); }
    static getType(): TargetType { throw new Error(); }
    static modelTypeName = "TargetType";
    
  static readonly $fields = meta.allFields(meta.clsByName["TargetType"]);
  static readonly $field_name: Field = ensure(
    BaseTargetType.$fields.find((f) => f.name === "name"),
    "TargetType has no field name",
  );
  /* Const */
get name(): "target" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "target") { meta.writeField(this, BaseTargetType.$field_name, value); }
}
class ClsTargetType extends BaseTargetType {
  constructor(args: TargetTypeParams) {
    super();
    meta.initializers.TargetType(this, args);
  }
  get typeTag(): "TargetType" {
        return "TargetType";
      }
}
export const TargetType = ClsTargetType;

type KnownRenderFuncType = ClsRenderFuncType;
export function isKnownRenderFuncType<T>(x: [Extract<T, RenderFuncType>] extends [never] ? never : T): x is [Extract<T, RenderFuncType>] extends [never]
  ? never
  : RenderFuncType extends T // Needed when T is any
  ? RenderFuncType
  : Extract<T, RenderFuncType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRenderFuncType;
}

    export function ensureKnownRenderFuncType<T>(x: [Extract<T, RenderFuncType>] extends [never] ? never : T): RenderFuncType {
      assert(isKnownRenderFuncType(x), () => mkUnexpectedTypeMsg([RenderFuncType], x));
      return x;
    }
    export function ensureMaybeKnownRenderFuncType<T>(x: [Extract<T, RenderFuncType>] extends [never] ? never : T): RenderFuncType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRenderFuncType(x as any);
    }
    
export type RenderFuncType = KnownRenderFuncType;
export interface RenderFuncTypeParams {
  readonly name: "renderFunc" /* Const */;
params: ReadonlyArray<ArgType> /*  */;
allowed: ReadonlyArray<ComponentInstance> /*  */;
allowRootWrapper: boolean | null | undefined /*  */;
}

abstract class BaseRenderFuncType extends BaseType {
  
    static isKnown(x: any): x is RenderFuncType { return isKnownRenderFuncType(x); }
    static getType(): RenderFuncType { throw new Error(); }
    static modelTypeName = "RenderFuncType";
    
  static readonly $fields = meta.allFields(meta.clsByName["RenderFuncType"]);
  static readonly $field_name: Field = ensure(
    BaseRenderFuncType.$fields.find((f) => f.name === "name"),
    "RenderFuncType has no field name",
  );
static readonly $field_params: Field = ensure(
    BaseRenderFuncType.$fields.find((f) => f.name === "params"),
    "RenderFuncType has no field params",
  );
static readonly $field_allowed: Field = ensure(
    BaseRenderFuncType.$fields.find((f) => f.name === "allowed"),
    "RenderFuncType has no field allowed",
  );
static readonly $field_allowRootWrapper: Field = ensure(
    BaseRenderFuncType.$fields.find((f) => f.name === "allowRootWrapper"),
    "RenderFuncType has no field allowRootWrapper",
  );
  /* Const */
get name(): "renderFunc" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "renderFunc") { meta.writeField(this, BaseRenderFuncType.$field_name, value); }
get params(): ReadonlyArray<ArgType> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: ReadonlyArray<ArgType>) { meta.writeField(this, BaseRenderFuncType.$field_params, value); }
get allowed(): ReadonlyArray<ComponentInstance> { meta.reportRead(this, "allowed"); return (this as any).$v.allowed; }
set allowed(value: ReadonlyArray<ComponentInstance>) { meta.writeField(this, BaseRenderFuncType.$field_allowed, value); }
get allowRootWrapper(): boolean | null | undefined { meta.reportRead(this, "allowRootWrapper"); return (this as any).$v.allowRootWrapper; }
set allowRootWrapper(value: boolean | null | undefined) { meta.writeField(this, BaseRenderFuncType.$field_allowRootWrapper, value); }
}
class ClsRenderFuncType extends BaseRenderFuncType {
  constructor(args: RenderFuncTypeParams) {
    super();
    meta.initializers.RenderFuncType(this, args);
  }
  get typeTag(): "RenderFuncType" {
        return "RenderFuncType";
      }
}
export const RenderFuncType = ClsRenderFuncType;

type KnownQueryData = ClsQueryData;
export function isKnownQueryData<T>(x: [Extract<T, QueryData>] extends [never] ? never : T): x is [Extract<T, QueryData>] extends [never]
  ? never
  : QueryData extends T // Needed when T is any
  ? QueryData
  : Extract<T, QueryData> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseQueryData;
}

    export function ensureKnownQueryData<T>(x: [Extract<T, QueryData>] extends [never] ? never : T): QueryData {
      assert(isKnownQueryData(x), () => mkUnexpectedTypeMsg([QueryData], x));
      return x;
    }
    export function ensureMaybeKnownQueryData<T>(x: [Extract<T, QueryData>] extends [never] ? never : T): QueryData | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownQueryData(x as any);
    }
    
export type QueryData = KnownQueryData;
export interface QueryDataParams {
  readonly name: "queryData" /* Const */;
}

abstract class BaseQueryData extends BaseType {
  
    static isKnown(x: any): x is QueryData { return isKnownQueryData(x); }
    static getType(): QueryData { throw new Error(); }
    static modelTypeName = "QueryData";
    
  static readonly $fields = meta.allFields(meta.clsByName["QueryData"]);
  static readonly $field_name: Field = ensure(
    BaseQueryData.$fields.find((f) => f.name === "name"),
    "QueryData has no field name",
  );
  /* Const */
get name(): "queryData" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "queryData") { meta.writeField(this, BaseQueryData.$field_name, value); }
}
class ClsQueryData extends BaseQueryData {
  constructor(args: QueryDataParams) {
    super();
    meta.initializers.QueryData(this, args);
  }
  get typeTag(): "QueryData" {
        return "QueryData";
      }
}
export const QueryData = ClsQueryData;

type KnownDateString = ClsDateString;
export function isKnownDateString<T>(x: [Extract<T, DateString>] extends [never] ? never : T): x is [Extract<T, DateString>] extends [never]
  ? never
  : DateString extends T // Needed when T is any
  ? DateString
  : Extract<T, DateString> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDateString;
}

    export function ensureKnownDateString<T>(x: [Extract<T, DateString>] extends [never] ? never : T): DateString {
      assert(isKnownDateString(x), () => mkUnexpectedTypeMsg([DateString], x));
      return x;
    }
    export function ensureMaybeKnownDateString<T>(x: [Extract<T, DateString>] extends [never] ? never : T): DateString | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDateString(x as any);
    }
    
export type DateString = KnownDateString;
export interface DateStringParams {
  readonly name: "dateString" /* Const */;
}

abstract class BaseDateString extends BaseType {
  
    static isKnown(x: any): x is DateString { return isKnownDateString(x); }
    static getType(): DateString { throw new Error(); }
    static modelTypeName = "DateString";
    
  static readonly $fields = meta.allFields(meta.clsByName["DateString"]);
  static readonly $field_name: Field = ensure(
    BaseDateString.$fields.find((f) => f.name === "name"),
    "DateString has no field name",
  );
  /* Const */
get name(): "dateString" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "dateString") { meta.writeField(this, BaseDateString.$field_name, value); }
}
class ClsDateString extends BaseDateString {
  constructor(args: DateStringParams) {
    super();
    meta.initializers.DateString(this, args);
  }
  get typeTag(): "DateString" {
        return "DateString";
      }
}
export const DateString = ClsDateString;

type KnownDateRangeStrings = ClsDateRangeStrings;
export function isKnownDateRangeStrings<T>(x: [Extract<T, DateRangeStrings>] extends [never] ? never : T): x is [Extract<T, DateRangeStrings>] extends [never]
  ? never
  : DateRangeStrings extends T // Needed when T is any
  ? DateRangeStrings
  : Extract<T, DateRangeStrings> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDateRangeStrings;
}

    export function ensureKnownDateRangeStrings<T>(x: [Extract<T, DateRangeStrings>] extends [never] ? never : T): DateRangeStrings {
      assert(isKnownDateRangeStrings(x), () => mkUnexpectedTypeMsg([DateRangeStrings], x));
      return x;
    }
    export function ensureMaybeKnownDateRangeStrings<T>(x: [Extract<T, DateRangeStrings>] extends [never] ? never : T): DateRangeStrings | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDateRangeStrings(x as any);
    }
    
export type DateRangeStrings = KnownDateRangeStrings;
export interface DateRangeStringsParams {
  readonly name: "dateRangeStrings" /* Const */;
}

abstract class BaseDateRangeStrings extends BaseType {
  
    static isKnown(x: any): x is DateRangeStrings { return isKnownDateRangeStrings(x); }
    static getType(): DateRangeStrings { throw new Error(); }
    static modelTypeName = "DateRangeStrings";
    
  static readonly $fields = meta.allFields(meta.clsByName["DateRangeStrings"]);
  static readonly $field_name: Field = ensure(
    BaseDateRangeStrings.$fields.find((f) => f.name === "name"),
    "DateRangeStrings has no field name",
  );
  /* Const */
get name(): "dateRangeStrings" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "dateRangeStrings") { meta.writeField(this, BaseDateRangeStrings.$field_name, value); }
}
class ClsDateRangeStrings extends BaseDateRangeStrings {
  constructor(args: DateRangeStringsParams) {
    super();
    meta.initializers.DateRangeStrings(this, args);
  }
  get typeTag(): "DateRangeStrings" {
        return "DateRangeStrings";
      }
}
export const DateRangeStrings = ClsDateRangeStrings;

type KnownFunctionType = ClsFunctionType;
export function isKnownFunctionType<T>(x: [Extract<T, FunctionType>] extends [never] ? never : T): x is [Extract<T, FunctionType>] extends [never]
  ? never
  : FunctionType extends T // Needed when T is any
  ? FunctionType
  : Extract<T, FunctionType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseFunctionType;
}

    export function ensureKnownFunctionType<T>(x: [Extract<T, FunctionType>] extends [never] ? never : T): FunctionType {
      assert(isKnownFunctionType(x), () => mkUnexpectedTypeMsg([FunctionType], x));
      return x;
    }
    export function ensureMaybeKnownFunctionType<T>(x: [Extract<T, FunctionType>] extends [never] ? never : T): FunctionType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownFunctionType(x as any);
    }
    
export type FunctionType = KnownFunctionType;
export interface FunctionTypeParams {
  readonly name: "func" /* Const */;
params: ReadonlyArray<ArgType> /*  */;
}

abstract class BaseFunctionType extends BaseType {
  
    static isKnown(x: any): x is FunctionType { return isKnownFunctionType(x); }
    static getType(): FunctionType { throw new Error(); }
    static modelTypeName = "FunctionType";
    
  static readonly $fields = meta.allFields(meta.clsByName["FunctionType"]);
  static readonly $field_name: Field = ensure(
    BaseFunctionType.$fields.find((f) => f.name === "name"),
    "FunctionType has no field name",
  );
static readonly $field_params: Field = ensure(
    BaseFunctionType.$fields.find((f) => f.name === "params"),
    "FunctionType has no field params",
  );
  /* Const */
get name(): "func" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "func") { meta.writeField(this, BaseFunctionType.$field_name, value); }
get params(): ReadonlyArray<ArgType> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: ReadonlyArray<ArgType>) { meta.writeField(this, BaseFunctionType.$field_params, value); }
}
class ClsFunctionType extends BaseFunctionType {
  constructor(args: FunctionTypeParams) {
    super();
    meta.initializers.FunctionType(this, args);
  }
  get typeTag(): "FunctionType" {
        return "FunctionType";
      }
}
export const FunctionType = ClsFunctionType;

type KnownArgType = ClsArgType;
export function isKnownArgType<T>(x: [Extract<T, ArgType>] extends [never] ? never : T): x is [Extract<T, ArgType>] extends [never]
  ? never
  : ArgType extends T // Needed when T is any
  ? ArgType
  : Extract<T, ArgType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArgType;
}

    export function ensureKnownArgType<T>(x: [Extract<T, ArgType>] extends [never] ? never : T): ArgType {
      assert(isKnownArgType(x), () => mkUnexpectedTypeMsg([ArgType], x));
      return x;
    }
    export function ensureMaybeKnownArgType<T>(x: [Extract<T, ArgType>] extends [never] ? never : T): ArgType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArgType(x as any);
    }
    
export type ArgType = KnownArgType;
export interface ArgTypeParams {
  readonly name: "arg" /* Const */;
argName: string /*  */;
displayName: string | null | undefined /*  */;
type: PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | StylePropType /*  */;
}

abstract class BaseArgType extends BaseType {
  
    static isKnown(x: any): x is ArgType { return isKnownArgType(x); }
    static getType(): ArgType { throw new Error(); }
    static modelTypeName = "ArgType";
    
  static readonly $fields = meta.allFields(meta.clsByName["ArgType"]);
  static readonly $field_name: Field = ensure(
    BaseArgType.$fields.find((f) => f.name === "name"),
    "ArgType has no field name",
  );
static readonly $field_argName: Field = ensure(
    BaseArgType.$fields.find((f) => f.name === "argName"),
    "ArgType has no field argName",
  );
static readonly $field_displayName: Field = ensure(
    BaseArgType.$fields.find((f) => f.name === "displayName"),
    "ArgType has no field displayName",
  );
static readonly $field_type: Field = ensure(
    BaseArgType.$fields.find((f) => f.name === "type"),
    "ArgType has no field type",
  );
  /* Const */
get name(): "arg" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "arg") { meta.writeField(this, BaseArgType.$field_name, value); }
get argName(): string { meta.reportRead(this, "argName"); return (this as any).$v.argName; }
set argName(value: string) { meta.writeField(this, BaseArgType.$field_argName, value); }
get displayName(): string | null | undefined { meta.reportRead(this, "displayName"); return (this as any).$v.displayName; }
set displayName(value: string | null | undefined) { meta.writeField(this, BaseArgType.$field_displayName, value); }
get type(): PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | StylePropType { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | StylePropType) { meta.writeField(this, BaseArgType.$field_type, value); }
}
class ClsArgType extends BaseArgType {
  constructor(args: ArgTypeParams) {
    super();
    meta.initializers.ArgType(this, args);
  }
  get typeTag(): "ArgType" {
        return "ArgType";
      }
}
export const ArgType = ClsArgType;

type KnownStylePropType = KnownClassNamePropType | KnownStyleScopeClassNamePropType | KnownDefaultStylesClassNamePropType | KnownDefaultStylesPropType | KnownColorPropType;
export function isKnownStylePropType<T>(x: [Extract<T, StylePropType>] extends [never] ? never : T): x is [Extract<T, StylePropType>] extends [never]
  ? never
  : StylePropType extends T // Needed when T is any
  ? StylePropType
  : Extract<T, StylePropType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStylePropType;
}

    export function ensureKnownStylePropType<T>(x: [Extract<T, StylePropType>] extends [never] ? never : T): StylePropType {
      assert(isKnownStylePropType(x), () => mkUnexpectedTypeMsg([StylePropType], x));
      return x;
    }
    export function ensureMaybeKnownStylePropType<T>(x: [Extract<T, StylePropType>] extends [never] ? never : T): StylePropType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStylePropType(x as any);
    }
    
export type StylePropType = KnownStylePropType;
export interface StylePropTypeParams {
  
}

abstract class BaseStylePropType extends BaseType {
  
    static isKnown(x: any): x is StylePropType { return isKnownStylePropType(x); }
    static getType(): StylePropType { throw new Error(); }
    static modelTypeName = "StylePropType";
    
  static readonly $fields = meta.allFields(meta.clsByName["StylePropType"]);
  
  
}

export const StylePropType = BaseStylePropType;

type KnownClassNamePropType = ClsClassNamePropType;
export function isKnownClassNamePropType<T>(x: [Extract<T, ClassNamePropType>] extends [never] ? never : T): x is [Extract<T, ClassNamePropType>] extends [never]
  ? never
  : ClassNamePropType extends T // Needed when T is any
  ? ClassNamePropType
  : Extract<T, ClassNamePropType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseClassNamePropType;
}

    export function ensureKnownClassNamePropType<T>(x: [Extract<T, ClassNamePropType>] extends [never] ? never : T): ClassNamePropType {
      assert(isKnownClassNamePropType(x), () => mkUnexpectedTypeMsg([ClassNamePropType], x));
      return x;
    }
    export function ensureMaybeKnownClassNamePropType<T>(x: [Extract<T, ClassNamePropType>] extends [never] ? never : T): ClassNamePropType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownClassNamePropType(x as any);
    }
    
export type ClassNamePropType = KnownClassNamePropType;
export interface ClassNamePropTypeParams {
  readonly name: "className" /* Const */;
selectors: ReadonlyArray<LabeledSelector> /*  */;
defaultStyles: Readonly<Record<string, string>> /*  */;
}

abstract class BaseClassNamePropType extends BaseStylePropType {
  
    static isKnown(x: any): x is ClassNamePropType { return isKnownClassNamePropType(x); }
    static getType(): ClassNamePropType { throw new Error(); }
    static modelTypeName = "ClassNamePropType";
    
  static readonly $fields = meta.allFields(meta.clsByName["ClassNamePropType"]);
  static readonly $field_name: Field = ensure(
    BaseClassNamePropType.$fields.find((f) => f.name === "name"),
    "ClassNamePropType has no field name",
  );
static readonly $field_selectors: Field = ensure(
    BaseClassNamePropType.$fields.find((f) => f.name === "selectors"),
    "ClassNamePropType has no field selectors",
  );
static readonly $field_defaultStyles: Field = ensure(
    BaseClassNamePropType.$fields.find((f) => f.name === "defaultStyles"),
    "ClassNamePropType has no field defaultStyles",
  );
  /* Const */
get name(): "className" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "className") { meta.writeField(this, BaseClassNamePropType.$field_name, value); }
get selectors(): ReadonlyArray<LabeledSelector> { meta.reportRead(this, "selectors"); return (this as any).$v.selectors; }
set selectors(value: ReadonlyArray<LabeledSelector>) { meta.writeField(this, BaseClassNamePropType.$field_selectors, value); }
get defaultStyles(): Readonly<Record<string, string>> { meta.reportRead(this, "defaultStyles"); return (this as any).$v.defaultStyles; }
set defaultStyles(value: Readonly<Record<string, string>>) { meta.writeField(this, BaseClassNamePropType.$field_defaultStyles, value); }
}
class ClsClassNamePropType extends BaseClassNamePropType {
  constructor(args: ClassNamePropTypeParams) {
    super();
    meta.initializers.ClassNamePropType(this, args);
  }
  get typeTag(): "ClassNamePropType" {
        return "ClassNamePropType";
      }
}
export const ClassNamePropType = ClsClassNamePropType;

type KnownStyleScopeClassNamePropType = ClsStyleScopeClassNamePropType;
export function isKnownStyleScopeClassNamePropType<T>(x: [Extract<T, StyleScopeClassNamePropType>] extends [never] ? never : T): x is [Extract<T, StyleScopeClassNamePropType>] extends [never]
  ? never
  : StyleScopeClassNamePropType extends T // Needed when T is any
  ? StyleScopeClassNamePropType
  : Extract<T, StyleScopeClassNamePropType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleScopeClassNamePropType;
}

    export function ensureKnownStyleScopeClassNamePropType<T>(x: [Extract<T, StyleScopeClassNamePropType>] extends [never] ? never : T): StyleScopeClassNamePropType {
      assert(isKnownStyleScopeClassNamePropType(x), () => mkUnexpectedTypeMsg([StyleScopeClassNamePropType], x));
      return x;
    }
    export function ensureMaybeKnownStyleScopeClassNamePropType<T>(x: [Extract<T, StyleScopeClassNamePropType>] extends [never] ? never : T): StyleScopeClassNamePropType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleScopeClassNamePropType(x as any);
    }
    
export type StyleScopeClassNamePropType = KnownStyleScopeClassNamePropType;
export interface StyleScopeClassNamePropTypeParams {
  readonly name: "styleScopeClassName" /* Const */;
scopeName: string /*  */;
}

abstract class BaseStyleScopeClassNamePropType extends BaseStylePropType {
  
    static isKnown(x: any): x is StyleScopeClassNamePropType { return isKnownStyleScopeClassNamePropType(x); }
    static getType(): StyleScopeClassNamePropType { throw new Error(); }
    static modelTypeName = "StyleScopeClassNamePropType";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleScopeClassNamePropType"]);
  static readonly $field_name: Field = ensure(
    BaseStyleScopeClassNamePropType.$fields.find((f) => f.name === "name"),
    "StyleScopeClassNamePropType has no field name",
  );
static readonly $field_scopeName: Field = ensure(
    BaseStyleScopeClassNamePropType.$fields.find((f) => f.name === "scopeName"),
    "StyleScopeClassNamePropType has no field scopeName",
  );
  /* Const */
get name(): "styleScopeClassName" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "styleScopeClassName") { meta.writeField(this, BaseStyleScopeClassNamePropType.$field_name, value); }
get scopeName(): string { meta.reportRead(this, "scopeName"); return (this as any).$v.scopeName; }
set scopeName(value: string) { meta.writeField(this, BaseStyleScopeClassNamePropType.$field_scopeName, value); }
}
class ClsStyleScopeClassNamePropType extends BaseStyleScopeClassNamePropType {
  constructor(args: StyleScopeClassNamePropTypeParams) {
    super();
    meta.initializers.StyleScopeClassNamePropType(this, args);
  }
  get typeTag(): "StyleScopeClassNamePropType" {
        return "StyleScopeClassNamePropType";
      }
}
export const StyleScopeClassNamePropType = ClsStyleScopeClassNamePropType;

type KnownDefaultStylesClassNamePropType = ClsDefaultStylesClassNamePropType;
export function isKnownDefaultStylesClassNamePropType<T>(x: [Extract<T, DefaultStylesClassNamePropType>] extends [never] ? never : T): x is [Extract<T, DefaultStylesClassNamePropType>] extends [never]
  ? never
  : DefaultStylesClassNamePropType extends T // Needed when T is any
  ? DefaultStylesClassNamePropType
  : Extract<T, DefaultStylesClassNamePropType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDefaultStylesClassNamePropType;
}

    export function ensureKnownDefaultStylesClassNamePropType<T>(x: [Extract<T, DefaultStylesClassNamePropType>] extends [never] ? never : T): DefaultStylesClassNamePropType {
      assert(isKnownDefaultStylesClassNamePropType(x), () => mkUnexpectedTypeMsg([DefaultStylesClassNamePropType], x));
      return x;
    }
    export function ensureMaybeKnownDefaultStylesClassNamePropType<T>(x: [Extract<T, DefaultStylesClassNamePropType>] extends [never] ? never : T): DefaultStylesClassNamePropType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDefaultStylesClassNamePropType(x as any);
    }
    
export type DefaultStylesClassNamePropType = KnownDefaultStylesClassNamePropType;
export interface DefaultStylesClassNamePropTypeParams {
  readonly name: "defaultStylesClassName" /* Const */;
includeTagStyles: boolean /*  */;
}

abstract class BaseDefaultStylesClassNamePropType extends BaseStylePropType {
  
    static isKnown(x: any): x is DefaultStylesClassNamePropType { return isKnownDefaultStylesClassNamePropType(x); }
    static getType(): DefaultStylesClassNamePropType { throw new Error(); }
    static modelTypeName = "DefaultStylesClassNamePropType";
    
  static readonly $fields = meta.allFields(meta.clsByName["DefaultStylesClassNamePropType"]);
  static readonly $field_name: Field = ensure(
    BaseDefaultStylesClassNamePropType.$fields.find((f) => f.name === "name"),
    "DefaultStylesClassNamePropType has no field name",
  );
static readonly $field_includeTagStyles: Field = ensure(
    BaseDefaultStylesClassNamePropType.$fields.find((f) => f.name === "includeTagStyles"),
    "DefaultStylesClassNamePropType has no field includeTagStyles",
  );
  /* Const */
get name(): "defaultStylesClassName" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "defaultStylesClassName") { meta.writeField(this, BaseDefaultStylesClassNamePropType.$field_name, value); }
get includeTagStyles(): boolean { meta.reportRead(this, "includeTagStyles"); return (this as any).$v.includeTagStyles; }
set includeTagStyles(value: boolean) { meta.writeField(this, BaseDefaultStylesClassNamePropType.$field_includeTagStyles, value); }
}
class ClsDefaultStylesClassNamePropType extends BaseDefaultStylesClassNamePropType {
  constructor(args: DefaultStylesClassNamePropTypeParams) {
    super();
    meta.initializers.DefaultStylesClassNamePropType(this, args);
  }
  get typeTag(): "DefaultStylesClassNamePropType" {
        return "DefaultStylesClassNamePropType";
      }
}
export const DefaultStylesClassNamePropType = ClsDefaultStylesClassNamePropType;

type KnownDefaultStylesPropType = ClsDefaultStylesPropType;
export function isKnownDefaultStylesPropType<T>(x: [Extract<T, DefaultStylesPropType>] extends [never] ? never : T): x is [Extract<T, DefaultStylesPropType>] extends [never]
  ? never
  : DefaultStylesPropType extends T // Needed when T is any
  ? DefaultStylesPropType
  : Extract<T, DefaultStylesPropType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDefaultStylesPropType;
}

    export function ensureKnownDefaultStylesPropType<T>(x: [Extract<T, DefaultStylesPropType>] extends [never] ? never : T): DefaultStylesPropType {
      assert(isKnownDefaultStylesPropType(x), () => mkUnexpectedTypeMsg([DefaultStylesPropType], x));
      return x;
    }
    export function ensureMaybeKnownDefaultStylesPropType<T>(x: [Extract<T, DefaultStylesPropType>] extends [never] ? never : T): DefaultStylesPropType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDefaultStylesPropType(x as any);
    }
    
export type DefaultStylesPropType = KnownDefaultStylesPropType;
export interface DefaultStylesPropTypeParams {
  readonly name: "defaultStyles" /* Const */;
}

abstract class BaseDefaultStylesPropType extends BaseStylePropType {
  
    static isKnown(x: any): x is DefaultStylesPropType { return isKnownDefaultStylesPropType(x); }
    static getType(): DefaultStylesPropType { throw new Error(); }
    static modelTypeName = "DefaultStylesPropType";
    
  static readonly $fields = meta.allFields(meta.clsByName["DefaultStylesPropType"]);
  static readonly $field_name: Field = ensure(
    BaseDefaultStylesPropType.$fields.find((f) => f.name === "name"),
    "DefaultStylesPropType has no field name",
  );
  /* Const */
get name(): "defaultStyles" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "defaultStyles") { meta.writeField(this, BaseDefaultStylesPropType.$field_name, value); }
}
class ClsDefaultStylesPropType extends BaseDefaultStylesPropType {
  constructor(args: DefaultStylesPropTypeParams) {
    super();
    meta.initializers.DefaultStylesPropType(this, args);
  }
  get typeTag(): "DefaultStylesPropType" {
        return "DefaultStylesPropType";
      }
}
export const DefaultStylesPropType = ClsDefaultStylesPropType;

type KnownColorPropType = ClsColorPropType;
export function isKnownColorPropType<T>(x: [Extract<T, ColorPropType>] extends [never] ? never : T): x is [Extract<T, ColorPropType>] extends [never]
  ? never
  : ColorPropType extends T // Needed when T is any
  ? ColorPropType
  : Extract<T, ColorPropType> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseColorPropType;
}

    export function ensureKnownColorPropType<T>(x: [Extract<T, ColorPropType>] extends [never] ? never : T): ColorPropType {
      assert(isKnownColorPropType(x), () => mkUnexpectedTypeMsg([ColorPropType], x));
      return x;
    }
    export function ensureMaybeKnownColorPropType<T>(x: [Extract<T, ColorPropType>] extends [never] ? never : T): ColorPropType | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownColorPropType(x as any);
    }
    
export type ColorPropType = KnownColorPropType;
export interface ColorPropTypeParams {
  readonly name: "color" /* Const */;
noDeref: boolean /*  */;
}

abstract class BaseColorPropType extends BaseStylePropType {
  
    static isKnown(x: any): x is ColorPropType { return isKnownColorPropType(x); }
    static getType(): ColorPropType { throw new Error(); }
    static modelTypeName = "ColorPropType";
    
  static readonly $fields = meta.allFields(meta.clsByName["ColorPropType"]);
  static readonly $field_name: Field = ensure(
    BaseColorPropType.$fields.find((f) => f.name === "name"),
    "ColorPropType has no field name",
  );
static readonly $field_noDeref: Field = ensure(
    BaseColorPropType.$fields.find((f) => f.name === "noDeref"),
    "ColorPropType has no field noDeref",
  );
  /* Const */
get name(): "color" { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: "color") { meta.writeField(this, BaseColorPropType.$field_name, value); }
get noDeref(): boolean { meta.reportRead(this, "noDeref"); return (this as any).$v.noDeref; }
set noDeref(value: boolean) { meta.writeField(this, BaseColorPropType.$field_noDeref, value); }
}
class ClsColorPropType extends BaseColorPropType {
  constructor(args: ColorPropTypeParams) {
    super();
    meta.initializers.ColorPropType(this, args);
  }
  get typeTag(): "ColorPropType" {
        return "ColorPropType";
      }
}
export const ColorPropType = ClsColorPropType;

type KnownVariantedValue = ClsVariantedValue;
export function isKnownVariantedValue<T>(x: [Extract<T, VariantedValue>] extends [never] ? never : T): x is [Extract<T, VariantedValue>] extends [never]
  ? never
  : VariantedValue extends T // Needed when T is any
  ? VariantedValue
  : Extract<T, VariantedValue> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariantedValue;
}

    export function ensureKnownVariantedValue<T>(x: [Extract<T, VariantedValue>] extends [never] ? never : T): VariantedValue {
      assert(isKnownVariantedValue(x), () => mkUnexpectedTypeMsg([VariantedValue], x));
      return x;
    }
    export function ensureMaybeKnownVariantedValue<T>(x: [Extract<T, VariantedValue>] extends [never] ? never : T): VariantedValue | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariantedValue(x as any);
    }
    
export type VariantedValue = KnownVariantedValue;
export interface VariantedValueParams {
  variants: ReadonlyArray<Variant> /* WeakRef */;
value: string /*  */;
}

abstract class BaseVariantedValue extends ModelInst {
  
    static isKnown(x: any): x is VariantedValue { return isKnownVariantedValue(x); }
    static getType(): VariantedValue { throw new Error(); }
    static modelTypeName = "VariantedValue";
    
  static readonly $fields = meta.allFields(meta.clsByName["VariantedValue"]);
  static readonly $field_variants: Field = ensure(
    BaseVariantedValue.$fields.find((f) => f.name === "variants"),
    "VariantedValue has no field variants",
  );
static readonly $field_value: Field = ensure(
    BaseVariantedValue.$fields.find((f) => f.name === "value"),
    "VariantedValue has no field value",
  );
  /* WeakRef */
get variants(): ReadonlyArray<Variant> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
set variants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseVariantedValue.$field_variants, value); }
get value(): string { meta.reportRead(this, "value"); return (this as any).$v.value; }
set value(value: string) { meta.writeField(this, BaseVariantedValue.$field_value, value); }
}
class ClsVariantedValue extends BaseVariantedValue {
  constructor(args: VariantedValueParams) {
    super();
    meta.initializers.VariantedValue(this, args);
  }
  get typeTag(): "VariantedValue" {
        return "VariantedValue";
      }
}
export const VariantedValue = ClsVariantedValue;

type KnownStyleTokenOverride = ClsStyleTokenOverride;
export function isKnownStyleTokenOverride<T>(x: [Extract<T, StyleTokenOverride>] extends [never] ? never : T): x is [Extract<T, StyleTokenOverride>] extends [never]
  ? never
  : StyleTokenOverride extends T // Needed when T is any
  ? StyleTokenOverride
  : Extract<T, StyleTokenOverride> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleTokenOverride;
}

    export function ensureKnownStyleTokenOverride<T>(x: [Extract<T, StyleTokenOverride>] extends [never] ? never : T): StyleTokenOverride {
      assert(isKnownStyleTokenOverride(x), () => mkUnexpectedTypeMsg([StyleTokenOverride], x));
      return x;
    }
    export function ensureMaybeKnownStyleTokenOverride<T>(x: [Extract<T, StyleTokenOverride>] extends [never] ? never : T): StyleTokenOverride | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleTokenOverride(x as any);
    }
    
export type StyleTokenOverride = KnownStyleTokenOverride;
export interface StyleTokenOverrideParams {
  token: StyleToken /* WeakRef */;
value: string | null | undefined /*  */;
variantedValues: ReadonlyArray<VariantedValue> /*  */;
}

abstract class BaseStyleTokenOverride extends ModelInst {
  
    static isKnown(x: any): x is StyleTokenOverride { return isKnownStyleTokenOverride(x); }
    static getType(): StyleTokenOverride { throw new Error(); }
    static modelTypeName = "StyleTokenOverride";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleTokenOverride"]);
  static readonly $field_token: Field = ensure(
    BaseStyleTokenOverride.$fields.find((f) => f.name === "token"),
    "StyleTokenOverride has no field token",
  );
static readonly $field_value: Field = ensure(
    BaseStyleTokenOverride.$fields.find((f) => f.name === "value"),
    "StyleTokenOverride has no field value",
  );
static readonly $field_variantedValues: Field = ensure(
    BaseStyleTokenOverride.$fields.find((f) => f.name === "variantedValues"),
    "StyleTokenOverride has no field variantedValues",
  );
  /* WeakRef */
get token(): StyleToken { meta.reportRead(this, "token"); return (this as any).$v.token; }
set token(value: StyleToken) { meta.writeField(this, BaseStyleTokenOverride.$field_token, value); }
get value(): string | null | undefined { meta.reportRead(this, "value"); return (this as any).$v.value; }
set value(value: string | null | undefined) { meta.writeField(this, BaseStyleTokenOverride.$field_value, value); }
get variantedValues(): ReadonlyArray<VariantedValue> { meta.reportRead(this, "variantedValues"); return (this as any).$v.variantedValues; }
set variantedValues(value: ReadonlyArray<VariantedValue>) { meta.writeField(this, BaseStyleTokenOverride.$field_variantedValues, value); }
}
class ClsStyleTokenOverride extends BaseStyleTokenOverride {
  constructor(args: StyleTokenOverrideParams) {
    super();
    meta.initializers.StyleTokenOverride(this, args);
  }
  get typeTag(): "StyleTokenOverride" {
        return "StyleTokenOverride";
      }
}
export const StyleTokenOverride = ClsStyleTokenOverride;

type KnownToken = KnownStyleToken | KnownDataToken;
export function isKnownToken<T>(x: [Extract<T, Token>] extends [never] ? never : T): x is [Extract<T, Token>] extends [never]
  ? never
  : Token extends T // Needed when T is any
  ? Token
  : Extract<T, Token> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseToken;
}

    export function ensureKnownToken<T>(x: [Extract<T, Token>] extends [never] ? never : T): Token {
      assert(isKnownToken(x), () => mkUnexpectedTypeMsg([Token], x));
      return x;
    }
    export function ensureMaybeKnownToken<T>(x: [Extract<T, Token>] extends [never] ? never : T): Token | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownToken(x as any);
    }
    
export type Token = KnownToken;
export interface TokenParams {
  name: string /*  */;
readonly uuid: string /* Const */;
value: string /*  */;
variantedValues: ReadonlyArray<VariantedValue> /*  */;
isRegistered: boolean /*  */;
regKey: string | null | undefined /*  */;
}

abstract class BaseToken extends ModelInst {
  
    static isKnown(x: any): x is Token { return isKnownToken(x); }
    static getType(): Token { throw new Error(); }
    static modelTypeName = "Token";
    
  static readonly $fields = meta.allFields(meta.clsByName["Token"]);
  static readonly $field_name: Field = ensure(
    BaseToken.$fields.find((f) => f.name === "name"),
    "Token has no field name",
  );
static readonly $field_uuid: Field = ensure(
    BaseToken.$fields.find((f) => f.name === "uuid"),
    "Token has no field uuid",
  );
static readonly $field_value: Field = ensure(
    BaseToken.$fields.find((f) => f.name === "value"),
    "Token has no field value",
  );
static readonly $field_variantedValues: Field = ensure(
    BaseToken.$fields.find((f) => f.name === "variantedValues"),
    "Token has no field variantedValues",
  );
static readonly $field_isRegistered: Field = ensure(
    BaseToken.$fields.find((f) => f.name === "isRegistered"),
    "Token has no field isRegistered",
  );
static readonly $field_regKey: Field = ensure(
    BaseToken.$fields.find((f) => f.name === "regKey"),
    "Token has no field regKey",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseToken.$field_name, value); }
/* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseToken.$field_uuid, value); }
get value(): string { meta.reportRead(this, "value"); return (this as any).$v.value; }
set value(value: string) { meta.writeField(this, BaseToken.$field_value, value); }
get variantedValues(): ReadonlyArray<VariantedValue> { meta.reportRead(this, "variantedValues"); return (this as any).$v.variantedValues; }
set variantedValues(value: ReadonlyArray<VariantedValue>) { meta.writeField(this, BaseToken.$field_variantedValues, value); }
get isRegistered(): boolean { meta.reportRead(this, "isRegistered"); return (this as any).$v.isRegistered; }
set isRegistered(value: boolean) { meta.writeField(this, BaseToken.$field_isRegistered, value); }
get regKey(): string | null | undefined { meta.reportRead(this, "regKey"); return (this as any).$v.regKey; }
set regKey(value: string | null | undefined) { meta.writeField(this, BaseToken.$field_regKey, value); }
}

export const Token = BaseToken;

type KnownStyleToken = ClsStyleToken;
export function isKnownStyleToken<T>(x: [Extract<T, StyleToken>] extends [never] ? never : T): x is [Extract<T, StyleToken>] extends [never]
  ? never
  : StyleToken extends T // Needed when T is any
  ? StyleToken
  : Extract<T, StyleToken> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleToken;
}

    export function ensureKnownStyleToken<T>(x: [Extract<T, StyleToken>] extends [never] ? never : T): StyleToken {
      assert(isKnownStyleToken(x), () => mkUnexpectedTypeMsg([StyleToken], x));
      return x;
    }
    export function ensureMaybeKnownStyleToken<T>(x: [Extract<T, StyleToken>] extends [never] ? never : T): StyleToken | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleToken(x as any);
    }
    
export type StyleToken = KnownStyleToken;
export interface StyleTokenParams {
  readonly type: "Color" | "Spacing" | "Opacity" | "LineHeight" | "FontFamily" | "FontSize" /* Const */;
name: string /*  */;
readonly uuid: string /* Const */;
value: string /*  */;
variantedValues: ReadonlyArray<VariantedValue> /*  */;
isRegistered: boolean /*  */;
regKey: string | null | undefined /*  */;
}

abstract class BaseStyleToken extends BaseToken {
  
    static isKnown(x: any): x is StyleToken { return isKnownStyleToken(x); }
    static getType(): StyleToken { throw new Error(); }
    static modelTypeName = "StyleToken";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleToken"]);
  static readonly $field_type: Field = ensure(
    BaseStyleToken.$fields.find((f) => f.name === "type"),
    "StyleToken has no field type",
  );
  /* Const */
get type(): "Color" | "Spacing" | "Opacity" | "LineHeight" | "FontFamily" | "FontSize" { meta.reportRead(this, "type"); return (this as any).$v.type; }
private set type(value: "Color" | "Spacing" | "Opacity" | "LineHeight" | "FontFamily" | "FontSize") { meta.writeField(this, BaseStyleToken.$field_type, value); }
}
class ClsStyleToken extends BaseStyleToken {
  constructor(args: StyleTokenParams) {
    super();
    meta.initializers.StyleToken(this, args);
  }
  get typeTag(): "StyleToken" {
        return "StyleToken";
      }
}
export const StyleToken = ClsStyleToken;

type KnownDataToken = ClsDataToken;
export function isKnownDataToken<T>(x: [Extract<T, DataToken>] extends [never] ? never : T): x is [Extract<T, DataToken>] extends [never]
  ? never
  : DataToken extends T // Needed when T is any
  ? DataToken
  : Extract<T, DataToken> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDataToken;
}

    export function ensureKnownDataToken<T>(x: [Extract<T, DataToken>] extends [never] ? never : T): DataToken {
      assert(isKnownDataToken(x), () => mkUnexpectedTypeMsg([DataToken], x));
      return x;
    }
    export function ensureMaybeKnownDataToken<T>(x: [Extract<T, DataToken>] extends [never] ? never : T): DataToken | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDataToken(x as any);
    }
    
export type DataToken = KnownDataToken;
export interface DataTokenParams {
  readonly type: "Data" /* Const */;
name: string /*  */;
readonly uuid: string /* Const */;
value: string /*  */;
variantedValues: ReadonlyArray<VariantedValue> /*  */;
isRegistered: boolean /*  */;
regKey: string | null | undefined /*  */;
}

abstract class BaseDataToken extends BaseToken {
  
    static isKnown(x: any): x is DataToken { return isKnownDataToken(x); }
    static getType(): DataToken { throw new Error(); }
    static modelTypeName = "DataToken";
    
  static readonly $fields = meta.allFields(meta.clsByName["DataToken"]);
  static readonly $field_type: Field = ensure(
    BaseDataToken.$fields.find((f) => f.name === "type"),
    "DataToken has no field type",
  );
  /* Const */
get type(): "Data" { meta.reportRead(this, "type"); return (this as any).$v.type; }
private set type(value: "Data") { meta.writeField(this, BaseDataToken.$field_type, value); }
}
class ClsDataToken extends BaseDataToken {
  constructor(args: DataTokenParams) {
    super();
    meta.initializers.DataToken(this, args);
  }
  get typeTag(): "DataToken" {
        return "DataToken";
      }
}
export const DataToken = ClsDataToken;

type KnownHostLessPackageInfo = ClsHostLessPackageInfo;
export function isKnownHostLessPackageInfo<T>(x: [Extract<T, HostLessPackageInfo>] extends [never] ? never : T): x is [Extract<T, HostLessPackageInfo>] extends [never]
  ? never
  : HostLessPackageInfo extends T // Needed when T is any
  ? HostLessPackageInfo
  : Extract<T, HostLessPackageInfo> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseHostLessPackageInfo;
}

    export function ensureKnownHostLessPackageInfo<T>(x: [Extract<T, HostLessPackageInfo>] extends [never] ? never : T): HostLessPackageInfo {
      assert(isKnownHostLessPackageInfo(x), () => mkUnexpectedTypeMsg([HostLessPackageInfo], x));
      return x;
    }
    export function ensureMaybeKnownHostLessPackageInfo<T>(x: [Extract<T, HostLessPackageInfo>] extends [never] ? never : T): HostLessPackageInfo | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownHostLessPackageInfo(x as any);
    }
    
export type HostLessPackageInfo = KnownHostLessPackageInfo;
export interface HostLessPackageInfoParams {
  name: string /*  */;
npmPkg: ReadonlyArray<string> /*  */;
cssImport: ReadonlyArray<string> /*  */;
deps: ReadonlyArray<string> /*  */;
registerCalls: ReadonlyArray<string> /*  */;
minimumReactVersion: string | null | undefined /*  */;
}

abstract class BaseHostLessPackageInfo extends ModelInst {
  
    static isKnown(x: any): x is HostLessPackageInfo { return isKnownHostLessPackageInfo(x); }
    static getType(): HostLessPackageInfo { throw new Error(); }
    static modelTypeName = "HostLessPackageInfo";
    
  static readonly $fields = meta.allFields(meta.clsByName["HostLessPackageInfo"]);
  static readonly $field_name: Field = ensure(
    BaseHostLessPackageInfo.$fields.find((f) => f.name === "name"),
    "HostLessPackageInfo has no field name",
  );
static readonly $field_npmPkg: Field = ensure(
    BaseHostLessPackageInfo.$fields.find((f) => f.name === "npmPkg"),
    "HostLessPackageInfo has no field npmPkg",
  );
static readonly $field_cssImport: Field = ensure(
    BaseHostLessPackageInfo.$fields.find((f) => f.name === "cssImport"),
    "HostLessPackageInfo has no field cssImport",
  );
static readonly $field_deps: Field = ensure(
    BaseHostLessPackageInfo.$fields.find((f) => f.name === "deps"),
    "HostLessPackageInfo has no field deps",
  );
static readonly $field_registerCalls: Field = ensure(
    BaseHostLessPackageInfo.$fields.find((f) => f.name === "registerCalls"),
    "HostLessPackageInfo has no field registerCalls",
  );
static readonly $field_minimumReactVersion: Field = ensure(
    BaseHostLessPackageInfo.$fields.find((f) => f.name === "minimumReactVersion"),
    "HostLessPackageInfo has no field minimumReactVersion",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseHostLessPackageInfo.$field_name, value); }
get npmPkg(): ReadonlyArray<string> { meta.reportRead(this, "npmPkg"); return (this as any).$v.npmPkg; }
set npmPkg(value: ReadonlyArray<string>) { meta.writeField(this, BaseHostLessPackageInfo.$field_npmPkg, value); }
get cssImport(): ReadonlyArray<string> { meta.reportRead(this, "cssImport"); return (this as any).$v.cssImport; }
set cssImport(value: ReadonlyArray<string>) { meta.writeField(this, BaseHostLessPackageInfo.$field_cssImport, value); }
get deps(): ReadonlyArray<string> { meta.reportRead(this, "deps"); return (this as any).$v.deps; }
set deps(value: ReadonlyArray<string>) { meta.writeField(this, BaseHostLessPackageInfo.$field_deps, value); }
get registerCalls(): ReadonlyArray<string> { meta.reportRead(this, "registerCalls"); return (this as any).$v.registerCalls; }
set registerCalls(value: ReadonlyArray<string>) { meta.writeField(this, BaseHostLessPackageInfo.$field_registerCalls, value); }
get minimumReactVersion(): string | null | undefined { meta.reportRead(this, "minimumReactVersion"); return (this as any).$v.minimumReactVersion; }
set minimumReactVersion(value: string | null | undefined) { meta.writeField(this, BaseHostLessPackageInfo.$field_minimumReactVersion, value); }
}
class ClsHostLessPackageInfo extends BaseHostLessPackageInfo {
  constructor(args: HostLessPackageInfoParams) {
    super();
    meta.initializers.HostLessPackageInfo(this, args);
  }
  get typeTag(): "HostLessPackageInfo" {
        return "HostLessPackageInfo";
      }
}
export const HostLessPackageInfo = ClsHostLessPackageInfo;

type KnownSite = ClsSite;
export function isKnownSite<T>(x: [Extract<T, Site>] extends [never] ? never : T): x is [Extract<T, Site>] extends [never]
  ? never
  : Site extends T // Needed when T is any
  ? Site
  : Extract<T, Site> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSite;
}

    export function ensureKnownSite<T>(x: [Extract<T, Site>] extends [never] ? never : T): Site {
      assert(isKnownSite(x), () => mkUnexpectedTypeMsg([Site], x));
      return x;
    }
    export function ensureMaybeKnownSite<T>(x: [Extract<T, Site>] extends [never] ? never : T): Site | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSite(x as any);
    }
    
export type Site = KnownSite;
export interface SiteParams {
  components: ReadonlyArray<Component> /*  */;
arenas: ReadonlyArray<Arena> /*  */;
pageArenas: ReadonlyArray<PageArena> /*  */;
componentArenas: ReadonlyArray<ComponentArena> /*  */;
globalVariantGroups: ReadonlyArray<GlobalVariantGroup> /*  */;
userManagedFonts: ReadonlyArray<string> /*  */;
readonly globalVariant: Variant /* Const */;
styleTokens: ReadonlyArray<StyleToken> /*  */;
styleTokenOverrides: ReadonlyArray<StyleTokenOverride> /*  */;
dataTokens: ReadonlyArray<DataToken> /*  */;
mixins: ReadonlyArray<Mixin> /*  */;
animationSequences: ReadonlyArray<AnimationSequence> /*  */;
readonly themes: ReadonlyArray<Theme> /* Const */;
activeTheme: Theme | null | undefined /* WeakRef */;
imageAssets: ReadonlyArray<ImageAsset> /*  */;
projectDependencies: ReadonlyArray<ProjectDependency> /* WeakRef */;
activeScreenVariantGroup: GlobalVariantGroup | null | undefined /* WeakRef */;
flags: Readonly<Record<string, string | boolean | number | null | undefined>> /*  */;
readonly hostLessPackageInfo: HostLessPackageInfo | null | undefined /* Const */;
globalContexts: ReadonlyArray<TplComponent> /*  */;
splits: ReadonlyArray<Split> /*  */;
defaultComponents: Readonly<Record<string, Component>> /* WeakRef */;
defaultPageRoleId: string | null | undefined /*  */;
pageWrapper: Component | null | undefined /* WeakRef */;
customFunctions: ReadonlyArray<CustomFunction> /*  */;
codeLibraries: ReadonlyArray<CodeLibrary> /*  */;
}

abstract class BaseSite extends ModelInst {
  
    static isKnown(x: any): x is Site { return isKnownSite(x); }
    static getType(): Site { throw new Error(); }
    static modelTypeName = "Site";
    
  static readonly $fields = meta.allFields(meta.clsByName["Site"]);
  static readonly $field_components: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "components"),
    "Site has no field components",
  );
static readonly $field_arenas: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "arenas"),
    "Site has no field arenas",
  );
static readonly $field_pageArenas: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "pageArenas"),
    "Site has no field pageArenas",
  );
static readonly $field_componentArenas: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "componentArenas"),
    "Site has no field componentArenas",
  );
static readonly $field_globalVariantGroups: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "globalVariantGroups"),
    "Site has no field globalVariantGroups",
  );
static readonly $field_userManagedFonts: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "userManagedFonts"),
    "Site has no field userManagedFonts",
  );
static readonly $field_globalVariant: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "globalVariant"),
    "Site has no field globalVariant",
  );
static readonly $field_styleTokens: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "styleTokens"),
    "Site has no field styleTokens",
  );
static readonly $field_styleTokenOverrides: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "styleTokenOverrides"),
    "Site has no field styleTokenOverrides",
  );
static readonly $field_dataTokens: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "dataTokens"),
    "Site has no field dataTokens",
  );
static readonly $field_mixins: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "mixins"),
    "Site has no field mixins",
  );
static readonly $field_animationSequences: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "animationSequences"),
    "Site has no field animationSequences",
  );
static readonly $field_themes: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "themes"),
    "Site has no field themes",
  );
static readonly $field_activeTheme: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "activeTheme"),
    "Site has no field activeTheme",
  );
static readonly $field_imageAssets: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "imageAssets"),
    "Site has no field imageAssets",
  );
static readonly $field_projectDependencies: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "projectDependencies"),
    "Site has no field projectDependencies",
  );
static readonly $field_activeScreenVariantGroup: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "activeScreenVariantGroup"),
    "Site has no field activeScreenVariantGroup",
  );
static readonly $field_flags: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "flags"),
    "Site has no field flags",
  );
static readonly $field_hostLessPackageInfo: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "hostLessPackageInfo"),
    "Site has no field hostLessPackageInfo",
  );
static readonly $field_globalContexts: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "globalContexts"),
    "Site has no field globalContexts",
  );
static readonly $field_splits: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "splits"),
    "Site has no field splits",
  );
static readonly $field_defaultComponents: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "defaultComponents"),
    "Site has no field defaultComponents",
  );
static readonly $field_defaultPageRoleId: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "defaultPageRoleId"),
    "Site has no field defaultPageRoleId",
  );
static readonly $field_pageWrapper: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "pageWrapper"),
    "Site has no field pageWrapper",
  );
static readonly $field_customFunctions: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "customFunctions"),
    "Site has no field customFunctions",
  );
static readonly $field_codeLibraries: Field = ensure(
    BaseSite.$fields.find((f) => f.name === "codeLibraries"),
    "Site has no field codeLibraries",
  );
  get components(): ReadonlyArray<Component> { meta.reportRead(this, "components"); return (this as any).$v.components; }
set components(value: ReadonlyArray<Component>) { meta.writeField(this, BaseSite.$field_components, value); }
get arenas(): ReadonlyArray<Arena> { meta.reportRead(this, "arenas"); return (this as any).$v.arenas; }
set arenas(value: ReadonlyArray<Arena>) { meta.writeField(this, BaseSite.$field_arenas, value); }
get pageArenas(): ReadonlyArray<PageArena> { meta.reportRead(this, "pageArenas"); return (this as any).$v.pageArenas; }
set pageArenas(value: ReadonlyArray<PageArena>) { meta.writeField(this, BaseSite.$field_pageArenas, value); }
get componentArenas(): ReadonlyArray<ComponentArena> { meta.reportRead(this, "componentArenas"); return (this as any).$v.componentArenas; }
set componentArenas(value: ReadonlyArray<ComponentArena>) { meta.writeField(this, BaseSite.$field_componentArenas, value); }
get globalVariantGroups(): ReadonlyArray<GlobalVariantGroup> { meta.reportRead(this, "globalVariantGroups"); return (this as any).$v.globalVariantGroups; }
set globalVariantGroups(value: ReadonlyArray<GlobalVariantGroup>) { meta.writeField(this, BaseSite.$field_globalVariantGroups, value); }
get userManagedFonts(): ReadonlyArray<string> { meta.reportRead(this, "userManagedFonts"); return (this as any).$v.userManagedFonts; }
set userManagedFonts(value: ReadonlyArray<string>) { meta.writeField(this, BaseSite.$field_userManagedFonts, value); }
/* Const */
get globalVariant(): Variant { meta.reportRead(this, "globalVariant"); return (this as any).$v.globalVariant; }
private set globalVariant(value: Variant) { meta.writeField(this, BaseSite.$field_globalVariant, value); }
get styleTokens(): ReadonlyArray<StyleToken> { meta.reportRead(this, "styleTokens"); return (this as any).$v.styleTokens; }
set styleTokens(value: ReadonlyArray<StyleToken>) { meta.writeField(this, BaseSite.$field_styleTokens, value); }
get styleTokenOverrides(): ReadonlyArray<StyleTokenOverride> { meta.reportRead(this, "styleTokenOverrides"); return (this as any).$v.styleTokenOverrides; }
set styleTokenOverrides(value: ReadonlyArray<StyleTokenOverride>) { meta.writeField(this, BaseSite.$field_styleTokenOverrides, value); }
get dataTokens(): ReadonlyArray<DataToken> { meta.reportRead(this, "dataTokens"); return (this as any).$v.dataTokens; }
set dataTokens(value: ReadonlyArray<DataToken>) { meta.writeField(this, BaseSite.$field_dataTokens, value); }
get mixins(): ReadonlyArray<Mixin> { meta.reportRead(this, "mixins"); return (this as any).$v.mixins; }
set mixins(value: ReadonlyArray<Mixin>) { meta.writeField(this, BaseSite.$field_mixins, value); }
get animationSequences(): ReadonlyArray<AnimationSequence> { meta.reportRead(this, "animationSequences"); return (this as any).$v.animationSequences; }
set animationSequences(value: ReadonlyArray<AnimationSequence>) { meta.writeField(this, BaseSite.$field_animationSequences, value); }
/* Const */
get themes(): ReadonlyArray<Theme> { meta.reportRead(this, "themes"); return (this as any).$v.themes; }
private set themes(value: ReadonlyArray<Theme>) { meta.writeField(this, BaseSite.$field_themes, value); }
/* WeakRef */
get activeTheme(): Theme | null | undefined { meta.reportRead(this, "activeTheme"); return (this as any).$v.activeTheme; }
set activeTheme(value: Theme | null | undefined) { meta.writeField(this, BaseSite.$field_activeTheme, value); }
get imageAssets(): ReadonlyArray<ImageAsset> { meta.reportRead(this, "imageAssets"); return (this as any).$v.imageAssets; }
set imageAssets(value: ReadonlyArray<ImageAsset>) { meta.writeField(this, BaseSite.$field_imageAssets, value); }
/* WeakRef */
get projectDependencies(): ReadonlyArray<ProjectDependency> { meta.reportRead(this, "projectDependencies"); return (this as any).$v.projectDependencies; }
set projectDependencies(value: ReadonlyArray<ProjectDependency>) { meta.writeField(this, BaseSite.$field_projectDependencies, value); }
/* WeakRef */
get activeScreenVariantGroup(): GlobalVariantGroup | null | undefined { meta.reportRead(this, "activeScreenVariantGroup"); return (this as any).$v.activeScreenVariantGroup; }
set activeScreenVariantGroup(value: GlobalVariantGroup | null | undefined) { meta.writeField(this, BaseSite.$field_activeScreenVariantGroup, value); }
get flags(): Readonly<Record<string, string | boolean | number | null | undefined>> { meta.reportRead(this, "flags"); return (this as any).$v.flags; }
set flags(value: Readonly<Record<string, string | boolean | number | null | undefined>>) { meta.writeField(this, BaseSite.$field_flags, value); }
/* Const */
get hostLessPackageInfo(): HostLessPackageInfo | null | undefined { meta.reportRead(this, "hostLessPackageInfo"); return (this as any).$v.hostLessPackageInfo; }
private set hostLessPackageInfo(value: HostLessPackageInfo | null | undefined) { meta.writeField(this, BaseSite.$field_hostLessPackageInfo, value); }
get globalContexts(): ReadonlyArray<TplComponent> { meta.reportRead(this, "globalContexts"); return (this as any).$v.globalContexts; }
set globalContexts(value: ReadonlyArray<TplComponent>) { meta.writeField(this, BaseSite.$field_globalContexts, value); }
get splits(): ReadonlyArray<Split> { meta.reportRead(this, "splits"); return (this as any).$v.splits; }
set splits(value: ReadonlyArray<Split>) { meta.writeField(this, BaseSite.$field_splits, value); }
/* WeakRef */
get defaultComponents(): Readonly<Record<string, Component>> { meta.reportRead(this, "defaultComponents"); return (this as any).$v.defaultComponents; }
set defaultComponents(value: Readonly<Record<string, Component>>) { meta.writeField(this, BaseSite.$field_defaultComponents, value); }
get defaultPageRoleId(): string | null | undefined { meta.reportRead(this, "defaultPageRoleId"); return (this as any).$v.defaultPageRoleId; }
set defaultPageRoleId(value: string | null | undefined) { meta.writeField(this, BaseSite.$field_defaultPageRoleId, value); }
/* WeakRef */
get pageWrapper(): Component | null | undefined { meta.reportRead(this, "pageWrapper"); return (this as any).$v.pageWrapper; }
set pageWrapper(value: Component | null | undefined) { meta.writeField(this, BaseSite.$field_pageWrapper, value); }
get customFunctions(): ReadonlyArray<CustomFunction> { meta.reportRead(this, "customFunctions"); return (this as any).$v.customFunctions; }
set customFunctions(value: ReadonlyArray<CustomFunction>) { meta.writeField(this, BaseSite.$field_customFunctions, value); }
get codeLibraries(): ReadonlyArray<CodeLibrary> { meta.reportRead(this, "codeLibraries"); return (this as any).$v.codeLibraries; }
set codeLibraries(value: ReadonlyArray<CodeLibrary>) { meta.writeField(this, BaseSite.$field_codeLibraries, value); }
}
class ClsSite extends BaseSite {
  constructor(args: SiteParams) {
    super();
    meta.initializers.Site(this, args);
  }
  get typeTag(): "Site" {
        return "Site";
      }
}
export const Site = ClsSite;

type KnownArenaFrameGrid = ClsArenaFrameGrid;
export function isKnownArenaFrameGrid<T>(x: [Extract<T, ArenaFrameGrid>] extends [never] ? never : T): x is [Extract<T, ArenaFrameGrid>] extends [never]
  ? never
  : ArenaFrameGrid extends T // Needed when T is any
  ? ArenaFrameGrid
  : Extract<T, ArenaFrameGrid> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArenaFrameGrid;
}

    export function ensureKnownArenaFrameGrid<T>(x: [Extract<T, ArenaFrameGrid>] extends [never] ? never : T): ArenaFrameGrid {
      assert(isKnownArenaFrameGrid(x), () => mkUnexpectedTypeMsg([ArenaFrameGrid], x));
      return x;
    }
    export function ensureMaybeKnownArenaFrameGrid<T>(x: [Extract<T, ArenaFrameGrid>] extends [never] ? never : T): ArenaFrameGrid | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArenaFrameGrid(x as any);
    }
    
export type ArenaFrameGrid = KnownArenaFrameGrid;
export interface ArenaFrameGridParams {
  rows: ReadonlyArray<ArenaFrameRow> /*  */;
}

abstract class BaseArenaFrameGrid extends ModelInst {
  
    static isKnown(x: any): x is ArenaFrameGrid { return isKnownArenaFrameGrid(x); }
    static getType(): ArenaFrameGrid { throw new Error(); }
    static modelTypeName = "ArenaFrameGrid";
    
  static readonly $fields = meta.allFields(meta.clsByName["ArenaFrameGrid"]);
  static readonly $field_rows: Field = ensure(
    BaseArenaFrameGrid.$fields.find((f) => f.name === "rows"),
    "ArenaFrameGrid has no field rows",
  );
  get rows(): ReadonlyArray<ArenaFrameRow> { meta.reportRead(this, "rows"); return (this as any).$v.rows; }
set rows(value: ReadonlyArray<ArenaFrameRow>) { meta.writeField(this, BaseArenaFrameGrid.$field_rows, value); }
}
class ClsArenaFrameGrid extends BaseArenaFrameGrid {
  constructor(args: ArenaFrameGridParams) {
    super();
    meta.initializers.ArenaFrameGrid(this, args);
  }
  get typeTag(): "ArenaFrameGrid" {
        return "ArenaFrameGrid";
      }
}
export const ArenaFrameGrid = ClsArenaFrameGrid;

type KnownArenaFrameRow = ClsArenaFrameRow;
export function isKnownArenaFrameRow<T>(x: [Extract<T, ArenaFrameRow>] extends [never] ? never : T): x is [Extract<T, ArenaFrameRow>] extends [never]
  ? never
  : ArenaFrameRow extends T // Needed when T is any
  ? ArenaFrameRow
  : Extract<T, ArenaFrameRow> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArenaFrameRow;
}

    export function ensureKnownArenaFrameRow<T>(x: [Extract<T, ArenaFrameRow>] extends [never] ? never : T): ArenaFrameRow {
      assert(isKnownArenaFrameRow(x), () => mkUnexpectedTypeMsg([ArenaFrameRow], x));
      return x;
    }
    export function ensureMaybeKnownArenaFrameRow<T>(x: [Extract<T, ArenaFrameRow>] extends [never] ? never : T): ArenaFrameRow | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArenaFrameRow(x as any);
    }
    
export type ArenaFrameRow = KnownArenaFrameRow;
export interface ArenaFrameRowParams {
  cols: ReadonlyArray<ArenaFrameCell> /*  */;
rowKey: VariantGroup | null | undefined | Variant | null | undefined /* WeakRef */;
}

abstract class BaseArenaFrameRow extends ModelInst {
  
    static isKnown(x: any): x is ArenaFrameRow { return isKnownArenaFrameRow(x); }
    static getType(): ArenaFrameRow { throw new Error(); }
    static modelTypeName = "ArenaFrameRow";
    
  static readonly $fields = meta.allFields(meta.clsByName["ArenaFrameRow"]);
  static readonly $field_cols: Field = ensure(
    BaseArenaFrameRow.$fields.find((f) => f.name === "cols"),
    "ArenaFrameRow has no field cols",
  );
static readonly $field_rowKey: Field = ensure(
    BaseArenaFrameRow.$fields.find((f) => f.name === "rowKey"),
    "ArenaFrameRow has no field rowKey",
  );
  get cols(): ReadonlyArray<ArenaFrameCell> { meta.reportRead(this, "cols"); return (this as any).$v.cols; }
set cols(value: ReadonlyArray<ArenaFrameCell>) { meta.writeField(this, BaseArenaFrameRow.$field_cols, value); }
/* WeakRef */
get rowKey(): VariantGroup | null | undefined | Variant | null | undefined { meta.reportRead(this, "rowKey"); return (this as any).$v.rowKey; }
set rowKey(value: VariantGroup | null | undefined | Variant | null | undefined) { meta.writeField(this, BaseArenaFrameRow.$field_rowKey, value); }
}
class ClsArenaFrameRow extends BaseArenaFrameRow {
  constructor(args: ArenaFrameRowParams) {
    super();
    meta.initializers.ArenaFrameRow(this, args);
  }
  get typeTag(): "ArenaFrameRow" {
        return "ArenaFrameRow";
      }
}
export const ArenaFrameRow = ClsArenaFrameRow;

type KnownArenaFrameCell = ClsArenaFrameCell;
export function isKnownArenaFrameCell<T>(x: [Extract<T, ArenaFrameCell>] extends [never] ? never : T): x is [Extract<T, ArenaFrameCell>] extends [never]
  ? never
  : ArenaFrameCell extends T // Needed when T is any
  ? ArenaFrameCell
  : Extract<T, ArenaFrameCell> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArenaFrameCell;
}

    export function ensureKnownArenaFrameCell<T>(x: [Extract<T, ArenaFrameCell>] extends [never] ? never : T): ArenaFrameCell {
      assert(isKnownArenaFrameCell(x), () => mkUnexpectedTypeMsg([ArenaFrameCell], x));
      return x;
    }
    export function ensureMaybeKnownArenaFrameCell<T>(x: [Extract<T, ArenaFrameCell>] extends [never] ? never : T): ArenaFrameCell | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArenaFrameCell(x as any);
    }
    
export type ArenaFrameCell = KnownArenaFrameCell;
export interface ArenaFrameCellParams {
  readonly frame: ArenaFrame /* Const */;
cellKey: Variant | null | undefined | ReadonlyArray<Variant> | null | undefined /* WeakRef */;
}

abstract class BaseArenaFrameCell extends ModelInst {
  
    static isKnown(x: any): x is ArenaFrameCell { return isKnownArenaFrameCell(x); }
    static getType(): ArenaFrameCell { throw new Error(); }
    static modelTypeName = "ArenaFrameCell";
    
  static readonly $fields = meta.allFields(meta.clsByName["ArenaFrameCell"]);
  static readonly $field_frame: Field = ensure(
    BaseArenaFrameCell.$fields.find((f) => f.name === "frame"),
    "ArenaFrameCell has no field frame",
  );
static readonly $field_cellKey: Field = ensure(
    BaseArenaFrameCell.$fields.find((f) => f.name === "cellKey"),
    "ArenaFrameCell has no field cellKey",
  );
  /* Const */
get frame(): ArenaFrame { meta.reportRead(this, "frame"); return (this as any).$v.frame; }
private set frame(value: ArenaFrame) { meta.writeField(this, BaseArenaFrameCell.$field_frame, value); }
/* WeakRef */
get cellKey(): Variant | null | undefined | ReadonlyArray<Variant> | null | undefined { meta.reportRead(this, "cellKey"); return (this as any).$v.cellKey; }
set cellKey(value: Variant | null | undefined | ReadonlyArray<Variant> | null | undefined) { meta.writeField(this, BaseArenaFrameCell.$field_cellKey, value); }
}
class ClsArenaFrameCell extends BaseArenaFrameCell {
  constructor(args: ArenaFrameCellParams) {
    super();
    meta.initializers.ArenaFrameCell(this, args);
  }
  get typeTag(): "ArenaFrameCell" {
        return "ArenaFrameCell";
      }
}
export const ArenaFrameCell = ClsArenaFrameCell;

type KnownComponentArena = ClsComponentArena;
export function isKnownComponentArena<T>(x: [Extract<T, ComponentArena>] extends [never] ? never : T): x is [Extract<T, ComponentArena>] extends [never]
  ? never
  : ComponentArena extends T // Needed when T is any
  ? ComponentArena
  : Extract<T, ComponentArena> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentArena;
}

    export function ensureKnownComponentArena<T>(x: [Extract<T, ComponentArena>] extends [never] ? never : T): ComponentArena {
      assert(isKnownComponentArena(x), () => mkUnexpectedTypeMsg([ComponentArena], x));
      return x;
    }
    export function ensureMaybeKnownComponentArena<T>(x: [Extract<T, ComponentArena>] extends [never] ? never : T): ComponentArena | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentArena(x as any);
    }
    
export type ComponentArena = KnownComponentArena;
export interface ComponentArenaParams {
  readonly component: Component /* Const,WeakRef */;
_focusedFrame?: ArenaFrame | null | undefined /* Transient */;
matrix: ArenaFrameGrid /*  */;
customMatrix: ArenaFrameGrid /*  */;
}

abstract class BaseComponentArena extends ModelInst {
  
    static isKnown(x: any): x is ComponentArena { return isKnownComponentArena(x); }
    static getType(): ComponentArena { throw new Error(); }
    static modelTypeName = "ComponentArena";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentArena"]);
  static readonly $field_component: Field = ensure(
    BaseComponentArena.$fields.find((f) => f.name === "component"),
    "ComponentArena has no field component",
  );
static readonly $field__focusedFrame: Field = ensure(
    BaseComponentArena.$fields.find((f) => f.name === "_focusedFrame"),
    "ComponentArena has no field _focusedFrame",
  );
static readonly $field_matrix: Field = ensure(
    BaseComponentArena.$fields.find((f) => f.name === "matrix"),
    "ComponentArena has no field matrix",
  );
static readonly $field_customMatrix: Field = ensure(
    BaseComponentArena.$fields.find((f) => f.name === "customMatrix"),
    "ComponentArena has no field customMatrix",
  );
  /* Const,WeakRef */
get component(): Component { meta.reportRead(this, "component"); return (this as any).$v.component; }
private set component(value: Component) { meta.writeField(this, BaseComponentArena.$field_component, value); }
/* Transient */
get _focusedFrame(): ArenaFrame | null | undefined | null | undefined { meta.reportRead(this, "_focusedFrame"); return (this as any).$v._focusedFrame; }
set _focusedFrame(value: ArenaFrame | null | undefined | null | undefined) { meta.writeField(this, BaseComponentArena.$field__focusedFrame, value); }
get matrix(): ArenaFrameGrid { meta.reportRead(this, "matrix"); return (this as any).$v.matrix; }
set matrix(value: ArenaFrameGrid) { meta.writeField(this, BaseComponentArena.$field_matrix, value); }
get customMatrix(): ArenaFrameGrid { meta.reportRead(this, "customMatrix"); return (this as any).$v.customMatrix; }
set customMatrix(value: ArenaFrameGrid) { meta.writeField(this, BaseComponentArena.$field_customMatrix, value); }
}
class ClsComponentArena extends BaseComponentArena {
  constructor(args: ComponentArenaParams) {
    super();
    meta.initializers.ComponentArena(this, args);
  }
  get typeTag(): "ComponentArena" {
        return "ComponentArena";
      }
}
export const ComponentArena = ClsComponentArena;

type KnownPageArena = ClsPageArena;
export function isKnownPageArena<T>(x: [Extract<T, PageArena>] extends [never] ? never : T): x is [Extract<T, PageArena>] extends [never]
  ? never
  : PageArena extends T // Needed when T is any
  ? PageArena
  : Extract<T, PageArena> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePageArena;
}

    export function ensureKnownPageArena<T>(x: [Extract<T, PageArena>] extends [never] ? never : T): PageArena {
      assert(isKnownPageArena(x), () => mkUnexpectedTypeMsg([PageArena], x));
      return x;
    }
    export function ensureMaybeKnownPageArena<T>(x: [Extract<T, PageArena>] extends [never] ? never : T): PageArena | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPageArena(x as any);
    }
    
export type PageArena = KnownPageArena;
export interface PageArenaParams {
  readonly component: Component /* Const,WeakRef */;
_focusedFrame?: ArenaFrame | null | undefined /* Transient */;
matrix: ArenaFrameGrid /*  */;
customMatrix: ArenaFrameGrid /*  */;
}

abstract class BasePageArena extends ModelInst {
  
    static isKnown(x: any): x is PageArena { return isKnownPageArena(x); }
    static getType(): PageArena { throw new Error(); }
    static modelTypeName = "PageArena";
    
  static readonly $fields = meta.allFields(meta.clsByName["PageArena"]);
  static readonly $field_component: Field = ensure(
    BasePageArena.$fields.find((f) => f.name === "component"),
    "PageArena has no field component",
  );
static readonly $field__focusedFrame: Field = ensure(
    BasePageArena.$fields.find((f) => f.name === "_focusedFrame"),
    "PageArena has no field _focusedFrame",
  );
static readonly $field_matrix: Field = ensure(
    BasePageArena.$fields.find((f) => f.name === "matrix"),
    "PageArena has no field matrix",
  );
static readonly $field_customMatrix: Field = ensure(
    BasePageArena.$fields.find((f) => f.name === "customMatrix"),
    "PageArena has no field customMatrix",
  );
  /* Const,WeakRef */
get component(): Component { meta.reportRead(this, "component"); return (this as any).$v.component; }
private set component(value: Component) { meta.writeField(this, BasePageArena.$field_component, value); }
/* Transient */
get _focusedFrame(): ArenaFrame | null | undefined | null | undefined { meta.reportRead(this, "_focusedFrame"); return (this as any).$v._focusedFrame; }
set _focusedFrame(value: ArenaFrame | null | undefined | null | undefined) { meta.writeField(this, BasePageArena.$field__focusedFrame, value); }
get matrix(): ArenaFrameGrid { meta.reportRead(this, "matrix"); return (this as any).$v.matrix; }
set matrix(value: ArenaFrameGrid) { meta.writeField(this, BasePageArena.$field_matrix, value); }
get customMatrix(): ArenaFrameGrid { meta.reportRead(this, "customMatrix"); return (this as any).$v.customMatrix; }
set customMatrix(value: ArenaFrameGrid) { meta.writeField(this, BasePageArena.$field_customMatrix, value); }
}
class ClsPageArena extends BasePageArena {
  constructor(args: PageArenaParams) {
    super();
    meta.initializers.PageArena(this, args);
  }
  get typeTag(): "PageArena" {
        return "PageArena";
      }
}
export const PageArena = ClsPageArena;

type KnownArena = ClsArena;
export function isKnownArena<T>(x: [Extract<T, Arena>] extends [never] ? never : T): x is [Extract<T, Arena>] extends [never]
  ? never
  : Arena extends T // Needed when T is any
  ? Arena
  : Extract<T, Arena> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArena;
}

    export function ensureKnownArena<T>(x: [Extract<T, Arena>] extends [never] ? never : T): Arena {
      assert(isKnownArena(x), () => mkUnexpectedTypeMsg([Arena], x));
      return x;
    }
    export function ensureMaybeKnownArena<T>(x: [Extract<T, Arena>] extends [never] ? never : T): Arena | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArena(x as any);
    }
    
export type Arena = KnownArena;
export interface ArenaParams {
  name: string /*  */;
children: ReadonlyArray<ArenaChild> /*  */;
}

abstract class BaseArena extends ModelInst {
  
    static isKnown(x: any): x is Arena { return isKnownArena(x); }
    static getType(): Arena { throw new Error(); }
    static modelTypeName = "Arena";
    
  static readonly $fields = meta.allFields(meta.clsByName["Arena"]);
  static readonly $field_name: Field = ensure(
    BaseArena.$fields.find((f) => f.name === "name"),
    "Arena has no field name",
  );
static readonly $field_children: Field = ensure(
    BaseArena.$fields.find((f) => f.name === "children"),
    "Arena has no field children",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseArena.$field_name, value); }
get children(): ReadonlyArray<ArenaChild> { meta.reportRead(this, "children"); return (this as any).$v.children; }
set children(value: ReadonlyArray<ArenaChild>) { meta.writeField(this, BaseArena.$field_children, value); }
}
class ClsArena extends BaseArena {
  constructor(args: ArenaParams) {
    super();
    meta.initializers.Arena(this, args);
  }
  get typeTag(): "Arena" {
        return "Arena";
      }
}
export const Arena = ClsArena;

type KnownArenaChild = KnownArenaFrame;
export function isKnownArenaChild<T>(x: [Extract<T, ArenaChild>] extends [never] ? never : T): x is [Extract<T, ArenaChild>] extends [never]
  ? never
  : ArenaChild extends T // Needed when T is any
  ? ArenaChild
  : Extract<T, ArenaChild> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArenaChild;
}

    export function ensureKnownArenaChild<T>(x: [Extract<T, ArenaChild>] extends [never] ? never : T): ArenaChild {
      assert(isKnownArenaChild(x), () => mkUnexpectedTypeMsg([ArenaChild], x));
      return x;
    }
    export function ensureMaybeKnownArenaChild<T>(x: [Extract<T, ArenaChild>] extends [never] ? never : T): ArenaChild | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArenaChild(x as any);
    }
    
export type ArenaChild = KnownArenaChild;
export interface ArenaChildParams {
  name: string /*  */;
top: number | null | undefined /*  */;
left: number | null | undefined /*  */;
}

abstract class BaseArenaChild extends ModelInst {
  
    static isKnown(x: any): x is ArenaChild { return isKnownArenaChild(x); }
    static getType(): ArenaChild { throw new Error(); }
    static modelTypeName = "ArenaChild";
    
  static readonly $fields = meta.allFields(meta.clsByName["ArenaChild"]);
  static readonly $field_name: Field = ensure(
    BaseArenaChild.$fields.find((f) => f.name === "name"),
    "ArenaChild has no field name",
  );
static readonly $field_top: Field = ensure(
    BaseArenaChild.$fields.find((f) => f.name === "top"),
    "ArenaChild has no field top",
  );
static readonly $field_left: Field = ensure(
    BaseArenaChild.$fields.find((f) => f.name === "left"),
    "ArenaChild has no field left",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseArenaChild.$field_name, value); }
get top(): number | null | undefined { meta.reportRead(this, "top"); return (this as any).$v.top; }
set top(value: number | null | undefined) { meta.writeField(this, BaseArenaChild.$field_top, value); }
get left(): number | null | undefined { meta.reportRead(this, "left"); return (this as any).$v.left; }
set left(value: number | null | undefined) { meta.writeField(this, BaseArenaChild.$field_left, value); }
}

export const ArenaChild = BaseArenaChild;

type KnownArenaFrame = ClsArenaFrame;
export function isKnownArenaFrame<T>(x: [Extract<T, ArenaFrame>] extends [never] ? never : T): x is [Extract<T, ArenaFrame>] extends [never]
  ? never
  : ArenaFrame extends T // Needed when T is any
  ? ArenaFrame
  : Extract<T, ArenaFrame> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArenaFrame;
}

    export function ensureKnownArenaFrame<T>(x: [Extract<T, ArenaFrame>] extends [never] ? never : T): ArenaFrame {
      assert(isKnownArenaFrame(x), () => mkUnexpectedTypeMsg([ArenaFrame], x));
      return x;
    }
    export function ensureMaybeKnownArenaFrame<T>(x: [Extract<T, ArenaFrame>] extends [never] ? never : T): ArenaFrame | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArenaFrame(x as any);
    }
    
export type ArenaFrame = KnownArenaFrame;
export interface ArenaFrameParams {
  readonly uuid: string /* Const */;
width: number /*  */;
height: number /*  */;
container: TplComponent /*  */;
lang: string /*  */;
pinnedVariants: Readonly<Record<string, boolean>> /*  */;
targetVariants: ReadonlyArray<Variant> /* WeakRef */;
pinnedGlobalVariants: Readonly<Record<string, boolean>> /*  */;
targetGlobalVariants: ReadonlyArray<Variant> /* WeakRef */;
viewMode: "stretch" | "centered" /*  */;
bgColor: string | null | undefined /*  */;
name: string /*  */;
top: number | null | undefined /*  */;
left: number | null | undefined /*  */;
}

abstract class BaseArenaFrame extends BaseArenaChild {
  
    static isKnown(x: any): x is ArenaFrame { return isKnownArenaFrame(x); }
    static getType(): ArenaFrame { throw new Error(); }
    static modelTypeName = "ArenaFrame";
    
  static readonly $fields = meta.allFields(meta.clsByName["ArenaFrame"]);
  static readonly $field_uuid: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "uuid"),
    "ArenaFrame has no field uuid",
  );
static readonly $field_width: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "width"),
    "ArenaFrame has no field width",
  );
static readonly $field_height: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "height"),
    "ArenaFrame has no field height",
  );
static readonly $field_container: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "container"),
    "ArenaFrame has no field container",
  );
static readonly $field_lang: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "lang"),
    "ArenaFrame has no field lang",
  );
static readonly $field_pinnedVariants: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "pinnedVariants"),
    "ArenaFrame has no field pinnedVariants",
  );
static readonly $field_targetVariants: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "targetVariants"),
    "ArenaFrame has no field targetVariants",
  );
static readonly $field_pinnedGlobalVariants: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "pinnedGlobalVariants"),
    "ArenaFrame has no field pinnedGlobalVariants",
  );
static readonly $field_targetGlobalVariants: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "targetGlobalVariants"),
    "ArenaFrame has no field targetGlobalVariants",
  );
static readonly $field_viewMode: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "viewMode"),
    "ArenaFrame has no field viewMode",
  );
static readonly $field_bgColor: Field = ensure(
    BaseArenaFrame.$fields.find((f) => f.name === "bgColor"),
    "ArenaFrame has no field bgColor",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseArenaFrame.$field_uuid, value); }
get width(): number { meta.reportRead(this, "width"); return (this as any).$v.width; }
set width(value: number) { meta.writeField(this, BaseArenaFrame.$field_width, value); }
get height(): number { meta.reportRead(this, "height"); return (this as any).$v.height; }
set height(value: number) { meta.writeField(this, BaseArenaFrame.$field_height, value); }
get container(): TplComponent { meta.reportRead(this, "container"); return (this as any).$v.container; }
set container(value: TplComponent) { meta.writeField(this, BaseArenaFrame.$field_container, value); }
get lang(): string { meta.reportRead(this, "lang"); return (this as any).$v.lang; }
set lang(value: string) { meta.writeField(this, BaseArenaFrame.$field_lang, value); }
get pinnedVariants(): Readonly<Record<string, boolean>> { meta.reportRead(this, "pinnedVariants"); return (this as any).$v.pinnedVariants; }
set pinnedVariants(value: Readonly<Record<string, boolean>>) { meta.writeField(this, BaseArenaFrame.$field_pinnedVariants, value); }
/* WeakRef */
get targetVariants(): ReadonlyArray<Variant> { meta.reportRead(this, "targetVariants"); return (this as any).$v.targetVariants; }
set targetVariants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseArenaFrame.$field_targetVariants, value); }
get pinnedGlobalVariants(): Readonly<Record<string, boolean>> { meta.reportRead(this, "pinnedGlobalVariants"); return (this as any).$v.pinnedGlobalVariants; }
set pinnedGlobalVariants(value: Readonly<Record<string, boolean>>) { meta.writeField(this, BaseArenaFrame.$field_pinnedGlobalVariants, value); }
/* WeakRef */
get targetGlobalVariants(): ReadonlyArray<Variant> { meta.reportRead(this, "targetGlobalVariants"); return (this as any).$v.targetGlobalVariants; }
set targetGlobalVariants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseArenaFrame.$field_targetGlobalVariants, value); }
get viewMode(): "stretch" | "centered" { meta.reportRead(this, "viewMode"); return (this as any).$v.viewMode; }
set viewMode(value: "stretch" | "centered") { meta.writeField(this, BaseArenaFrame.$field_viewMode, value); }
get bgColor(): string | null | undefined { meta.reportRead(this, "bgColor"); return (this as any).$v.bgColor; }
set bgColor(value: string | null | undefined) { meta.writeField(this, BaseArenaFrame.$field_bgColor, value); }
}
class ClsArenaFrame extends BaseArenaFrame {
  constructor(args: ArenaFrameParams) {
    super();
    meta.initializers.ArenaFrame(this, args);
  }
  get typeTag(): "ArenaFrame" {
        return "ArenaFrame";
      }
}
export const ArenaFrame = ClsArenaFrame;

type KnownCustomFunction = ClsCustomFunction;
export function isKnownCustomFunction<T>(x: [Extract<T, CustomFunction>] extends [never] ? never : T): x is [Extract<T, CustomFunction>] extends [never]
  ? never
  : CustomFunction extends T // Needed when T is any
  ? CustomFunction
  : Extract<T, CustomFunction> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCustomFunction;
}

    export function ensureKnownCustomFunction<T>(x: [Extract<T, CustomFunction>] extends [never] ? never : T): CustomFunction {
      assert(isKnownCustomFunction(x), () => mkUnexpectedTypeMsg([CustomFunction], x));
      return x;
    }
    export function ensureMaybeKnownCustomFunction<T>(x: [Extract<T, CustomFunction>] extends [never] ? never : T): CustomFunction | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCustomFunction(x as any);
    }
    
export type CustomFunction = KnownCustomFunction;
export interface CustomFunctionParams {
  importPath: string /*  */;
importName: string /*  */;
defaultExport: boolean /*  */;
namespace: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
params: ReadonlyArray<ArgType> /*  */;
isQuery: boolean /*  */;
isMutation: boolean /*  */;
}

abstract class BaseCustomFunction extends ModelInst {
  
    static isKnown(x: any): x is CustomFunction { return isKnownCustomFunction(x); }
    static getType(): CustomFunction { throw new Error(); }
    static modelTypeName = "CustomFunction";
    
  static readonly $fields = meta.allFields(meta.clsByName["CustomFunction"]);
  static readonly $field_importPath: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "importPath"),
    "CustomFunction has no field importPath",
  );
static readonly $field_importName: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "importName"),
    "CustomFunction has no field importName",
  );
static readonly $field_defaultExport: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "defaultExport"),
    "CustomFunction has no field defaultExport",
  );
static readonly $field_namespace: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "namespace"),
    "CustomFunction has no field namespace",
  );
static readonly $field_displayName: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "displayName"),
    "CustomFunction has no field displayName",
  );
static readonly $field_params: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "params"),
    "CustomFunction has no field params",
  );
static readonly $field_isQuery: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "isQuery"),
    "CustomFunction has no field isQuery",
  );
static readonly $field_isMutation: Field = ensure(
    BaseCustomFunction.$fields.find((f) => f.name === "isMutation"),
    "CustomFunction has no field isMutation",
  );
  get importPath(): string { meta.reportRead(this, "importPath"); return (this as any).$v.importPath; }
set importPath(value: string) { meta.writeField(this, BaseCustomFunction.$field_importPath, value); }
get importName(): string { meta.reportRead(this, "importName"); return (this as any).$v.importName; }
set importName(value: string) { meta.writeField(this, BaseCustomFunction.$field_importName, value); }
get defaultExport(): boolean { meta.reportRead(this, "defaultExport"); return (this as any).$v.defaultExport; }
set defaultExport(value: boolean) { meta.writeField(this, BaseCustomFunction.$field_defaultExport, value); }
get namespace(): string | null | undefined { meta.reportRead(this, "namespace"); return (this as any).$v.namespace; }
set namespace(value: string | null | undefined) { meta.writeField(this, BaseCustomFunction.$field_namespace, value); }
get displayName(): string | null | undefined { meta.reportRead(this, "displayName"); return (this as any).$v.displayName; }
set displayName(value: string | null | undefined) { meta.writeField(this, BaseCustomFunction.$field_displayName, value); }
get params(): ReadonlyArray<ArgType> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: ReadonlyArray<ArgType>) { meta.writeField(this, BaseCustomFunction.$field_params, value); }
get isQuery(): boolean { meta.reportRead(this, "isQuery"); return (this as any).$v.isQuery; }
set isQuery(value: boolean) { meta.writeField(this, BaseCustomFunction.$field_isQuery, value); }
get isMutation(): boolean { meta.reportRead(this, "isMutation"); return (this as any).$v.isMutation; }
set isMutation(value: boolean) { meta.writeField(this, BaseCustomFunction.$field_isMutation, value); }
}
class ClsCustomFunction extends BaseCustomFunction {
  constructor(args: CustomFunctionParams) {
    super();
    meta.initializers.CustomFunction(this, args);
  }
  get typeTag(): "CustomFunction" {
        return "CustomFunction";
      }
}
export const CustomFunction = ClsCustomFunction;

type KnownCodeLibrary = ClsCodeLibrary;
export function isKnownCodeLibrary<T>(x: [Extract<T, CodeLibrary>] extends [never] ? never : T): x is [Extract<T, CodeLibrary>] extends [never]
  ? never
  : CodeLibrary extends T // Needed when T is any
  ? CodeLibrary
  : Extract<T, CodeLibrary> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCodeLibrary;
}

    export function ensureKnownCodeLibrary<T>(x: [Extract<T, CodeLibrary>] extends [never] ? never : T): CodeLibrary {
      assert(isKnownCodeLibrary(x), () => mkUnexpectedTypeMsg([CodeLibrary], x));
      return x;
    }
    export function ensureMaybeKnownCodeLibrary<T>(x: [Extract<T, CodeLibrary>] extends [never] ? never : T): CodeLibrary | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCodeLibrary(x as any);
    }
    
export type CodeLibrary = KnownCodeLibrary;
export interface CodeLibraryParams {
  name: string /*  */;
importPath: string /*  */;
jsIdentifier: string /*  */;
importType: "default" | "namespace" | "named" /*  */;
namedImport: string | null | undefined /*  */;
isSyntheticDefaultImport: boolean /*  */;
}

abstract class BaseCodeLibrary extends ModelInst {
  
    static isKnown(x: any): x is CodeLibrary { return isKnownCodeLibrary(x); }
    static getType(): CodeLibrary { throw new Error(); }
    static modelTypeName = "CodeLibrary";
    
  static readonly $fields = meta.allFields(meta.clsByName["CodeLibrary"]);
  static readonly $field_name: Field = ensure(
    BaseCodeLibrary.$fields.find((f) => f.name === "name"),
    "CodeLibrary has no field name",
  );
static readonly $field_importPath: Field = ensure(
    BaseCodeLibrary.$fields.find((f) => f.name === "importPath"),
    "CodeLibrary has no field importPath",
  );
static readonly $field_jsIdentifier: Field = ensure(
    BaseCodeLibrary.$fields.find((f) => f.name === "jsIdentifier"),
    "CodeLibrary has no field jsIdentifier",
  );
static readonly $field_importType: Field = ensure(
    BaseCodeLibrary.$fields.find((f) => f.name === "importType"),
    "CodeLibrary has no field importType",
  );
static readonly $field_namedImport: Field = ensure(
    BaseCodeLibrary.$fields.find((f) => f.name === "namedImport"),
    "CodeLibrary has no field namedImport",
  );
static readonly $field_isSyntheticDefaultImport: Field = ensure(
    BaseCodeLibrary.$fields.find((f) => f.name === "isSyntheticDefaultImport"),
    "CodeLibrary has no field isSyntheticDefaultImport",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseCodeLibrary.$field_name, value); }
get importPath(): string { meta.reportRead(this, "importPath"); return (this as any).$v.importPath; }
set importPath(value: string) { meta.writeField(this, BaseCodeLibrary.$field_importPath, value); }
get jsIdentifier(): string { meta.reportRead(this, "jsIdentifier"); return (this as any).$v.jsIdentifier; }
set jsIdentifier(value: string) { meta.writeField(this, BaseCodeLibrary.$field_jsIdentifier, value); }
get importType(): "default" | "namespace" | "named" { meta.reportRead(this, "importType"); return (this as any).$v.importType; }
set importType(value: "default" | "namespace" | "named") { meta.writeField(this, BaseCodeLibrary.$field_importType, value); }
get namedImport(): string | null | undefined { meta.reportRead(this, "namedImport"); return (this as any).$v.namedImport; }
set namedImport(value: string | null | undefined) { meta.writeField(this, BaseCodeLibrary.$field_namedImport, value); }
get isSyntheticDefaultImport(): boolean { meta.reportRead(this, "isSyntheticDefaultImport"); return (this as any).$v.isSyntheticDefaultImport; }
set isSyntheticDefaultImport(value: boolean) { meta.writeField(this, BaseCodeLibrary.$field_isSyntheticDefaultImport, value); }
}
class ClsCodeLibrary extends BaseCodeLibrary {
  constructor(args: CodeLibraryParams) {
    super();
    meta.initializers.CodeLibrary(this, args);
  }
  get typeTag(): "CodeLibrary" {
        return "CodeLibrary";
      }
}
export const CodeLibrary = ClsCodeLibrary;

type KnownStyleNode = KnownRuleSet | KnownRule;
export function isKnownStyleNode<T>(x: [Extract<T, StyleNode>] extends [never] ? never : T): x is [Extract<T, StyleNode>] extends [never]
  ? never
  : StyleNode extends T // Needed when T is any
  ? StyleNode
  : Extract<T, StyleNode> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleNode;
}

    export function ensureKnownStyleNode<T>(x: [Extract<T, StyleNode>] extends [never] ? never : T): StyleNode {
      assert(isKnownStyleNode(x), () => mkUnexpectedTypeMsg([StyleNode], x));
      return x;
    }
    export function ensureMaybeKnownStyleNode<T>(x: [Extract<T, StyleNode>] extends [never] ? never : T): StyleNode | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleNode(x as any);
    }
    
export type StyleNode = KnownStyleNode;
export interface StyleNodeParams {
  
}

abstract class BaseStyleNode extends ModelInst {
  
    static isKnown(x: any): x is StyleNode { return isKnownStyleNode(x); }
    static getType(): StyleNode { throw new Error(); }
    static modelTypeName = "StyleNode";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleNode"]);
  
  
}

export const StyleNode = BaseStyleNode;

type KnownRuleSet = ClsRuleSet;
export function isKnownRuleSet<T>(x: [Extract<T, RuleSet>] extends [never] ? never : T): x is [Extract<T, RuleSet>] extends [never]
  ? never
  : RuleSet extends T // Needed when T is any
  ? RuleSet
  : Extract<T, RuleSet> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRuleSet;
}

    export function ensureKnownRuleSet<T>(x: [Extract<T, RuleSet>] extends [never] ? never : T): RuleSet {
      assert(isKnownRuleSet(x), () => mkUnexpectedTypeMsg([RuleSet], x));
      return x;
    }
    export function ensureMaybeKnownRuleSet<T>(x: [Extract<T, RuleSet>] extends [never] ? never : T): RuleSet | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRuleSet(x as any);
    }
    
export type RuleSet = KnownRuleSet;
export interface RuleSetParams {
  values: Readonly<Record<string, string>> /*  */;
mixins: ReadonlyArray<Mixin> /* WeakRef */;
animations: ReadonlyArray<Animation> | null | undefined /*  */;
}

abstract class BaseRuleSet extends BaseStyleNode {
  
    static isKnown(x: any): x is RuleSet { return isKnownRuleSet(x); }
    static getType(): RuleSet { throw new Error(); }
    static modelTypeName = "RuleSet";
    
  static readonly $fields = meta.allFields(meta.clsByName["RuleSet"]);
  static readonly $field_values: Field = ensure(
    BaseRuleSet.$fields.find((f) => f.name === "values"),
    "RuleSet has no field values",
  );
static readonly $field_mixins: Field = ensure(
    BaseRuleSet.$fields.find((f) => f.name === "mixins"),
    "RuleSet has no field mixins",
  );
static readonly $field_animations: Field = ensure(
    BaseRuleSet.$fields.find((f) => f.name === "animations"),
    "RuleSet has no field animations",
  );
  get values(): Readonly<Record<string, string>> { meta.reportRead(this, "values"); return (this as any).$v.values; }
set values(value: Readonly<Record<string, string>>) { meta.writeField(this, BaseRuleSet.$field_values, value); }
/* WeakRef */
get mixins(): ReadonlyArray<Mixin> { meta.reportRead(this, "mixins"); return (this as any).$v.mixins; }
set mixins(value: ReadonlyArray<Mixin>) { meta.writeField(this, BaseRuleSet.$field_mixins, value); }
get animations(): ReadonlyArray<Animation> | null | undefined { meta.reportRead(this, "animations"); return (this as any).$v.animations; }
set animations(value: ReadonlyArray<Animation> | null | undefined) { meta.writeField(this, BaseRuleSet.$field_animations, value); }
}
class ClsRuleSet extends BaseRuleSet {
  constructor(args: RuleSetParams) {
    super();
    meta.initializers.RuleSet(this, args);
  }
  get typeTag(): "RuleSet" {
        return "RuleSet";
      }
}
export const RuleSet = ClsRuleSet;

type KnownRule = ClsRule;
export function isKnownRule<T>(x: [Extract<T, Rule>] extends [never] ? never : T): x is [Extract<T, Rule>] extends [never]
  ? never
  : Rule extends T // Needed when T is any
  ? Rule
  : Extract<T, Rule> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRule;
}

    export function ensureKnownRule<T>(x: [Extract<T, Rule>] extends [never] ? never : T): Rule {
      assert(isKnownRule(x), () => mkUnexpectedTypeMsg([Rule], x));
      return x;
    }
    export function ensureMaybeKnownRule<T>(x: [Extract<T, Rule>] extends [never] ? never : T): Rule | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRule(x as any);
    }
    
export type Rule = KnownRule;
export interface RuleParams {
  readonly name: string /* Const */;
values: ReadonlyArray<string> /*  */;
}

abstract class BaseRule extends BaseStyleNode {
  
    static isKnown(x: any): x is Rule { return isKnownRule(x); }
    static getType(): Rule { throw new Error(); }
    static modelTypeName = "Rule";
    
  static readonly $fields = meta.allFields(meta.clsByName["Rule"]);
  static readonly $field_name: Field = ensure(
    BaseRule.$fields.find((f) => f.name === "name"),
    "Rule has no field name",
  );
static readonly $field_values: Field = ensure(
    BaseRule.$fields.find((f) => f.name === "values"),
    "Rule has no field values",
  );
  /* Const */
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
private set name(value: string) { meta.writeField(this, BaseRule.$field_name, value); }
get values(): ReadonlyArray<string> { meta.reportRead(this, "values"); return (this as any).$v.values; }
set values(value: ReadonlyArray<string>) { meta.writeField(this, BaseRule.$field_values, value); }
}
class ClsRule extends BaseRule {
  constructor(args: RuleParams) {
    super();
    meta.initializers.Rule(this, args);
  }
  get typeTag(): "Rule" {
        return "Rule";
      }
}
export const Rule = ClsRule;

type KnownVariantedRuleSet = ClsVariantedRuleSet;
export function isKnownVariantedRuleSet<T>(x: [Extract<T, VariantedRuleSet>] extends [never] ? never : T): x is [Extract<T, VariantedRuleSet>] extends [never]
  ? never
  : VariantedRuleSet extends T // Needed when T is any
  ? VariantedRuleSet
  : Extract<T, VariantedRuleSet> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariantedRuleSet;
}

    export function ensureKnownVariantedRuleSet<T>(x: [Extract<T, VariantedRuleSet>] extends [never] ? never : T): VariantedRuleSet {
      assert(isKnownVariantedRuleSet(x), () => mkUnexpectedTypeMsg([VariantedRuleSet], x));
      return x;
    }
    export function ensureMaybeKnownVariantedRuleSet<T>(x: [Extract<T, VariantedRuleSet>] extends [never] ? never : T): VariantedRuleSet | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariantedRuleSet(x as any);
    }
    
export type VariantedRuleSet = KnownVariantedRuleSet;
export interface VariantedRuleSetParams {
  readonly variants: ReadonlyArray<Variant> /* Const,WeakRef */;
rs: RuleSet /*  */;
}

abstract class BaseVariantedRuleSet extends ModelInst {
  
    static isKnown(x: any): x is VariantedRuleSet { return isKnownVariantedRuleSet(x); }
    static getType(): VariantedRuleSet { throw new Error(); }
    static modelTypeName = "VariantedRuleSet";
    
  static readonly $fields = meta.allFields(meta.clsByName["VariantedRuleSet"]);
  static readonly $field_variants: Field = ensure(
    BaseVariantedRuleSet.$fields.find((f) => f.name === "variants"),
    "VariantedRuleSet has no field variants",
  );
static readonly $field_rs: Field = ensure(
    BaseVariantedRuleSet.$fields.find((f) => f.name === "rs"),
    "VariantedRuleSet has no field rs",
  );
  /* Const,WeakRef */
get variants(): ReadonlyArray<Variant> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
private set variants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseVariantedRuleSet.$field_variants, value); }
get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseVariantedRuleSet.$field_rs, value); }
}
class ClsVariantedRuleSet extends BaseVariantedRuleSet {
  constructor(args: VariantedRuleSetParams) {
    super();
    meta.initializers.VariantedRuleSet(this, args);
  }
  get typeTag(): "VariantedRuleSet" {
        return "VariantedRuleSet";
      }
}
export const VariantedRuleSet = ClsVariantedRuleSet;

type KnownMixin = ClsMixin;
export function isKnownMixin<T>(x: [Extract<T, Mixin>] extends [never] ? never : T): x is [Extract<T, Mixin>] extends [never]
  ? never
  : Mixin extends T // Needed when T is any
  ? Mixin
  : Extract<T, Mixin> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseMixin;
}

    export function ensureKnownMixin<T>(x: [Extract<T, Mixin>] extends [never] ? never : T): Mixin {
      assert(isKnownMixin(x), () => mkUnexpectedTypeMsg([Mixin], x));
      return x;
    }
    export function ensureMaybeKnownMixin<T>(x: [Extract<T, Mixin>] extends [never] ? never : T): Mixin | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownMixin(x as any);
    }
    
export type Mixin = KnownMixin;
export interface MixinParams {
  name: string /*  */;
rs: RuleSet /*  */;
preview: string | null | undefined /*  */;
readonly uuid: string /* Const */;
readonly forTheme: boolean /* Const */;
variantedRs: ReadonlyArray<VariantedRuleSet> /*  */;
}

abstract class BaseMixin extends ModelInst {
  
    static isKnown(x: any): x is Mixin { return isKnownMixin(x); }
    static getType(): Mixin { throw new Error(); }
    static modelTypeName = "Mixin";
    
  static readonly $fields = meta.allFields(meta.clsByName["Mixin"]);
  static readonly $field_name: Field = ensure(
    BaseMixin.$fields.find((f) => f.name === "name"),
    "Mixin has no field name",
  );
static readonly $field_rs: Field = ensure(
    BaseMixin.$fields.find((f) => f.name === "rs"),
    "Mixin has no field rs",
  );
static readonly $field_preview: Field = ensure(
    BaseMixin.$fields.find((f) => f.name === "preview"),
    "Mixin has no field preview",
  );
static readonly $field_uuid: Field = ensure(
    BaseMixin.$fields.find((f) => f.name === "uuid"),
    "Mixin has no field uuid",
  );
static readonly $field_forTheme: Field = ensure(
    BaseMixin.$fields.find((f) => f.name === "forTheme"),
    "Mixin has no field forTheme",
  );
static readonly $field_variantedRs: Field = ensure(
    BaseMixin.$fields.find((f) => f.name === "variantedRs"),
    "Mixin has no field variantedRs",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseMixin.$field_name, value); }
get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseMixin.$field_rs, value); }
get preview(): string | null | undefined { meta.reportRead(this, "preview"); return (this as any).$v.preview; }
set preview(value: string | null | undefined) { meta.writeField(this, BaseMixin.$field_preview, value); }
/* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseMixin.$field_uuid, value); }
/* Const */
get forTheme(): boolean { meta.reportRead(this, "forTheme"); return (this as any).$v.forTheme; }
private set forTheme(value: boolean) { meta.writeField(this, BaseMixin.$field_forTheme, value); }
get variantedRs(): ReadonlyArray<VariantedRuleSet> { meta.reportRead(this, "variantedRs"); return (this as any).$v.variantedRs; }
set variantedRs(value: ReadonlyArray<VariantedRuleSet>) { meta.writeField(this, BaseMixin.$field_variantedRs, value); }
}
class ClsMixin extends BaseMixin {
  constructor(args: MixinParams) {
    super();
    meta.initializers.Mixin(this, args);
  }
  get typeTag(): "Mixin" {
        return "Mixin";
      }
}
export const Mixin = ClsMixin;

type KnownKeyFrame = ClsKeyFrame;
export function isKnownKeyFrame<T>(x: [Extract<T, KeyFrame>] extends [never] ? never : T): x is [Extract<T, KeyFrame>] extends [never]
  ? never
  : KeyFrame extends T // Needed when T is any
  ? KeyFrame
  : Extract<T, KeyFrame> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseKeyFrame;
}

    export function ensureKnownKeyFrame<T>(x: [Extract<T, KeyFrame>] extends [never] ? never : T): KeyFrame {
      assert(isKnownKeyFrame(x), () => mkUnexpectedTypeMsg([KeyFrame], x));
      return x;
    }
    export function ensureMaybeKnownKeyFrame<T>(x: [Extract<T, KeyFrame>] extends [never] ? never : T): KeyFrame | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownKeyFrame(x as any);
    }
    
export type KeyFrame = KnownKeyFrame;
export interface KeyFrameParams {
  percentage: number /*  */;
rs: RuleSet /*  */;
}

abstract class BaseKeyFrame extends ModelInst {
  
    static isKnown(x: any): x is KeyFrame { return isKnownKeyFrame(x); }
    static getType(): KeyFrame { throw new Error(); }
    static modelTypeName = "KeyFrame";
    
  static readonly $fields = meta.allFields(meta.clsByName["KeyFrame"]);
  static readonly $field_percentage: Field = ensure(
    BaseKeyFrame.$fields.find((f) => f.name === "percentage"),
    "KeyFrame has no field percentage",
  );
static readonly $field_rs: Field = ensure(
    BaseKeyFrame.$fields.find((f) => f.name === "rs"),
    "KeyFrame has no field rs",
  );
  get percentage(): number { meta.reportRead(this, "percentage"); return (this as any).$v.percentage; }
set percentage(value: number) { meta.writeField(this, BaseKeyFrame.$field_percentage, value); }
get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseKeyFrame.$field_rs, value); }
}
class ClsKeyFrame extends BaseKeyFrame {
  constructor(args: KeyFrameParams) {
    super();
    meta.initializers.KeyFrame(this, args);
  }
  get typeTag(): "KeyFrame" {
        return "KeyFrame";
      }
}
export const KeyFrame = ClsKeyFrame;

type KnownAnimationSequence = ClsAnimationSequence;
export function isKnownAnimationSequence<T>(x: [Extract<T, AnimationSequence>] extends [never] ? never : T): x is [Extract<T, AnimationSequence>] extends [never]
  ? never
  : AnimationSequence extends T // Needed when T is any
  ? AnimationSequence
  : Extract<T, AnimationSequence> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseAnimationSequence;
}

    export function ensureKnownAnimationSequence<T>(x: [Extract<T, AnimationSequence>] extends [never] ? never : T): AnimationSequence {
      assert(isKnownAnimationSequence(x), () => mkUnexpectedTypeMsg([AnimationSequence], x));
      return x;
    }
    export function ensureMaybeKnownAnimationSequence<T>(x: [Extract<T, AnimationSequence>] extends [never] ? never : T): AnimationSequence | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownAnimationSequence(x as any);
    }
    
export type AnimationSequence = KnownAnimationSequence;
export interface AnimationSequenceParams {
  name: string /*  */;
readonly uuid: string /* Const */;
keyframes: ReadonlyArray<KeyFrame> /*  */;
}

abstract class BaseAnimationSequence extends ModelInst {
  
    static isKnown(x: any): x is AnimationSequence { return isKnownAnimationSequence(x); }
    static getType(): AnimationSequence { throw new Error(); }
    static modelTypeName = "AnimationSequence";
    
  static readonly $fields = meta.allFields(meta.clsByName["AnimationSequence"]);
  static readonly $field_name: Field = ensure(
    BaseAnimationSequence.$fields.find((f) => f.name === "name"),
    "AnimationSequence has no field name",
  );
static readonly $field_uuid: Field = ensure(
    BaseAnimationSequence.$fields.find((f) => f.name === "uuid"),
    "AnimationSequence has no field uuid",
  );
static readonly $field_keyframes: Field = ensure(
    BaseAnimationSequence.$fields.find((f) => f.name === "keyframes"),
    "AnimationSequence has no field keyframes",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseAnimationSequence.$field_name, value); }
/* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseAnimationSequence.$field_uuid, value); }
get keyframes(): ReadonlyArray<KeyFrame> { meta.reportRead(this, "keyframes"); return (this as any).$v.keyframes; }
set keyframes(value: ReadonlyArray<KeyFrame>) { meta.writeField(this, BaseAnimationSequence.$field_keyframes, value); }
}
class ClsAnimationSequence extends BaseAnimationSequence {
  constructor(args: AnimationSequenceParams) {
    super();
    meta.initializers.AnimationSequence(this, args);
  }
  get typeTag(): "AnimationSequence" {
        return "AnimationSequence";
      }
}
export const AnimationSequence = ClsAnimationSequence;

type KnownAnimation = ClsAnimation;
export function isKnownAnimation<T>(x: [Extract<T, Animation>] extends [never] ? never : T): x is [Extract<T, Animation>] extends [never]
  ? never
  : Animation extends T // Needed when T is any
  ? Animation
  : Extract<T, Animation> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseAnimation;
}

    export function ensureKnownAnimation<T>(x: [Extract<T, Animation>] extends [never] ? never : T): Animation {
      assert(isKnownAnimation(x), () => mkUnexpectedTypeMsg([Animation], x));
      return x;
    }
    export function ensureMaybeKnownAnimation<T>(x: [Extract<T, Animation>] extends [never] ? never : T): Animation | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownAnimation(x as any);
    }
    
export type Animation = KnownAnimation;
export interface AnimationParams {
  sequence: AnimationSequence /* WeakRef */;
duration: string /*  */;
timingFunction: string /*  */;
iterationCount: string /*  */;
direction: "normal" | "reverse" | "alternate" | "alternate-reverse" /*  */;
delay: string /*  */;
fillMode: "none" | "forwards" | "backwards" | "both" /*  */;
playState: "paused" | "running" /*  */;
}

abstract class BaseAnimation extends ModelInst {
  
    static isKnown(x: any): x is Animation { return isKnownAnimation(x); }
    static getType(): Animation { throw new Error(); }
    static modelTypeName = "Animation";
    
  static readonly $fields = meta.allFields(meta.clsByName["Animation"]);
  static readonly $field_sequence: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "sequence"),
    "Animation has no field sequence",
  );
static readonly $field_duration: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "duration"),
    "Animation has no field duration",
  );
static readonly $field_timingFunction: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "timingFunction"),
    "Animation has no field timingFunction",
  );
static readonly $field_iterationCount: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "iterationCount"),
    "Animation has no field iterationCount",
  );
static readonly $field_direction: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "direction"),
    "Animation has no field direction",
  );
static readonly $field_delay: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "delay"),
    "Animation has no field delay",
  );
static readonly $field_fillMode: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "fillMode"),
    "Animation has no field fillMode",
  );
static readonly $field_playState: Field = ensure(
    BaseAnimation.$fields.find((f) => f.name === "playState"),
    "Animation has no field playState",
  );
  /* WeakRef */
get sequence(): AnimationSequence { meta.reportRead(this, "sequence"); return (this as any).$v.sequence; }
set sequence(value: AnimationSequence) { meta.writeField(this, BaseAnimation.$field_sequence, value); }
get duration(): string { meta.reportRead(this, "duration"); return (this as any).$v.duration; }
set duration(value: string) { meta.writeField(this, BaseAnimation.$field_duration, value); }
get timingFunction(): string { meta.reportRead(this, "timingFunction"); return (this as any).$v.timingFunction; }
set timingFunction(value: string) { meta.writeField(this, BaseAnimation.$field_timingFunction, value); }
get iterationCount(): string { meta.reportRead(this, "iterationCount"); return (this as any).$v.iterationCount; }
set iterationCount(value: string) { meta.writeField(this, BaseAnimation.$field_iterationCount, value); }
get direction(): "normal" | "reverse" | "alternate" | "alternate-reverse" { meta.reportRead(this, "direction"); return (this as any).$v.direction; }
set direction(value: "normal" | "reverse" | "alternate" | "alternate-reverse") { meta.writeField(this, BaseAnimation.$field_direction, value); }
get delay(): string { meta.reportRead(this, "delay"); return (this as any).$v.delay; }
set delay(value: string) { meta.writeField(this, BaseAnimation.$field_delay, value); }
get fillMode(): "none" | "forwards" | "backwards" | "both" { meta.reportRead(this, "fillMode"); return (this as any).$v.fillMode; }
set fillMode(value: "none" | "forwards" | "backwards" | "both") { meta.writeField(this, BaseAnimation.$field_fillMode, value); }
get playState(): "paused" | "running" { meta.reportRead(this, "playState"); return (this as any).$v.playState; }
set playState(value: "paused" | "running") { meta.writeField(this, BaseAnimation.$field_playState, value); }
}
class ClsAnimation extends BaseAnimation {
  constructor(args: AnimationParams) {
    super();
    meta.initializers.Animation(this, args);
  }
  get typeTag(): "Animation" {
        return "Animation";
      }
}
export const Animation = ClsAnimation;

type KnownTheme = ClsTheme;
export function isKnownTheme<T>(x: [Extract<T, Theme>] extends [never] ? never : T): x is [Extract<T, Theme>] extends [never]
  ? never
  : Theme extends T // Needed when T is any
  ? Theme
  : Extract<T, Theme> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTheme;
}

    export function ensureKnownTheme<T>(x: [Extract<T, Theme>] extends [never] ? never : T): Theme {
      assert(isKnownTheme(x), () => mkUnexpectedTypeMsg([Theme], x));
      return x;
    }
    export function ensureMaybeKnownTheme<T>(x: [Extract<T, Theme>] extends [never] ? never : T): Theme | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTheme(x as any);
    }
    
export type Theme = KnownTheme;
export interface ThemeParams {
  defaultStyle: Mixin /*  */;
styles: ReadonlyArray<ThemeStyle> /*  */;
layout: ThemeLayoutSettings | null | undefined /*  */;
addItemPrefs: Readonly<Record<string, RuleSet>> /*  */;
readonly active: boolean /* Const */;
}

abstract class BaseTheme extends ModelInst {
  
    static isKnown(x: any): x is Theme { return isKnownTheme(x); }
    static getType(): Theme { throw new Error(); }
    static modelTypeName = "Theme";
    
  static readonly $fields = meta.allFields(meta.clsByName["Theme"]);
  static readonly $field_defaultStyle: Field = ensure(
    BaseTheme.$fields.find((f) => f.name === "defaultStyle"),
    "Theme has no field defaultStyle",
  );
static readonly $field_styles: Field = ensure(
    BaseTheme.$fields.find((f) => f.name === "styles"),
    "Theme has no field styles",
  );
static readonly $field_layout: Field = ensure(
    BaseTheme.$fields.find((f) => f.name === "layout"),
    "Theme has no field layout",
  );
static readonly $field_addItemPrefs: Field = ensure(
    BaseTheme.$fields.find((f) => f.name === "addItemPrefs"),
    "Theme has no field addItemPrefs",
  );
static readonly $field_active: Field = ensure(
    BaseTheme.$fields.find((f) => f.name === "active"),
    "Theme has no field active",
  );
  get defaultStyle(): Mixin { meta.reportRead(this, "defaultStyle"); return (this as any).$v.defaultStyle; }
set defaultStyle(value: Mixin) { meta.writeField(this, BaseTheme.$field_defaultStyle, value); }
get styles(): ReadonlyArray<ThemeStyle> { meta.reportRead(this, "styles"); return (this as any).$v.styles; }
set styles(value: ReadonlyArray<ThemeStyle>) { meta.writeField(this, BaseTheme.$field_styles, value); }
get layout(): ThemeLayoutSettings | null | undefined { meta.reportRead(this, "layout"); return (this as any).$v.layout; }
set layout(value: ThemeLayoutSettings | null | undefined) { meta.writeField(this, BaseTheme.$field_layout, value); }
get addItemPrefs(): Readonly<Record<string, RuleSet>> { meta.reportRead(this, "addItemPrefs"); return (this as any).$v.addItemPrefs; }
set addItemPrefs(value: Readonly<Record<string, RuleSet>>) { meta.writeField(this, BaseTheme.$field_addItemPrefs, value); }
/* Const */
get active(): boolean { meta.reportRead(this, "active"); return (this as any).$v.active; }
private set active(value: boolean) { meta.writeField(this, BaseTheme.$field_active, value); }
}
class ClsTheme extends BaseTheme {
  constructor(args: ThemeParams) {
    super();
    meta.initializers.Theme(this, args);
  }
  get typeTag(): "Theme" {
        return "Theme";
      }
}
export const Theme = ClsTheme;

type KnownThemeStyle = ClsThemeStyle;
export function isKnownThemeStyle<T>(x: [Extract<T, ThemeStyle>] extends [never] ? never : T): x is [Extract<T, ThemeStyle>] extends [never]
  ? never
  : ThemeStyle extends T // Needed when T is any
  ? ThemeStyle
  : Extract<T, ThemeStyle> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseThemeStyle;
}

    export function ensureKnownThemeStyle<T>(x: [Extract<T, ThemeStyle>] extends [never] ? never : T): ThemeStyle {
      assert(isKnownThemeStyle(x), () => mkUnexpectedTypeMsg([ThemeStyle], x));
      return x;
    }
    export function ensureMaybeKnownThemeStyle<T>(x: [Extract<T, ThemeStyle>] extends [never] ? never : T): ThemeStyle | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownThemeStyle(x as any);
    }
    
export type ThemeStyle = KnownThemeStyle;
export interface ThemeStyleParams {
  readonly selector: string /* Const */;
style: Mixin /*  */;
}

abstract class BaseThemeStyle extends ModelInst {
  
    static isKnown(x: any): x is ThemeStyle { return isKnownThemeStyle(x); }
    static getType(): ThemeStyle { throw new Error(); }
    static modelTypeName = "ThemeStyle";
    
  static readonly $fields = meta.allFields(meta.clsByName["ThemeStyle"]);
  static readonly $field_selector: Field = ensure(
    BaseThemeStyle.$fields.find((f) => f.name === "selector"),
    "ThemeStyle has no field selector",
  );
static readonly $field_style: Field = ensure(
    BaseThemeStyle.$fields.find((f) => f.name === "style"),
    "ThemeStyle has no field style",
  );
  /* Const */
get selector(): string { meta.reportRead(this, "selector"); return (this as any).$v.selector; }
private set selector(value: string) { meta.writeField(this, BaseThemeStyle.$field_selector, value); }
get style(): Mixin { meta.reportRead(this, "style"); return (this as any).$v.style; }
set style(value: Mixin) { meta.writeField(this, BaseThemeStyle.$field_style, value); }
}
class ClsThemeStyle extends BaseThemeStyle {
  constructor(args: ThemeStyleParams) {
    super();
    meta.initializers.ThemeStyle(this, args);
  }
  get typeTag(): "ThemeStyle" {
        return "ThemeStyle";
      }
}
export const ThemeStyle = ClsThemeStyle;

type KnownThemeLayoutSettings = ClsThemeLayoutSettings;
export function isKnownThemeLayoutSettings<T>(x: [Extract<T, ThemeLayoutSettings>] extends [never] ? never : T): x is [Extract<T, ThemeLayoutSettings>] extends [never]
  ? never
  : ThemeLayoutSettings extends T // Needed when T is any
  ? ThemeLayoutSettings
  : Extract<T, ThemeLayoutSettings> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseThemeLayoutSettings;
}

    export function ensureKnownThemeLayoutSettings<T>(x: [Extract<T, ThemeLayoutSettings>] extends [never] ? never : T): ThemeLayoutSettings {
      assert(isKnownThemeLayoutSettings(x), () => mkUnexpectedTypeMsg([ThemeLayoutSettings], x));
      return x;
    }
    export function ensureMaybeKnownThemeLayoutSettings<T>(x: [Extract<T, ThemeLayoutSettings>] extends [never] ? never : T): ThemeLayoutSettings | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownThemeLayoutSettings(x as any);
    }
    
export type ThemeLayoutSettings = KnownThemeLayoutSettings;
export interface ThemeLayoutSettingsParams {
  rs: RuleSet /*  */;
}

abstract class BaseThemeLayoutSettings extends ModelInst {
  
    static isKnown(x: any): x is ThemeLayoutSettings { return isKnownThemeLayoutSettings(x); }
    static getType(): ThemeLayoutSettings { throw new Error(); }
    static modelTypeName = "ThemeLayoutSettings";
    
  static readonly $fields = meta.allFields(meta.clsByName["ThemeLayoutSettings"]);
  static readonly $field_rs: Field = ensure(
    BaseThemeLayoutSettings.$fields.find((f) => f.name === "rs"),
    "ThemeLayoutSettings has no field rs",
  );
  get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseThemeLayoutSettings.$field_rs, value); }
}
class ClsThemeLayoutSettings extends BaseThemeLayoutSettings {
  constructor(args: ThemeLayoutSettingsParams) {
    super();
    meta.initializers.ThemeLayoutSettings(this, args);
  }
  get typeTag(): "ThemeLayoutSettings" {
        return "ThemeLayoutSettings";
      }
}
export const ThemeLayoutSettings = ClsThemeLayoutSettings;

type KnownProjectDependency = ClsProjectDependency;
export function isKnownProjectDependency<T>(x: [Extract<T, ProjectDependency>] extends [never] ? never : T): x is [Extract<T, ProjectDependency>] extends [never]
  ? never
  : ProjectDependency extends T // Needed when T is any
  ? ProjectDependency
  : Extract<T, ProjectDependency> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseProjectDependency;
}

    export function ensureKnownProjectDependency<T>(x: [Extract<T, ProjectDependency>] extends [never] ? never : T): ProjectDependency {
      assert(isKnownProjectDependency(x), () => mkUnexpectedTypeMsg([ProjectDependency], x));
      return x;
    }
    export function ensureMaybeKnownProjectDependency<T>(x: [Extract<T, ProjectDependency>] extends [never] ? never : T): ProjectDependency | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownProjectDependency(x as any);
    }
    
export type ProjectDependency = KnownProjectDependency;
export interface ProjectDependencyParams {
  readonly uuid: string /* Const */;
pkgId: string /*  */;
projectId: string /*  */;
version: string /*  */;
name: string /*  */;
site: Site /*  */;
}

abstract class BaseProjectDependency extends ModelInst {
  
    static isKnown(x: any): x is ProjectDependency { return isKnownProjectDependency(x); }
    static getType(): ProjectDependency { throw new Error(); }
    static modelTypeName = "ProjectDependency";
    
  static readonly $fields = meta.allFields(meta.clsByName["ProjectDependency"]);
  static readonly $field_uuid: Field = ensure(
    BaseProjectDependency.$fields.find((f) => f.name === "uuid"),
    "ProjectDependency has no field uuid",
  );
static readonly $field_pkgId: Field = ensure(
    BaseProjectDependency.$fields.find((f) => f.name === "pkgId"),
    "ProjectDependency has no field pkgId",
  );
static readonly $field_projectId: Field = ensure(
    BaseProjectDependency.$fields.find((f) => f.name === "projectId"),
    "ProjectDependency has no field projectId",
  );
static readonly $field_version: Field = ensure(
    BaseProjectDependency.$fields.find((f) => f.name === "version"),
    "ProjectDependency has no field version",
  );
static readonly $field_name: Field = ensure(
    BaseProjectDependency.$fields.find((f) => f.name === "name"),
    "ProjectDependency has no field name",
  );
static readonly $field_site: Field = ensure(
    BaseProjectDependency.$fields.find((f) => f.name === "site"),
    "ProjectDependency has no field site",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseProjectDependency.$field_uuid, value); }
get pkgId(): string { meta.reportRead(this, "pkgId"); return (this as any).$v.pkgId; }
set pkgId(value: string) { meta.writeField(this, BaseProjectDependency.$field_pkgId, value); }
get projectId(): string { meta.reportRead(this, "projectId"); return (this as any).$v.projectId; }
set projectId(value: string) { meta.writeField(this, BaseProjectDependency.$field_projectId, value); }
get version(): string { meta.reportRead(this, "version"); return (this as any).$v.version; }
set version(value: string) { meta.writeField(this, BaseProjectDependency.$field_version, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseProjectDependency.$field_name, value); }
get site(): Site { meta.reportRead(this, "site"); return (this as any).$v.site; }
set site(value: Site) { meta.writeField(this, BaseProjectDependency.$field_site, value); }
}
class ClsProjectDependency extends BaseProjectDependency {
  constructor(args: ProjectDependencyParams) {
    super();
    meta.initializers.ProjectDependency(this, args);
  }
  get typeTag(): "ProjectDependency" {
        return "ProjectDependency";
      }
}
export const ProjectDependency = ClsProjectDependency;

type KnownImageAsset = ClsImageAsset;
export function isKnownImageAsset<T>(x: [Extract<T, ImageAsset>] extends [never] ? never : T): x is [Extract<T, ImageAsset>] extends [never]
  ? never
  : ImageAsset extends T // Needed when T is any
  ? ImageAsset
  : Extract<T, ImageAsset> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseImageAsset;
}

    export function ensureKnownImageAsset<T>(x: [Extract<T, ImageAsset>] extends [never] ? never : T): ImageAsset {
      assert(isKnownImageAsset(x), () => mkUnexpectedTypeMsg([ImageAsset], x));
      return x;
    }
    export function ensureMaybeKnownImageAsset<T>(x: [Extract<T, ImageAsset>] extends [never] ? never : T): ImageAsset | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownImageAsset(x as any);
    }
    
export type ImageAsset = KnownImageAsset;
export interface ImageAssetParams {
  readonly uuid: string /* Const */;
name: string /*  */;
readonly type: "picture" | "icon" /* Const */;
dataUri: string | null | undefined /*  */;
width: number | null | undefined /*  */;
height: number | null | undefined /*  */;
aspectRatio: number | null | undefined /*  */;
description: string | null | undefined /*  */;
}

abstract class BaseImageAsset extends ModelInst {
  
    static isKnown(x: any): x is ImageAsset { return isKnownImageAsset(x); }
    static getType(): ImageAsset { throw new Error(); }
    static modelTypeName = "ImageAsset";
    
  static readonly $fields = meta.allFields(meta.clsByName["ImageAsset"]);
  static readonly $field_uuid: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "uuid"),
    "ImageAsset has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "name"),
    "ImageAsset has no field name",
  );
static readonly $field_type: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "type"),
    "ImageAsset has no field type",
  );
static readonly $field_dataUri: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "dataUri"),
    "ImageAsset has no field dataUri",
  );
static readonly $field_width: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "width"),
    "ImageAsset has no field width",
  );
static readonly $field_height: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "height"),
    "ImageAsset has no field height",
  );
static readonly $field_aspectRatio: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "aspectRatio"),
    "ImageAsset has no field aspectRatio",
  );
static readonly $field_description: Field = ensure(
    BaseImageAsset.$fields.find((f) => f.name === "description"),
    "ImageAsset has no field description",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseImageAsset.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseImageAsset.$field_name, value); }
/* Const */
get type(): "picture" | "icon" { meta.reportRead(this, "type"); return (this as any).$v.type; }
private set type(value: "picture" | "icon") { meta.writeField(this, BaseImageAsset.$field_type, value); }
get dataUri(): string | null | undefined { meta.reportRead(this, "dataUri"); return (this as any).$v.dataUri; }
set dataUri(value: string | null | undefined) { meta.writeField(this, BaseImageAsset.$field_dataUri, value); }
get width(): number | null | undefined { meta.reportRead(this, "width"); return (this as any).$v.width; }
set width(value: number | null | undefined) { meta.writeField(this, BaseImageAsset.$field_width, value); }
get height(): number | null | undefined { meta.reportRead(this, "height"); return (this as any).$v.height; }
set height(value: number | null | undefined) { meta.writeField(this, BaseImageAsset.$field_height, value); }
get aspectRatio(): number | null | undefined { meta.reportRead(this, "aspectRatio"); return (this as any).$v.aspectRatio; }
set aspectRatio(value: number | null | undefined) { meta.writeField(this, BaseImageAsset.$field_aspectRatio, value); }
get description(): string | null | undefined { meta.reportRead(this, "description"); return (this as any).$v.description; }
set description(value: string | null | undefined) { meta.writeField(this, BaseImageAsset.$field_description, value); }
}
class ClsImageAsset extends BaseImageAsset {
  constructor(args: ImageAssetParams) {
    super();
    meta.initializers.ImageAsset(this, args);
  }
  get typeTag(): "ImageAsset" {
        return "ImageAsset";
      }
}
export const ImageAsset = ClsImageAsset;

type KnownTplNode = KnownTplTag | KnownTplComponent | KnownTplSlot;
export function isKnownTplNode<T>(x: [Extract<T, TplNode>] extends [never] ? never : T): x is [Extract<T, TplNode>] extends [never]
  ? never
  : TplNode extends T // Needed when T is any
  ? TplNode
  : Extract<T, TplNode> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTplNode;
}

    export function ensureKnownTplNode<T>(x: [Extract<T, TplNode>] extends [never] ? never : T): TplNode {
      assert(isKnownTplNode(x), () => mkUnexpectedTypeMsg([TplNode], x));
      return x;
    }
    export function ensureMaybeKnownTplNode<T>(x: [Extract<T, TplNode>] extends [never] ? never : T): TplNode | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTplNode(x as any);
    }
    
export type TplNode = KnownTplNode;
export interface TplNodeParams {
  readonly uuid: string /* Const */;
parent: TplNode | null | undefined /* WeakRef */;
locked: boolean | null | undefined /*  */;
vsettings: ReadonlyArray<VariantSetting> /*  */;
}

abstract class BaseTplNode extends ModelInst {
  
    static isKnown(x: any): x is TplNode { return isKnownTplNode(x); }
    static getType(): TplNode { throw new Error(); }
    static modelTypeName = "TplNode";
    
  static readonly $fields = meta.allFields(meta.clsByName["TplNode"]);
  static readonly $field_uuid: Field = ensure(
    BaseTplNode.$fields.find((f) => f.name === "uuid"),
    "TplNode has no field uuid",
  );
static readonly $field_parent: Field = ensure(
    BaseTplNode.$fields.find((f) => f.name === "parent"),
    "TplNode has no field parent",
  );
static readonly $field_locked: Field = ensure(
    BaseTplNode.$fields.find((f) => f.name === "locked"),
    "TplNode has no field locked",
  );
static readonly $field_vsettings: Field = ensure(
    BaseTplNode.$fields.find((f) => f.name === "vsettings"),
    "TplNode has no field vsettings",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseTplNode.$field_uuid, value); }
/* WeakRef */
get parent(): TplNode | null | undefined { meta.reportRead(this, "parent"); return (this as any).$v.parent; }
set parent(value: TplNode | null | undefined) { meta.writeField(this, BaseTplNode.$field_parent, value); }
get locked(): boolean | null | undefined { meta.reportRead(this, "locked"); return (this as any).$v.locked; }
set locked(value: boolean | null | undefined) { meta.writeField(this, BaseTplNode.$field_locked, value); }
get vsettings(): ReadonlyArray<VariantSetting> { meta.reportRead(this, "vsettings"); return (this as any).$v.vsettings; }
set vsettings(value: ReadonlyArray<VariantSetting>) { meta.writeField(this, BaseTplNode.$field_vsettings, value); }
}

export const TplNode = BaseTplNode;

type KnownTplTag = ClsTplTag;
export function isKnownTplTag<T>(x: [Extract<T, TplTag>] extends [never] ? never : T): x is [Extract<T, TplTag>] extends [never]
  ? never
  : TplTag extends T // Needed when T is any
  ? TplTag
  : Extract<T, TplTag> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTplTag;
}

    export function ensureKnownTplTag<T>(x: [Extract<T, TplTag>] extends [never] ? never : T): TplTag {
      assert(isKnownTplTag(x), () => mkUnexpectedTypeMsg([TplTag], x));
      return x;
    }
    export function ensureMaybeKnownTplTag<T>(x: [Extract<T, TplTag>] extends [never] ? never : T): TplTag | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTplTag(x as any);
    }
    
export type TplTag = KnownTplTag;
export interface TplTagParams {
  tag: string /*  */;
name: string | null | undefined /*  */;
children: ReadonlyArray<TplNode> /*  */;
type: "text" | "image" | "column" | "columns" | "other" /*  */;
codeGenType: "auto" | null | undefined | "alwaysTag" | null | undefined | "noTag" | null | undefined /*  */;
columnsSetting: ColumnsSetting | null | undefined /*  */;
readonly uuid: string /* Const */;
parent: TplNode | null | undefined /* WeakRef */;
locked: boolean | null | undefined /*  */;
vsettings: ReadonlyArray<VariantSetting> /*  */;
}

abstract class BaseTplTag extends BaseTplNode {
  
    static isKnown(x: any): x is TplTag { return isKnownTplTag(x); }
    static getType(): TplTag { throw new Error(); }
    static modelTypeName = "TplTag";
    
  static readonly $fields = meta.allFields(meta.clsByName["TplTag"]);
  static readonly $field_tag: Field = ensure(
    BaseTplTag.$fields.find((f) => f.name === "tag"),
    "TplTag has no field tag",
  );
static readonly $field_name: Field = ensure(
    BaseTplTag.$fields.find((f) => f.name === "name"),
    "TplTag has no field name",
  );
static readonly $field_children: Field = ensure(
    BaseTplTag.$fields.find((f) => f.name === "children"),
    "TplTag has no field children",
  );
static readonly $field_type: Field = ensure(
    BaseTplTag.$fields.find((f) => f.name === "type"),
    "TplTag has no field type",
  );
static readonly $field_codeGenType: Field = ensure(
    BaseTplTag.$fields.find((f) => f.name === "codeGenType"),
    "TplTag has no field codeGenType",
  );
static readonly $field_columnsSetting: Field = ensure(
    BaseTplTag.$fields.find((f) => f.name === "columnsSetting"),
    "TplTag has no field columnsSetting",
  );
  get tag(): string { meta.reportRead(this, "tag"); return (this as any).$v.tag; }
set tag(value: string) { meta.writeField(this, BaseTplTag.$field_tag, value); }
get name(): string | null | undefined { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string | null | undefined) { meta.writeField(this, BaseTplTag.$field_name, value); }
get children(): ReadonlyArray<TplNode> { meta.reportRead(this, "children"); return (this as any).$v.children; }
set children(value: ReadonlyArray<TplNode>) { meta.writeField(this, BaseTplTag.$field_children, value); }
get type(): "text" | "image" | "column" | "columns" | "other" { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: "text" | "image" | "column" | "columns" | "other") { meta.writeField(this, BaseTplTag.$field_type, value); }
get codeGenType(): "auto" | null | undefined | "alwaysTag" | null | undefined | "noTag" | null | undefined { meta.reportRead(this, "codeGenType"); return (this as any).$v.codeGenType; }
set codeGenType(value: "auto" | null | undefined | "alwaysTag" | null | undefined | "noTag" | null | undefined) { meta.writeField(this, BaseTplTag.$field_codeGenType, value); }
get columnsSetting(): ColumnsSetting | null | undefined { meta.reportRead(this, "columnsSetting"); return (this as any).$v.columnsSetting; }
set columnsSetting(value: ColumnsSetting | null | undefined) { meta.writeField(this, BaseTplTag.$field_columnsSetting, value); }
}
class ClsTplTag extends BaseTplTag {
  constructor(args: TplTagParams) {
    super();
    meta.initializers.TplTag(this, args);
  }
  get typeTag(): "TplTag" {
        return "TplTag";
      }
}
export const TplTag = ClsTplTag;

type KnownTplComponent = ClsTplComponent;
export function isKnownTplComponent<T>(x: [Extract<T, TplComponent>] extends [never] ? never : T): x is [Extract<T, TplComponent>] extends [never]
  ? never
  : TplComponent extends T // Needed when T is any
  ? TplComponent
  : Extract<T, TplComponent> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTplComponent;
}

    export function ensureKnownTplComponent<T>(x: [Extract<T, TplComponent>] extends [never] ? never : T): TplComponent {
      assert(isKnownTplComponent(x), () => mkUnexpectedTypeMsg([TplComponent], x));
      return x;
    }
    export function ensureMaybeKnownTplComponent<T>(x: [Extract<T, TplComponent>] extends [never] ? never : T): TplComponent | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTplComponent(x as any);
    }
    
export type TplComponent = KnownTplComponent;
export interface TplComponentParams {
  name: string | null | undefined /*  */;
component: Component /* WeakRef */;
readonly uuid: string /* Const */;
parent: TplNode | null | undefined /* WeakRef */;
locked: boolean | null | undefined /*  */;
vsettings: ReadonlyArray<VariantSetting> /*  */;
}

abstract class BaseTplComponent extends BaseTplNode {
  
    static isKnown(x: any): x is TplComponent { return isKnownTplComponent(x); }
    static getType(): TplComponent { throw new Error(); }
    static modelTypeName = "TplComponent";
    
  static readonly $fields = meta.allFields(meta.clsByName["TplComponent"]);
  static readonly $field_name: Field = ensure(
    BaseTplComponent.$fields.find((f) => f.name === "name"),
    "TplComponent has no field name",
  );
static readonly $field_component: Field = ensure(
    BaseTplComponent.$fields.find((f) => f.name === "component"),
    "TplComponent has no field component",
  );
  get name(): string | null | undefined { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string | null | undefined) { meta.writeField(this, BaseTplComponent.$field_name, value); }
/* WeakRef */
get component(): Component { meta.reportRead(this, "component"); return (this as any).$v.component; }
set component(value: Component) { meta.writeField(this, BaseTplComponent.$field_component, value); }
}
class ClsTplComponent extends BaseTplComponent {
  constructor(args: TplComponentParams) {
    super();
    meta.initializers.TplComponent(this, args);
  }
  get typeTag(): "TplComponent" {
        return "TplComponent";
      }
}
export const TplComponent = ClsTplComponent;

type KnownTplSlot = ClsTplSlot;
export function isKnownTplSlot<T>(x: [Extract<T, TplSlot>] extends [never] ? never : T): x is [Extract<T, TplSlot>] extends [never]
  ? never
  : TplSlot extends T // Needed when T is any
  ? TplSlot
  : Extract<T, TplSlot> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTplSlot;
}

    export function ensureKnownTplSlot<T>(x: [Extract<T, TplSlot>] extends [never] ? never : T): TplSlot {
      assert(isKnownTplSlot(x), () => mkUnexpectedTypeMsg([TplSlot], x));
      return x;
    }
    export function ensureMaybeKnownTplSlot<T>(x: [Extract<T, TplSlot>] extends [never] ? never : T): TplSlot | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTplSlot(x as any);
    }
    
export type TplSlot = KnownTplSlot;
export interface TplSlotParams {
  readonly param: SlotParam /* Const,WeakRef */;
defaultContents: ReadonlyArray<TplNode> /*  */;
readonly uuid: string /* Const */;
parent: TplNode | null | undefined /* WeakRef */;
locked: boolean | null | undefined /*  */;
vsettings: ReadonlyArray<VariantSetting> /*  */;
}

abstract class BaseTplSlot extends BaseTplNode {
  
    static isKnown(x: any): x is TplSlot { return isKnownTplSlot(x); }
    static getType(): TplSlot { throw new Error(); }
    static modelTypeName = "TplSlot";
    
  static readonly $fields = meta.allFields(meta.clsByName["TplSlot"]);
  static readonly $field_param: Field = ensure(
    BaseTplSlot.$fields.find((f) => f.name === "param"),
    "TplSlot has no field param",
  );
static readonly $field_defaultContents: Field = ensure(
    BaseTplSlot.$fields.find((f) => f.name === "defaultContents"),
    "TplSlot has no field defaultContents",
  );
  /* Const,WeakRef */
get param(): SlotParam { meta.reportRead(this, "param"); return (this as any).$v.param; }
private set param(value: SlotParam) { meta.writeField(this, BaseTplSlot.$field_param, value); }
get defaultContents(): ReadonlyArray<TplNode> { meta.reportRead(this, "defaultContents"); return (this as any).$v.defaultContents; }
set defaultContents(value: ReadonlyArray<TplNode>) { meta.writeField(this, BaseTplSlot.$field_defaultContents, value); }
}
class ClsTplSlot extends BaseTplSlot {
  constructor(args: TplSlotParams) {
    super();
    meta.initializers.TplSlot(this, args);
  }
  get typeTag(): "TplSlot" {
        return "TplSlot";
      }
}
export const TplSlot = ClsTplSlot;

type KnownColumnsSetting = ClsColumnsSetting;
export function isKnownColumnsSetting<T>(x: [Extract<T, ColumnsSetting>] extends [never] ? never : T): x is [Extract<T, ColumnsSetting>] extends [never]
  ? never
  : ColumnsSetting extends T // Needed when T is any
  ? ColumnsSetting
  : Extract<T, ColumnsSetting> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseColumnsSetting;
}

    export function ensureKnownColumnsSetting<T>(x: [Extract<T, ColumnsSetting>] extends [never] ? never : T): ColumnsSetting {
      assert(isKnownColumnsSetting(x), () => mkUnexpectedTypeMsg([ColumnsSetting], x));
      return x;
    }
    export function ensureMaybeKnownColumnsSetting<T>(x: [Extract<T, ColumnsSetting>] extends [never] ? never : T): ColumnsSetting | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownColumnsSetting(x as any);
    }
    
export type ColumnsSetting = KnownColumnsSetting;
export interface ColumnsSettingParams {
  screenBreakpoint: Variant | null | undefined /* WeakRef */;
}

abstract class BaseColumnsSetting extends ModelInst {
  
    static isKnown(x: any): x is ColumnsSetting { return isKnownColumnsSetting(x); }
    static getType(): ColumnsSetting { throw new Error(); }
    static modelTypeName = "ColumnsSetting";
    
  static readonly $fields = meta.allFields(meta.clsByName["ColumnsSetting"]);
  static readonly $field_screenBreakpoint: Field = ensure(
    BaseColumnsSetting.$fields.find((f) => f.name === "screenBreakpoint"),
    "ColumnsSetting has no field screenBreakpoint",
  );
  /* WeakRef */
get screenBreakpoint(): Variant | null | undefined { meta.reportRead(this, "screenBreakpoint"); return (this as any).$v.screenBreakpoint; }
set screenBreakpoint(value: Variant | null | undefined) { meta.writeField(this, BaseColumnsSetting.$field_screenBreakpoint, value); }
}
class ClsColumnsSetting extends BaseColumnsSetting {
  constructor(args: ColumnsSettingParams) {
    super();
    meta.initializers.ColumnsSetting(this, args);
  }
  get typeTag(): "ColumnsSetting" {
        return "ColumnsSetting";
      }
}
export const ColumnsSetting = ClsColumnsSetting;

type KnownPageMeta = ClsPageMeta;
export function isKnownPageMeta<T>(x: [Extract<T, PageMeta>] extends [never] ? never : T): x is [Extract<T, PageMeta>] extends [never]
  ? never
  : PageMeta extends T // Needed when T is any
  ? PageMeta
  : Extract<T, PageMeta> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePageMeta;
}

    export function ensureKnownPageMeta<T>(x: [Extract<T, PageMeta>] extends [never] ? never : T): PageMeta {
      assert(isKnownPageMeta(x), () => mkUnexpectedTypeMsg([PageMeta], x));
      return x;
    }
    export function ensureMaybeKnownPageMeta<T>(x: [Extract<T, PageMeta>] extends [never] ? never : T): PageMeta | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPageMeta(x as any);
    }
    
export type PageMeta = KnownPageMeta;
export interface PageMetaParams {
  path: string /*  */;
params: Readonly<Record<string, string>> /*  */;
query: Readonly<Record<string, string>> /*  */;
title: string | null | undefined | TemplatedString | null | undefined /*  */;
description: string | TemplatedString /*  */;
canonical: string | null | undefined | TemplatedString | null | undefined /*  */;
roleId: string | null | undefined /*  */;
openGraphImage: string | null | undefined | ImageAssetRef | null | undefined | TemplatedString | null | undefined /*  */;
}

abstract class BasePageMeta extends ModelInst {
  
    static isKnown(x: any): x is PageMeta { return isKnownPageMeta(x); }
    static getType(): PageMeta { throw new Error(); }
    static modelTypeName = "PageMeta";
    
  static readonly $fields = meta.allFields(meta.clsByName["PageMeta"]);
  static readonly $field_path: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "path"),
    "PageMeta has no field path",
  );
static readonly $field_params: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "params"),
    "PageMeta has no field params",
  );
static readonly $field_query: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "query"),
    "PageMeta has no field query",
  );
static readonly $field_title: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "title"),
    "PageMeta has no field title",
  );
static readonly $field_description: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "description"),
    "PageMeta has no field description",
  );
static readonly $field_canonical: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "canonical"),
    "PageMeta has no field canonical",
  );
static readonly $field_roleId: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "roleId"),
    "PageMeta has no field roleId",
  );
static readonly $field_openGraphImage: Field = ensure(
    BasePageMeta.$fields.find((f) => f.name === "openGraphImage"),
    "PageMeta has no field openGraphImage",
  );
  get path(): string { meta.reportRead(this, "path"); return (this as any).$v.path; }
set path(value: string) { meta.writeField(this, BasePageMeta.$field_path, value); }
get params(): Readonly<Record<string, string>> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: Readonly<Record<string, string>>) { meta.writeField(this, BasePageMeta.$field_params, value); }
get query(): Readonly<Record<string, string>> { meta.reportRead(this, "query"); return (this as any).$v.query; }
set query(value: Readonly<Record<string, string>>) { meta.writeField(this, BasePageMeta.$field_query, value); }
get title(): string | null | undefined | TemplatedString | null | undefined { meta.reportRead(this, "title"); return (this as any).$v.title; }
set title(value: string | null | undefined | TemplatedString | null | undefined) { meta.writeField(this, BasePageMeta.$field_title, value); }
get description(): string | TemplatedString { meta.reportRead(this, "description"); return (this as any).$v.description; }
set description(value: string | TemplatedString) { meta.writeField(this, BasePageMeta.$field_description, value); }
get canonical(): string | null | undefined | TemplatedString | null | undefined { meta.reportRead(this, "canonical"); return (this as any).$v.canonical; }
set canonical(value: string | null | undefined | TemplatedString | null | undefined) { meta.writeField(this, BasePageMeta.$field_canonical, value); }
get roleId(): string | null | undefined { meta.reportRead(this, "roleId"); return (this as any).$v.roleId; }
set roleId(value: string | null | undefined) { meta.writeField(this, BasePageMeta.$field_roleId, value); }
get openGraphImage(): string | null | undefined | ImageAssetRef | null | undefined | TemplatedString | null | undefined { meta.reportRead(this, "openGraphImage"); return (this as any).$v.openGraphImage; }
set openGraphImage(value: string | null | undefined | ImageAssetRef | null | undefined | TemplatedString | null | undefined) { meta.writeField(this, BasePageMeta.$field_openGraphImage, value); }
}
class ClsPageMeta extends BasePageMeta {
  constructor(args: PageMetaParams) {
    super();
    meta.initializers.PageMeta(this, args);
  }
  get typeTag(): "PageMeta" {
        return "PageMeta";
      }
}
export const PageMeta = ClsPageMeta;

type KnownComponentDataQuery = ClsComponentDataQuery;
export function isKnownComponentDataQuery<T>(x: [Extract<T, ComponentDataQuery>] extends [never] ? never : T): x is [Extract<T, ComponentDataQuery>] extends [never]
  ? never
  : ComponentDataQuery extends T // Needed when T is any
  ? ComponentDataQuery
  : Extract<T, ComponentDataQuery> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentDataQuery;
}

    export function ensureKnownComponentDataQuery<T>(x: [Extract<T, ComponentDataQuery>] extends [never] ? never : T): ComponentDataQuery {
      assert(isKnownComponentDataQuery(x), () => mkUnexpectedTypeMsg([ComponentDataQuery], x));
      return x;
    }
    export function ensureMaybeKnownComponentDataQuery<T>(x: [Extract<T, ComponentDataQuery>] extends [never] ? never : T): ComponentDataQuery | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentDataQuery(x as any);
    }
    
export type ComponentDataQuery = KnownComponentDataQuery;
export interface ComponentDataQueryParams {
  readonly uuid: string /* Const */;
name: string /*  */;
op: DataSourceOpExpr | null | undefined /*  */;
}

abstract class BaseComponentDataQuery extends ModelInst {
  
    static isKnown(x: any): x is ComponentDataQuery { return isKnownComponentDataQuery(x); }
    static getType(): ComponentDataQuery { throw new Error(); }
    static modelTypeName = "ComponentDataQuery";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentDataQuery"]);
  static readonly $field_uuid: Field = ensure(
    BaseComponentDataQuery.$fields.find((f) => f.name === "uuid"),
    "ComponentDataQuery has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseComponentDataQuery.$fields.find((f) => f.name === "name"),
    "ComponentDataQuery has no field name",
  );
static readonly $field_op: Field = ensure(
    BaseComponentDataQuery.$fields.find((f) => f.name === "op"),
    "ComponentDataQuery has no field op",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseComponentDataQuery.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseComponentDataQuery.$field_name, value); }
get op(): DataSourceOpExpr | null | undefined { meta.reportRead(this, "op"); return (this as any).$v.op; }
set op(value: DataSourceOpExpr | null | undefined) { meta.writeField(this, BaseComponentDataQuery.$field_op, value); }
}
class ClsComponentDataQuery extends BaseComponentDataQuery {
  constructor(args: ComponentDataQueryParams) {
    super();
    meta.initializers.ComponentDataQuery(this, args);
  }
  get typeTag(): "ComponentDataQuery" {
        return "ComponentDataQuery";
      }
}
export const ComponentDataQuery = ClsComponentDataQuery;

type KnownComponentServerQuery = ClsComponentServerQuery;
export function isKnownComponentServerQuery<T>(x: [Extract<T, ComponentServerQuery>] extends [never] ? never : T): x is [Extract<T, ComponentServerQuery>] extends [never]
  ? never
  : ComponentServerQuery extends T // Needed when T is any
  ? ComponentServerQuery
  : Extract<T, ComponentServerQuery> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentServerQuery;
}

    export function ensureKnownComponentServerQuery<T>(x: [Extract<T, ComponentServerQuery>] extends [never] ? never : T): ComponentServerQuery {
      assert(isKnownComponentServerQuery(x), () => mkUnexpectedTypeMsg([ComponentServerQuery], x));
      return x;
    }
    export function ensureMaybeKnownComponentServerQuery<T>(x: [Extract<T, ComponentServerQuery>] extends [never] ? never : T): ComponentServerQuery | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentServerQuery(x as any);
    }
    
export type ComponentServerQuery = KnownComponentServerQuery;
export interface ComponentServerQueryParams {
  readonly uuid: string /* Const */;
name: string /*  */;
op: CustomFunctionExpr | null | undefined | CustomCode | null | undefined /*  */;
}

abstract class BaseComponentServerQuery extends ModelInst {
  
    static isKnown(x: any): x is ComponentServerQuery { return isKnownComponentServerQuery(x); }
    static getType(): ComponentServerQuery { throw new Error(); }
    static modelTypeName = "ComponentServerQuery";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentServerQuery"]);
  static readonly $field_uuid: Field = ensure(
    BaseComponentServerQuery.$fields.find((f) => f.name === "uuid"),
    "ComponentServerQuery has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseComponentServerQuery.$fields.find((f) => f.name === "name"),
    "ComponentServerQuery has no field name",
  );
static readonly $field_op: Field = ensure(
    BaseComponentServerQuery.$fields.find((f) => f.name === "op"),
    "ComponentServerQuery has no field op",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseComponentServerQuery.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseComponentServerQuery.$field_name, value); }
get op(): CustomFunctionExpr | null | undefined | CustomCode | null | undefined { meta.reportRead(this, "op"); return (this as any).$v.op; }
set op(value: CustomFunctionExpr | null | undefined | CustomCode | null | undefined) { meta.writeField(this, BaseComponentServerQuery.$field_op, value); }
}
class ClsComponentServerQuery extends BaseComponentServerQuery {
  constructor(args: ComponentServerQueryParams) {
    super();
    meta.initializers.ComponentServerQuery(this, args);
  }
  get typeTag(): "ComponentServerQuery" {
        return "ComponentServerQuery";
      }
}
export const ComponentServerQuery = ClsComponentServerQuery;

type KnownCodeComponentHelper = ClsCodeComponentHelper;
export function isKnownCodeComponentHelper<T>(x: [Extract<T, CodeComponentHelper>] extends [never] ? never : T): x is [Extract<T, CodeComponentHelper>] extends [never]
  ? never
  : CodeComponentHelper extends T // Needed when T is any
  ? CodeComponentHelper
  : Extract<T, CodeComponentHelper> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCodeComponentHelper;
}

    export function ensureKnownCodeComponentHelper<T>(x: [Extract<T, CodeComponentHelper>] extends [never] ? never : T): CodeComponentHelper {
      assert(isKnownCodeComponentHelper(x), () => mkUnexpectedTypeMsg([CodeComponentHelper], x));
      return x;
    }
    export function ensureMaybeKnownCodeComponentHelper<T>(x: [Extract<T, CodeComponentHelper>] extends [never] ? never : T): CodeComponentHelper | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCodeComponentHelper(x as any);
    }
    
export type CodeComponentHelper = KnownCodeComponentHelper;
export interface CodeComponentHelperParams {
  importPath: string /*  */;
importName: string /*  */;
defaultExport: boolean /*  */;
}

abstract class BaseCodeComponentHelper extends ModelInst {
  
    static isKnown(x: any): x is CodeComponentHelper { return isKnownCodeComponentHelper(x); }
    static getType(): CodeComponentHelper { throw new Error(); }
    static modelTypeName = "CodeComponentHelper";
    
  static readonly $fields = meta.allFields(meta.clsByName["CodeComponentHelper"]);
  static readonly $field_importPath: Field = ensure(
    BaseCodeComponentHelper.$fields.find((f) => f.name === "importPath"),
    "CodeComponentHelper has no field importPath",
  );
static readonly $field_importName: Field = ensure(
    BaseCodeComponentHelper.$fields.find((f) => f.name === "importName"),
    "CodeComponentHelper has no field importName",
  );
static readonly $field_defaultExport: Field = ensure(
    BaseCodeComponentHelper.$fields.find((f) => f.name === "defaultExport"),
    "CodeComponentHelper has no field defaultExport",
  );
  get importPath(): string { meta.reportRead(this, "importPath"); return (this as any).$v.importPath; }
set importPath(value: string) { meta.writeField(this, BaseCodeComponentHelper.$field_importPath, value); }
get importName(): string { meta.reportRead(this, "importName"); return (this as any).$v.importName; }
set importName(value: string) { meta.writeField(this, BaseCodeComponentHelper.$field_importName, value); }
get defaultExport(): boolean { meta.reportRead(this, "defaultExport"); return (this as any).$v.defaultExport; }
set defaultExport(value: boolean) { meta.writeField(this, BaseCodeComponentHelper.$field_defaultExport, value); }
}
class ClsCodeComponentHelper extends BaseCodeComponentHelper {
  constructor(args: CodeComponentHelperParams) {
    super();
    meta.initializers.CodeComponentHelper(this, args);
  }
  get typeTag(): "CodeComponentHelper" {
        return "CodeComponentHelper";
      }
}
export const CodeComponentHelper = ClsCodeComponentHelper;

type KnownCodeComponentVariantMeta = ClsCodeComponentVariantMeta;
export function isKnownCodeComponentVariantMeta<T>(x: [Extract<T, CodeComponentVariantMeta>] extends [never] ? never : T): x is [Extract<T, CodeComponentVariantMeta>] extends [never]
  ? never
  : CodeComponentVariantMeta extends T // Needed when T is any
  ? CodeComponentVariantMeta
  : Extract<T, CodeComponentVariantMeta> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCodeComponentVariantMeta;
}

    export function ensureKnownCodeComponentVariantMeta<T>(x: [Extract<T, CodeComponentVariantMeta>] extends [never] ? never : T): CodeComponentVariantMeta {
      assert(isKnownCodeComponentVariantMeta(x), () => mkUnexpectedTypeMsg([CodeComponentVariantMeta], x));
      return x;
    }
    export function ensureMaybeKnownCodeComponentVariantMeta<T>(x: [Extract<T, CodeComponentVariantMeta>] extends [never] ? never : T): CodeComponentVariantMeta | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCodeComponentVariantMeta(x as any);
    }
    
export type CodeComponentVariantMeta = KnownCodeComponentVariantMeta;
export interface CodeComponentVariantMetaParams {
  cssSelector: string /*  */;
displayName: string /*  */;
}

abstract class BaseCodeComponentVariantMeta extends ModelInst {
  
    static isKnown(x: any): x is CodeComponentVariantMeta { return isKnownCodeComponentVariantMeta(x); }
    static getType(): CodeComponentVariantMeta { throw new Error(); }
    static modelTypeName = "CodeComponentVariantMeta";
    
  static readonly $fields = meta.allFields(meta.clsByName["CodeComponentVariantMeta"]);
  static readonly $field_cssSelector: Field = ensure(
    BaseCodeComponentVariantMeta.$fields.find((f) => f.name === "cssSelector"),
    "CodeComponentVariantMeta has no field cssSelector",
  );
static readonly $field_displayName: Field = ensure(
    BaseCodeComponentVariantMeta.$fields.find((f) => f.name === "displayName"),
    "CodeComponentVariantMeta has no field displayName",
  );
  get cssSelector(): string { meta.reportRead(this, "cssSelector"); return (this as any).$v.cssSelector; }
set cssSelector(value: string) { meta.writeField(this, BaseCodeComponentVariantMeta.$field_cssSelector, value); }
get displayName(): string { meta.reportRead(this, "displayName"); return (this as any).$v.displayName; }
set displayName(value: string) { meta.writeField(this, BaseCodeComponentVariantMeta.$field_displayName, value); }
}
class ClsCodeComponentVariantMeta extends BaseCodeComponentVariantMeta {
  constructor(args: CodeComponentVariantMetaParams) {
    super();
    meta.initializers.CodeComponentVariantMeta(this, args);
  }
  get typeTag(): "CodeComponentVariantMeta" {
        return "CodeComponentVariantMeta";
      }
}
export const CodeComponentVariantMeta = ClsCodeComponentVariantMeta;

type KnownCodeComponentMeta = ClsCodeComponentMeta;
export function isKnownCodeComponentMeta<T>(x: [Extract<T, CodeComponentMeta>] extends [never] ? never : T): x is [Extract<T, CodeComponentMeta>] extends [never]
  ? never
  : CodeComponentMeta extends T // Needed when T is any
  ? CodeComponentMeta
  : Extract<T, CodeComponentMeta> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCodeComponentMeta;
}

    export function ensureKnownCodeComponentMeta<T>(x: [Extract<T, CodeComponentMeta>] extends [never] ? never : T): CodeComponentMeta {
      assert(isKnownCodeComponentMeta(x), () => mkUnexpectedTypeMsg([CodeComponentMeta], x));
      return x;
    }
    export function ensureMaybeKnownCodeComponentMeta<T>(x: [Extract<T, CodeComponentMeta>] extends [never] ? never : T): CodeComponentMeta | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCodeComponentMeta(x as any);
    }
    
export type CodeComponentMeta = KnownCodeComponentMeta;
export interface CodeComponentMetaParams {
  importPath: string /*  */;
defaultExport: boolean /*  */;
displayName: string | null | undefined /*  */;
importName: string | null | undefined /*  */;
description: string | null | undefined /*  */;
section: string | null | undefined /*  */;
thumbnailUrl: string | null | undefined /*  */;
classNameProp: string | null | undefined /*  */;
refProp: string | null | undefined /*  */;
defaultStyles: RuleSet | null | undefined /*  */;
defaultDisplay: string | null | undefined /*  */;
isHostLess: boolean /*  */;
isContext: boolean /*  */;
isAttachment: boolean /*  */;
providesData: boolean /*  */;
hasRef: boolean /*  */;
isRepeatable: boolean /*  */;
subtreePrefetchingConfig: boolean | null | undefined /*  */;
styleSections: boolean | null | undefined /*  */;
helpers: CodeComponentHelper | null | undefined /*  */;
defaultSlotContents: Readonly<Record<string, any>> /*  */;
variants: Readonly<Record<string, CodeComponentVariantMeta>> /*  */;
refActions: ReadonlyArray<string> /*  */;
}

abstract class BaseCodeComponentMeta extends ModelInst {
  
    static isKnown(x: any): x is CodeComponentMeta { return isKnownCodeComponentMeta(x); }
    static getType(): CodeComponentMeta { throw new Error(); }
    static modelTypeName = "CodeComponentMeta";
    
  static readonly $fields = meta.allFields(meta.clsByName["CodeComponentMeta"]);
  static readonly $field_importPath: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "importPath"),
    "CodeComponentMeta has no field importPath",
  );
static readonly $field_defaultExport: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "defaultExport"),
    "CodeComponentMeta has no field defaultExport",
  );
static readonly $field_displayName: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "displayName"),
    "CodeComponentMeta has no field displayName",
  );
static readonly $field_importName: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "importName"),
    "CodeComponentMeta has no field importName",
  );
static readonly $field_description: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "description"),
    "CodeComponentMeta has no field description",
  );
static readonly $field_section: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "section"),
    "CodeComponentMeta has no field section",
  );
static readonly $field_thumbnailUrl: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "thumbnailUrl"),
    "CodeComponentMeta has no field thumbnailUrl",
  );
static readonly $field_classNameProp: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "classNameProp"),
    "CodeComponentMeta has no field classNameProp",
  );
static readonly $field_refProp: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "refProp"),
    "CodeComponentMeta has no field refProp",
  );
static readonly $field_defaultStyles: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "defaultStyles"),
    "CodeComponentMeta has no field defaultStyles",
  );
static readonly $field_defaultDisplay: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "defaultDisplay"),
    "CodeComponentMeta has no field defaultDisplay",
  );
static readonly $field_isHostLess: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "isHostLess"),
    "CodeComponentMeta has no field isHostLess",
  );
static readonly $field_isContext: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "isContext"),
    "CodeComponentMeta has no field isContext",
  );
static readonly $field_isAttachment: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "isAttachment"),
    "CodeComponentMeta has no field isAttachment",
  );
static readonly $field_providesData: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "providesData"),
    "CodeComponentMeta has no field providesData",
  );
static readonly $field_hasRef: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "hasRef"),
    "CodeComponentMeta has no field hasRef",
  );
static readonly $field_isRepeatable: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "isRepeatable"),
    "CodeComponentMeta has no field isRepeatable",
  );
static readonly $field_subtreePrefetchingConfig: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "subtreePrefetchingConfig"),
    "CodeComponentMeta has no field subtreePrefetchingConfig",
  );
static readonly $field_styleSections: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "styleSections"),
    "CodeComponentMeta has no field styleSections",
  );
static readonly $field_helpers: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "helpers"),
    "CodeComponentMeta has no field helpers",
  );
static readonly $field_defaultSlotContents: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "defaultSlotContents"),
    "CodeComponentMeta has no field defaultSlotContents",
  );
static readonly $field_variants: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "variants"),
    "CodeComponentMeta has no field variants",
  );
static readonly $field_refActions: Field = ensure(
    BaseCodeComponentMeta.$fields.find((f) => f.name === "refActions"),
    "CodeComponentMeta has no field refActions",
  );
  get importPath(): string { meta.reportRead(this, "importPath"); return (this as any).$v.importPath; }
set importPath(value: string) { meta.writeField(this, BaseCodeComponentMeta.$field_importPath, value); }
get defaultExport(): boolean { meta.reportRead(this, "defaultExport"); return (this as any).$v.defaultExport; }
set defaultExport(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_defaultExport, value); }
get displayName(): string | null | undefined { meta.reportRead(this, "displayName"); return (this as any).$v.displayName; }
set displayName(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_displayName, value); }
get importName(): string | null | undefined { meta.reportRead(this, "importName"); return (this as any).$v.importName; }
set importName(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_importName, value); }
get description(): string | null | undefined { meta.reportRead(this, "description"); return (this as any).$v.description; }
set description(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_description, value); }
get section(): string | null | undefined { meta.reportRead(this, "section"); return (this as any).$v.section; }
set section(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_section, value); }
get thumbnailUrl(): string | null | undefined { meta.reportRead(this, "thumbnailUrl"); return (this as any).$v.thumbnailUrl; }
set thumbnailUrl(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_thumbnailUrl, value); }
get classNameProp(): string | null | undefined { meta.reportRead(this, "classNameProp"); return (this as any).$v.classNameProp; }
set classNameProp(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_classNameProp, value); }
get refProp(): string | null | undefined { meta.reportRead(this, "refProp"); return (this as any).$v.refProp; }
set refProp(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_refProp, value); }
get defaultStyles(): RuleSet | null | undefined { meta.reportRead(this, "defaultStyles"); return (this as any).$v.defaultStyles; }
set defaultStyles(value: RuleSet | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_defaultStyles, value); }
get defaultDisplay(): string | null | undefined { meta.reportRead(this, "defaultDisplay"); return (this as any).$v.defaultDisplay; }
set defaultDisplay(value: string | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_defaultDisplay, value); }
get isHostLess(): boolean { meta.reportRead(this, "isHostLess"); return (this as any).$v.isHostLess; }
set isHostLess(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_isHostLess, value); }
get isContext(): boolean { meta.reportRead(this, "isContext"); return (this as any).$v.isContext; }
set isContext(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_isContext, value); }
get isAttachment(): boolean { meta.reportRead(this, "isAttachment"); return (this as any).$v.isAttachment; }
set isAttachment(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_isAttachment, value); }
get providesData(): boolean { meta.reportRead(this, "providesData"); return (this as any).$v.providesData; }
set providesData(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_providesData, value); }
get hasRef(): boolean { meta.reportRead(this, "hasRef"); return (this as any).$v.hasRef; }
set hasRef(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_hasRef, value); }
get isRepeatable(): boolean { meta.reportRead(this, "isRepeatable"); return (this as any).$v.isRepeatable; }
set isRepeatable(value: boolean) { meta.writeField(this, BaseCodeComponentMeta.$field_isRepeatable, value); }
get subtreePrefetchingConfig(): boolean | null | undefined { meta.reportRead(this, "subtreePrefetchingConfig"); return (this as any).$v.subtreePrefetchingConfig; }
set subtreePrefetchingConfig(value: boolean | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_subtreePrefetchingConfig, value); }
get styleSections(): boolean | null | undefined { meta.reportRead(this, "styleSections"); return (this as any).$v.styleSections; }
set styleSections(value: boolean | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_styleSections, value); }
get helpers(): CodeComponentHelper | null | undefined { meta.reportRead(this, "helpers"); return (this as any).$v.helpers; }
set helpers(value: CodeComponentHelper | null | undefined) { meta.writeField(this, BaseCodeComponentMeta.$field_helpers, value); }
get defaultSlotContents(): Readonly<Record<string, any>> { meta.reportRead(this, "defaultSlotContents"); return (this as any).$v.defaultSlotContents; }
set defaultSlotContents(value: Readonly<Record<string, any>>) { meta.writeField(this, BaseCodeComponentMeta.$field_defaultSlotContents, value); }
get variants(): Readonly<Record<string, CodeComponentVariantMeta>> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
set variants(value: Readonly<Record<string, CodeComponentVariantMeta>>) { meta.writeField(this, BaseCodeComponentMeta.$field_variants, value); }
get refActions(): ReadonlyArray<string> { meta.reportRead(this, "refActions"); return (this as any).$v.refActions; }
set refActions(value: ReadonlyArray<string>) { meta.writeField(this, BaseCodeComponentMeta.$field_refActions, value); }
}
class ClsCodeComponentMeta extends BaseCodeComponentMeta {
  constructor(args: CodeComponentMetaParams) {
    super();
    meta.initializers.CodeComponentMeta(this, args);
  }
  get typeTag(): "CodeComponentMeta" {
        return "CodeComponentMeta";
      }
}
export const CodeComponentMeta = ClsCodeComponentMeta;

type KnownComponent = ClsComponent;
export function isKnownComponent<T>(x: [Extract<T, Component>] extends [never] ? never : T): x is [Extract<T, Component>] extends [never]
  ? never
  : Component extends T // Needed when T is any
  ? Component
  : Extract<T, Component> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponent;
}

    export function ensureKnownComponent<T>(x: [Extract<T, Component>] extends [never] ? never : T): Component {
      assert(isKnownComponent(x), () => mkUnexpectedTypeMsg([Component], x));
      return x;
    }
    export function ensureMaybeKnownComponent<T>(x: [Extract<T, Component>] extends [never] ? never : T): Component | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponent(x as any);
    }
    
export type Component = KnownComponent;
export interface ComponentParams {
  readonly uuid: string /* Const */;
name: string /*  */;
params: ReadonlyArray<Param> /*  */;
states: ReadonlyArray<State> /*  */;
tplTree: TplNode /*  */;
editableByContentEditor: boolean /*  */;
hiddenFromContentEditor: boolean /*  */;
variants: ReadonlyArray<Variant> /*  */;
variantGroups: ReadonlyArray<ComponentVariantGroup> /*  */;
pageMeta: PageMeta | null | undefined /*  */;
codeComponentMeta: CodeComponentMeta | null | undefined /*  */;
type: "plain" | "page" | "code" | "frame" /*  */;
subComps: ReadonlyArray<Component> /* WeakRef */;
superComp: Component | null | undefined /* WeakRef */;
readonly plumeInfo: PlumeInfo | null | undefined /* Const */;
templateInfo: ComponentTemplateInfo | null | undefined /*  */;
metadata: Readonly<Record<string, string>> /*  */;
dataQueries: ReadonlyArray<ComponentDataQuery> /*  */;
serverQueries: ReadonlyArray<ComponentServerQuery> /*  */;
figmaMappings: ReadonlyArray<FigmaComponentMapping> /*  */;
alwaysAutoName: boolean /*  */;
trapsFocus: boolean /*  */;
updatedAt: number | null | undefined /* Inert */;
}

abstract class BaseComponent extends ModelInst {
  
    static isKnown(x: any): x is Component { return isKnownComponent(x); }
    static getType(): Component { throw new Error(); }
    static modelTypeName = "Component";
    
  static readonly $fields = meta.allFields(meta.clsByName["Component"]);
  static readonly $field_uuid: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "uuid"),
    "Component has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "name"),
    "Component has no field name",
  );
static readonly $field_params: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "params"),
    "Component has no field params",
  );
static readonly $field_states: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "states"),
    "Component has no field states",
  );
static readonly $field_tplTree: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "tplTree"),
    "Component has no field tplTree",
  );
static readonly $field_editableByContentEditor: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "editableByContentEditor"),
    "Component has no field editableByContentEditor",
  );
static readonly $field_hiddenFromContentEditor: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "hiddenFromContentEditor"),
    "Component has no field hiddenFromContentEditor",
  );
static readonly $field_variants: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "variants"),
    "Component has no field variants",
  );
static readonly $field_variantGroups: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "variantGroups"),
    "Component has no field variantGroups",
  );
static readonly $field_pageMeta: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "pageMeta"),
    "Component has no field pageMeta",
  );
static readonly $field_codeComponentMeta: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "codeComponentMeta"),
    "Component has no field codeComponentMeta",
  );
static readonly $field_type: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "type"),
    "Component has no field type",
  );
static readonly $field_subComps: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "subComps"),
    "Component has no field subComps",
  );
static readonly $field_superComp: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "superComp"),
    "Component has no field superComp",
  );
static readonly $field_plumeInfo: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "plumeInfo"),
    "Component has no field plumeInfo",
  );
static readonly $field_templateInfo: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "templateInfo"),
    "Component has no field templateInfo",
  );
static readonly $field_metadata: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "metadata"),
    "Component has no field metadata",
  );
static readonly $field_dataQueries: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "dataQueries"),
    "Component has no field dataQueries",
  );
static readonly $field_serverQueries: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "serverQueries"),
    "Component has no field serverQueries",
  );
static readonly $field_figmaMappings: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "figmaMappings"),
    "Component has no field figmaMappings",
  );
static readonly $field_alwaysAutoName: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "alwaysAutoName"),
    "Component has no field alwaysAutoName",
  );
static readonly $field_trapsFocus: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "trapsFocus"),
    "Component has no field trapsFocus",
  );
static readonly $field_updatedAt: Field = ensure(
    BaseComponent.$fields.find((f) => f.name === "updatedAt"),
    "Component has no field updatedAt",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseComponent.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseComponent.$field_name, value); }
get params(): ReadonlyArray<Param> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: ReadonlyArray<Param>) { meta.writeField(this, BaseComponent.$field_params, value); }
get states(): ReadonlyArray<State> { meta.reportRead(this, "states"); return (this as any).$v.states; }
set states(value: ReadonlyArray<State>) { meta.writeField(this, BaseComponent.$field_states, value); }
get tplTree(): TplNode { meta.reportRead(this, "tplTree"); return (this as any).$v.tplTree; }
set tplTree(value: TplNode) { meta.writeField(this, BaseComponent.$field_tplTree, value); }
get editableByContentEditor(): boolean { meta.reportRead(this, "editableByContentEditor"); return (this as any).$v.editableByContentEditor; }
set editableByContentEditor(value: boolean) { meta.writeField(this, BaseComponent.$field_editableByContentEditor, value); }
get hiddenFromContentEditor(): boolean { meta.reportRead(this, "hiddenFromContentEditor"); return (this as any).$v.hiddenFromContentEditor; }
set hiddenFromContentEditor(value: boolean) { meta.writeField(this, BaseComponent.$field_hiddenFromContentEditor, value); }
get variants(): ReadonlyArray<Variant> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
set variants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseComponent.$field_variants, value); }
get variantGroups(): ReadonlyArray<ComponentVariantGroup> { meta.reportRead(this, "variantGroups"); return (this as any).$v.variantGroups; }
set variantGroups(value: ReadonlyArray<ComponentVariantGroup>) { meta.writeField(this, BaseComponent.$field_variantGroups, value); }
get pageMeta(): PageMeta | null | undefined { meta.reportRead(this, "pageMeta"); return (this as any).$v.pageMeta; }
set pageMeta(value: PageMeta | null | undefined) { meta.writeField(this, BaseComponent.$field_pageMeta, value); }
get codeComponentMeta(): CodeComponentMeta | null | undefined { meta.reportRead(this, "codeComponentMeta"); return (this as any).$v.codeComponentMeta; }
set codeComponentMeta(value: CodeComponentMeta | null | undefined) { meta.writeField(this, BaseComponent.$field_codeComponentMeta, value); }
get type(): "plain" | "page" | "code" | "frame" { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: "plain" | "page" | "code" | "frame") { meta.writeField(this, BaseComponent.$field_type, value); }
/* WeakRef */
get subComps(): ReadonlyArray<Component> { meta.reportRead(this, "subComps"); return (this as any).$v.subComps; }
set subComps(value: ReadonlyArray<Component>) { meta.writeField(this, BaseComponent.$field_subComps, value); }
/* WeakRef */
get superComp(): Component | null | undefined { meta.reportRead(this, "superComp"); return (this as any).$v.superComp; }
set superComp(value: Component | null | undefined) { meta.writeField(this, BaseComponent.$field_superComp, value); }
/* Const */
get plumeInfo(): PlumeInfo | null | undefined { meta.reportRead(this, "plumeInfo"); return (this as any).$v.plumeInfo; }
private set plumeInfo(value: PlumeInfo | null | undefined) { meta.writeField(this, BaseComponent.$field_plumeInfo, value); }
get templateInfo(): ComponentTemplateInfo | null | undefined { meta.reportRead(this, "templateInfo"); return (this as any).$v.templateInfo; }
set templateInfo(value: ComponentTemplateInfo | null | undefined) { meta.writeField(this, BaseComponent.$field_templateInfo, value); }
get metadata(): Readonly<Record<string, string>> { meta.reportRead(this, "metadata"); return (this as any).$v.metadata; }
set metadata(value: Readonly<Record<string, string>>) { meta.writeField(this, BaseComponent.$field_metadata, value); }
get dataQueries(): ReadonlyArray<ComponentDataQuery> { meta.reportRead(this, "dataQueries"); return (this as any).$v.dataQueries; }
set dataQueries(value: ReadonlyArray<ComponentDataQuery>) { meta.writeField(this, BaseComponent.$field_dataQueries, value); }
get serverQueries(): ReadonlyArray<ComponentServerQuery> { meta.reportRead(this, "serverQueries"); return (this as any).$v.serverQueries; }
set serverQueries(value: ReadonlyArray<ComponentServerQuery>) { meta.writeField(this, BaseComponent.$field_serverQueries, value); }
get figmaMappings(): ReadonlyArray<FigmaComponentMapping> { meta.reportRead(this, "figmaMappings"); return (this as any).$v.figmaMappings; }
set figmaMappings(value: ReadonlyArray<FigmaComponentMapping>) { meta.writeField(this, BaseComponent.$field_figmaMappings, value); }
get alwaysAutoName(): boolean { meta.reportRead(this, "alwaysAutoName"); return (this as any).$v.alwaysAutoName; }
set alwaysAutoName(value: boolean) { meta.writeField(this, BaseComponent.$field_alwaysAutoName, value); }
get trapsFocus(): boolean { meta.reportRead(this, "trapsFocus"); return (this as any).$v.trapsFocus; }
set trapsFocus(value: boolean) { meta.writeField(this, BaseComponent.$field_trapsFocus, value); }
/* Inert */
get updatedAt(): number | null | undefined { meta.reportInertRead(this, "updatedAt"); return (this as any).$v.updatedAt; }
set updatedAt(value: number | null | undefined) { meta.writeField(this, BaseComponent.$field_updatedAt, value); }
}
class ClsComponent extends BaseComponent {
  constructor(args: ComponentParams) {
    super();
    meta.initializers.Component(this, args);
  }
  get typeTag(): "Component" {
        return "Component";
      }
}
export const Component = ClsComponent;

type KnownNameArg = ClsNameArg;
export function isKnownNameArg<T>(x: [Extract<T, NameArg>] extends [never] ? never : T): x is [Extract<T, NameArg>] extends [never]
  ? never
  : NameArg extends T // Needed when T is any
  ? NameArg
  : Extract<T, NameArg> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseNameArg;
}

    export function ensureKnownNameArg<T>(x: [Extract<T, NameArg>] extends [never] ? never : T): NameArg {
      assert(isKnownNameArg(x), () => mkUnexpectedTypeMsg([NameArg], x));
      return x;
    }
    export function ensureMaybeKnownNameArg<T>(x: [Extract<T, NameArg>] extends [never] ? never : T): NameArg | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownNameArg(x as any);
    }
    
export type NameArg = KnownNameArg;
export interface NameArgParams {
  name: string /*  */;
expr: Expr /*  */;
}

abstract class BaseNameArg extends ModelInst {
  
    static isKnown(x: any): x is NameArg { return isKnownNameArg(x); }
    static getType(): NameArg { throw new Error(); }
    static modelTypeName = "NameArg";
    
  static readonly $fields = meta.allFields(meta.clsByName["NameArg"]);
  static readonly $field_name: Field = ensure(
    BaseNameArg.$fields.find((f) => f.name === "name"),
    "NameArg has no field name",
  );
static readonly $field_expr: Field = ensure(
    BaseNameArg.$fields.find((f) => f.name === "expr"),
    "NameArg has no field expr",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseNameArg.$field_name, value); }
get expr(): Expr { meta.reportRead(this, "expr"); return (this as any).$v.expr; }
set expr(value: Expr) { meta.writeField(this, BaseNameArg.$field_expr, value); }
}
class ClsNameArg extends BaseNameArg {
  constructor(args: NameArgParams) {
    super();
    meta.initializers.NameArg(this, args);
  }
  get typeTag(): "NameArg" {
        return "NameArg";
      }
}
export const NameArg = ClsNameArg;

type KnownPlumeInfo = ClsPlumeInfo;
export function isKnownPlumeInfo<T>(x: [Extract<T, PlumeInfo>] extends [never] ? never : T): x is [Extract<T, PlumeInfo>] extends [never]
  ? never
  : PlumeInfo extends T // Needed when T is any
  ? PlumeInfo
  : Extract<T, PlumeInfo> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePlumeInfo;
}

    export function ensureKnownPlumeInfo<T>(x: [Extract<T, PlumeInfo>] extends [never] ? never : T): PlumeInfo {
      assert(isKnownPlumeInfo(x), () => mkUnexpectedTypeMsg([PlumeInfo], x));
      return x;
    }
    export function ensureMaybeKnownPlumeInfo<T>(x: [Extract<T, PlumeInfo>] extends [never] ? never : T): PlumeInfo | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPlumeInfo(x as any);
    }
    
export type PlumeInfo = KnownPlumeInfo;
export interface PlumeInfoParams {
  type: string /*  */;
}

abstract class BasePlumeInfo extends ModelInst {
  
    static isKnown(x: any): x is PlumeInfo { return isKnownPlumeInfo(x); }
    static getType(): PlumeInfo { throw new Error(); }
    static modelTypeName = "PlumeInfo";
    
  static readonly $fields = meta.allFields(meta.clsByName["PlumeInfo"]);
  static readonly $field_type: Field = ensure(
    BasePlumeInfo.$fields.find((f) => f.name === "type"),
    "PlumeInfo has no field type",
  );
  get type(): string { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: string) { meta.writeField(this, BasePlumeInfo.$field_type, value); }
}
class ClsPlumeInfo extends BasePlumeInfo {
  constructor(args: PlumeInfoParams) {
    super();
    meta.initializers.PlumeInfo(this, args);
  }
  get typeTag(): "PlumeInfo" {
        return "PlumeInfo";
      }
}
export const PlumeInfo = ClsPlumeInfo;

type KnownComponentTemplateInfo = ClsComponentTemplateInfo;
export function isKnownComponentTemplateInfo<T>(x: [Extract<T, ComponentTemplateInfo>] extends [never] ? never : T): x is [Extract<T, ComponentTemplateInfo>] extends [never]
  ? never
  : ComponentTemplateInfo extends T // Needed when T is any
  ? ComponentTemplateInfo
  : Extract<T, ComponentTemplateInfo> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentTemplateInfo;
}

    export function ensureKnownComponentTemplateInfo<T>(x: [Extract<T, ComponentTemplateInfo>] extends [never] ? never : T): ComponentTemplateInfo {
      assert(isKnownComponentTemplateInfo(x), () => mkUnexpectedTypeMsg([ComponentTemplateInfo], x));
      return x;
    }
    export function ensureMaybeKnownComponentTemplateInfo<T>(x: [Extract<T, ComponentTemplateInfo>] extends [never] ? never : T): ComponentTemplateInfo | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentTemplateInfo(x as any);
    }
    
export type ComponentTemplateInfo = KnownComponentTemplateInfo;
export interface ComponentTemplateInfoParams {
  name: string | null | undefined /*  */;
projectId: string | null | undefined /*  */;
componentId: string | null | undefined /*  */;
}

abstract class BaseComponentTemplateInfo extends ModelInst {
  
    static isKnown(x: any): x is ComponentTemplateInfo { return isKnownComponentTemplateInfo(x); }
    static getType(): ComponentTemplateInfo { throw new Error(); }
    static modelTypeName = "ComponentTemplateInfo";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentTemplateInfo"]);
  static readonly $field_name: Field = ensure(
    BaseComponentTemplateInfo.$fields.find((f) => f.name === "name"),
    "ComponentTemplateInfo has no field name",
  );
static readonly $field_projectId: Field = ensure(
    BaseComponentTemplateInfo.$fields.find((f) => f.name === "projectId"),
    "ComponentTemplateInfo has no field projectId",
  );
static readonly $field_componentId: Field = ensure(
    BaseComponentTemplateInfo.$fields.find((f) => f.name === "componentId"),
    "ComponentTemplateInfo has no field componentId",
  );
  get name(): string | null | undefined { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string | null | undefined) { meta.writeField(this, BaseComponentTemplateInfo.$field_name, value); }
get projectId(): string | null | undefined { meta.reportRead(this, "projectId"); return (this as any).$v.projectId; }
set projectId(value: string | null | undefined) { meta.writeField(this, BaseComponentTemplateInfo.$field_projectId, value); }
get componentId(): string | null | undefined { meta.reportRead(this, "componentId"); return (this as any).$v.componentId; }
set componentId(value: string | null | undefined) { meta.writeField(this, BaseComponentTemplateInfo.$field_componentId, value); }
}
class ClsComponentTemplateInfo extends BaseComponentTemplateInfo {
  constructor(args: ComponentTemplateInfoParams) {
    super();
    meta.initializers.ComponentTemplateInfo(this, args);
  }
  get typeTag(): "ComponentTemplateInfo" {
        return "ComponentTemplateInfo";
      }
}
export const ComponentTemplateInfo = ClsComponentTemplateInfo;

type KnownVariant = ClsVariant;
export function isKnownVariant<T>(x: [Extract<T, Variant>] extends [never] ? never : T): x is [Extract<T, Variant>] extends [never]
  ? never
  : Variant extends T // Needed when T is any
  ? Variant
  : Extract<T, Variant> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariant;
}

    export function ensureKnownVariant<T>(x: [Extract<T, Variant>] extends [never] ? never : T): Variant {
      assert(isKnownVariant(x), () => mkUnexpectedTypeMsg([Variant], x));
      return x;
    }
    export function ensureMaybeKnownVariant<T>(x: [Extract<T, Variant>] extends [never] ? never : T): Variant | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariant(x as any);
    }
    
export type Variant = KnownVariant;
export interface VariantParams {
  readonly uuid: string /* Const */;
name: string /*  */;
selectors: ReadonlyArray<string> | null | undefined /*  */;
codeComponentName: string | null | undefined /*  */;
codeComponentVariantKeys: ReadonlyArray<string> | null | undefined /*  */;
parent: VariantGroup | null | undefined /* WeakRef */;
mediaQuery: string | null | undefined /*  */;
description: string | null | undefined /*  */;
forTpl: TplTag | null | undefined | TplComponent | null | undefined | TplSlot | null | undefined /* WeakRef */;
}

abstract class BaseVariant extends ModelInst {
  
    static isKnown(x: any): x is Variant { return isKnownVariant(x); }
    static getType(): Variant { throw new Error(); }
    static modelTypeName = "Variant";
    
  static readonly $fields = meta.allFields(meta.clsByName["Variant"]);
  static readonly $field_uuid: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "uuid"),
    "Variant has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "name"),
    "Variant has no field name",
  );
static readonly $field_selectors: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "selectors"),
    "Variant has no field selectors",
  );
static readonly $field_codeComponentName: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "codeComponentName"),
    "Variant has no field codeComponentName",
  );
static readonly $field_codeComponentVariantKeys: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "codeComponentVariantKeys"),
    "Variant has no field codeComponentVariantKeys",
  );
static readonly $field_parent: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "parent"),
    "Variant has no field parent",
  );
static readonly $field_mediaQuery: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "mediaQuery"),
    "Variant has no field mediaQuery",
  );
static readonly $field_description: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "description"),
    "Variant has no field description",
  );
static readonly $field_forTpl: Field = ensure(
    BaseVariant.$fields.find((f) => f.name === "forTpl"),
    "Variant has no field forTpl",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseVariant.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseVariant.$field_name, value); }
get selectors(): ReadonlyArray<string> | null | undefined { meta.reportRead(this, "selectors"); return (this as any).$v.selectors; }
set selectors(value: ReadonlyArray<string> | null | undefined) { meta.writeField(this, BaseVariant.$field_selectors, value); }
get codeComponentName(): string | null | undefined { meta.reportRead(this, "codeComponentName"); return (this as any).$v.codeComponentName; }
set codeComponentName(value: string | null | undefined) { meta.writeField(this, BaseVariant.$field_codeComponentName, value); }
get codeComponentVariantKeys(): ReadonlyArray<string> | null | undefined { meta.reportRead(this, "codeComponentVariantKeys"); return (this as any).$v.codeComponentVariantKeys; }
set codeComponentVariantKeys(value: ReadonlyArray<string> | null | undefined) { meta.writeField(this, BaseVariant.$field_codeComponentVariantKeys, value); }
/* WeakRef */
get parent(): VariantGroup | null | undefined { meta.reportRead(this, "parent"); return (this as any).$v.parent; }
set parent(value: VariantGroup | null | undefined) { meta.writeField(this, BaseVariant.$field_parent, value); }
get mediaQuery(): string | null | undefined { meta.reportRead(this, "mediaQuery"); return (this as any).$v.mediaQuery; }
set mediaQuery(value: string | null | undefined) { meta.writeField(this, BaseVariant.$field_mediaQuery, value); }
get description(): string | null | undefined { meta.reportRead(this, "description"); return (this as any).$v.description; }
set description(value: string | null | undefined) { meta.writeField(this, BaseVariant.$field_description, value); }
/* WeakRef */
get forTpl(): TplTag | null | undefined | TplComponent | null | undefined | TplSlot | null | undefined { meta.reportRead(this, "forTpl"); return (this as any).$v.forTpl; }
set forTpl(value: TplTag | null | undefined | TplComponent | null | undefined | TplSlot | null | undefined) { meta.writeField(this, BaseVariant.$field_forTpl, value); }
}
class ClsVariant extends BaseVariant {
  constructor(args: VariantParams) {
    super();
    meta.initializers.Variant(this, args);
  }
  get typeTag(): "Variant" {
        return "Variant";
      }
}
export const Variant = ClsVariant;

type KnownVariantGroup = KnownGlobalVariantGroup | KnownComponentVariantGroup;
export function isKnownVariantGroup<T>(x: [Extract<T, VariantGroup>] extends [never] ? never : T): x is [Extract<T, VariantGroup>] extends [never]
  ? never
  : VariantGroup extends T // Needed when T is any
  ? VariantGroup
  : Extract<T, VariantGroup> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariantGroup;
}

    export function ensureKnownVariantGroup<T>(x: [Extract<T, VariantGroup>] extends [never] ? never : T): VariantGroup {
      assert(isKnownVariantGroup(x), () => mkUnexpectedTypeMsg([VariantGroup], x));
      return x;
    }
    export function ensureMaybeKnownVariantGroup<T>(x: [Extract<T, VariantGroup>] extends [never] ? never : T): VariantGroup | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariantGroup(x as any);
    }
    
export type VariantGroup = KnownVariantGroup;
export interface VariantGroupParams {
  readonly uuid: string /* Const */;
variants: ReadonlyArray<Variant> /*  */;
multi: boolean /*  */;
}

abstract class BaseVariantGroup extends ModelInst {
  
    static isKnown(x: any): x is VariantGroup { return isKnownVariantGroup(x); }
    static getType(): VariantGroup { throw new Error(); }
    static modelTypeName = "VariantGroup";
    
  static readonly $fields = meta.allFields(meta.clsByName["VariantGroup"]);
  static readonly $field_uuid: Field = ensure(
    BaseVariantGroup.$fields.find((f) => f.name === "uuid"),
    "VariantGroup has no field uuid",
  );
static readonly $field_variants: Field = ensure(
    BaseVariantGroup.$fields.find((f) => f.name === "variants"),
    "VariantGroup has no field variants",
  );
static readonly $field_multi: Field = ensure(
    BaseVariantGroup.$fields.find((f) => f.name === "multi"),
    "VariantGroup has no field multi",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseVariantGroup.$field_uuid, value); }
get variants(): ReadonlyArray<Variant> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
set variants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseVariantGroup.$field_variants, value); }
get multi(): boolean { meta.reportRead(this, "multi"); return (this as any).$v.multi; }
set multi(value: boolean) { meta.writeField(this, BaseVariantGroup.$field_multi, value); }
}

export const VariantGroup = BaseVariantGroup;

type KnownGlobalVariantGroup = ClsGlobalVariantGroup;
export function isKnownGlobalVariantGroup<T>(x: [Extract<T, GlobalVariantGroup>] extends [never] ? never : T): x is [Extract<T, GlobalVariantGroup>] extends [never]
  ? never
  : GlobalVariantGroup extends T // Needed when T is any
  ? GlobalVariantGroup
  : Extract<T, GlobalVariantGroup> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseGlobalVariantGroup;
}

    export function ensureKnownGlobalVariantGroup<T>(x: [Extract<T, GlobalVariantGroup>] extends [never] ? never : T): GlobalVariantGroup {
      assert(isKnownGlobalVariantGroup(x), () => mkUnexpectedTypeMsg([GlobalVariantGroup], x));
      return x;
    }
    export function ensureMaybeKnownGlobalVariantGroup<T>(x: [Extract<T, GlobalVariantGroup>] extends [never] ? never : T): GlobalVariantGroup | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownGlobalVariantGroup(x as any);
    }
    
export type GlobalVariantGroup = KnownGlobalVariantGroup;
export interface GlobalVariantGroupParams {
  type: "global-screen" | "global-user-defined" /*  */;
readonly param: GlobalVariantGroupParam /* Const */;
readonly uuid: string /* Const */;
variants: ReadonlyArray<Variant> /*  */;
multi: boolean /*  */;
}

abstract class BaseGlobalVariantGroup extends BaseVariantGroup {
  
    static isKnown(x: any): x is GlobalVariantGroup { return isKnownGlobalVariantGroup(x); }
    static getType(): GlobalVariantGroup { throw new Error(); }
    static modelTypeName = "GlobalVariantGroup";
    
  static readonly $fields = meta.allFields(meta.clsByName["GlobalVariantGroup"]);
  static readonly $field_type: Field = ensure(
    BaseGlobalVariantGroup.$fields.find((f) => f.name === "type"),
    "GlobalVariantGroup has no field type",
  );
static readonly $field_param: Field = ensure(
    BaseGlobalVariantGroup.$fields.find((f) => f.name === "param"),
    "GlobalVariantGroup has no field param",
  );
  get type(): "global-screen" | "global-user-defined" { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: "global-screen" | "global-user-defined") { meta.writeField(this, BaseGlobalVariantGroup.$field_type, value); }
/* Const */
get param(): GlobalVariantGroupParam { meta.reportRead(this, "param"); return (this as any).$v.param; }
private set param(value: GlobalVariantGroupParam) { meta.writeField(this, BaseGlobalVariantGroup.$field_param, value); }
}
class ClsGlobalVariantGroup extends BaseGlobalVariantGroup {
  constructor(args: GlobalVariantGroupParams) {
    super();
    meta.initializers.GlobalVariantGroup(this, args);
  }
  get typeTag(): "GlobalVariantGroup" {
        return "GlobalVariantGroup";
      }
}
export const GlobalVariantGroup = ClsGlobalVariantGroup;

type KnownComponentVariantGroup = ClsComponentVariantGroup;
export function isKnownComponentVariantGroup<T>(x: [Extract<T, ComponentVariantGroup>] extends [never] ? never : T): x is [Extract<T, ComponentVariantGroup>] extends [never]
  ? never
  : ComponentVariantGroup extends T // Needed when T is any
  ? ComponentVariantGroup
  : Extract<T, ComponentVariantGroup> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentVariantGroup;
}

    export function ensureKnownComponentVariantGroup<T>(x: [Extract<T, ComponentVariantGroup>] extends [never] ? never : T): ComponentVariantGroup {
      assert(isKnownComponentVariantGroup(x), () => mkUnexpectedTypeMsg([ComponentVariantGroup], x));
      return x;
    }
    export function ensureMaybeKnownComponentVariantGroup<T>(x: [Extract<T, ComponentVariantGroup>] extends [never] ? never : T): ComponentVariantGroup | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentVariantGroup(x as any);
    }
    
export type ComponentVariantGroup = KnownComponentVariantGroup;
export interface ComponentVariantGroupParams {
  type: "component" /*  */;
readonly param: StateParam /* Const,WeakRef */;
readonly linkedState: VariantGroupState /* Const,WeakRef */;
readonly uuid: string /* Const */;
variants: ReadonlyArray<Variant> /*  */;
multi: boolean /*  */;
}

abstract class BaseComponentVariantGroup extends BaseVariantGroup {
  
    static isKnown(x: any): x is ComponentVariantGroup { return isKnownComponentVariantGroup(x); }
    static getType(): ComponentVariantGroup { throw new Error(); }
    static modelTypeName = "ComponentVariantGroup";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentVariantGroup"]);
  static readonly $field_type: Field = ensure(
    BaseComponentVariantGroup.$fields.find((f) => f.name === "type"),
    "ComponentVariantGroup has no field type",
  );
static readonly $field_param: Field = ensure(
    BaseComponentVariantGroup.$fields.find((f) => f.name === "param"),
    "ComponentVariantGroup has no field param",
  );
static readonly $field_linkedState: Field = ensure(
    BaseComponentVariantGroup.$fields.find((f) => f.name === "linkedState"),
    "ComponentVariantGroup has no field linkedState",
  );
  get type(): "component" { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: "component") { meta.writeField(this, BaseComponentVariantGroup.$field_type, value); }
/* Const,WeakRef */
get param(): StateParam { meta.reportRead(this, "param"); return (this as any).$v.param; }
private set param(value: StateParam) { meta.writeField(this, BaseComponentVariantGroup.$field_param, value); }
/* Const,WeakRef */
get linkedState(): VariantGroupState { meta.reportRead(this, "linkedState"); return (this as any).$v.linkedState; }
private set linkedState(value: VariantGroupState) { meta.writeField(this, BaseComponentVariantGroup.$field_linkedState, value); }
}
class ClsComponentVariantGroup extends BaseComponentVariantGroup {
  constructor(args: ComponentVariantGroupParams) {
    super();
    meta.initializers.ComponentVariantGroup(this, args);
  }
  get typeTag(): "ComponentVariantGroup" {
        return "ComponentVariantGroup";
      }
}
export const ComponentVariantGroup = ClsComponentVariantGroup;

type KnownVariantSetting = ClsVariantSetting;
export function isKnownVariantSetting<T>(x: [Extract<T, VariantSetting>] extends [never] ? never : T): x is [Extract<T, VariantSetting>] extends [never]
  ? never
  : VariantSetting extends T // Needed when T is any
  ? VariantSetting
  : Extract<T, VariantSetting> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariantSetting;
}

    export function ensureKnownVariantSetting<T>(x: [Extract<T, VariantSetting>] extends [never] ? never : T): VariantSetting {
      assert(isKnownVariantSetting(x), () => mkUnexpectedTypeMsg([VariantSetting], x));
      return x;
    }
    export function ensureMaybeKnownVariantSetting<T>(x: [Extract<T, VariantSetting>] extends [never] ? never : T): VariantSetting | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariantSetting(x as any);
    }
    
export type VariantSetting = KnownVariantSetting;
export interface VariantSettingParams {
  variants: ReadonlyArray<Variant> /* WeakRef */;
args: ReadonlyArray<Arg> /*  */;
attrs: Readonly<Record<string, Expr>> /*  */;
rs: RuleSet /*  */;
dataCond: CustomCode | null | undefined | ObjectPath | null | undefined /*  */;
dataRep: Rep | null | undefined /*  */;
text: RichText | null | undefined /*  */;
columnsConfig: ColumnsConfig | null | undefined /*  */;
}

abstract class BaseVariantSetting extends ModelInst {
  
    static isKnown(x: any): x is VariantSetting { return isKnownVariantSetting(x); }
    static getType(): VariantSetting { throw new Error(); }
    static modelTypeName = "VariantSetting";
    
  static readonly $fields = meta.allFields(meta.clsByName["VariantSetting"]);
  static readonly $field_variants: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "variants"),
    "VariantSetting has no field variants",
  );
static readonly $field_args: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "args"),
    "VariantSetting has no field args",
  );
static readonly $field_attrs: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "attrs"),
    "VariantSetting has no field attrs",
  );
static readonly $field_rs: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "rs"),
    "VariantSetting has no field rs",
  );
static readonly $field_dataCond: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "dataCond"),
    "VariantSetting has no field dataCond",
  );
static readonly $field_dataRep: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "dataRep"),
    "VariantSetting has no field dataRep",
  );
static readonly $field_text: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "text"),
    "VariantSetting has no field text",
  );
static readonly $field_columnsConfig: Field = ensure(
    BaseVariantSetting.$fields.find((f) => f.name === "columnsConfig"),
    "VariantSetting has no field columnsConfig",
  );
  /* WeakRef */
get variants(): ReadonlyArray<Variant> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
set variants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseVariantSetting.$field_variants, value); }
get args(): ReadonlyArray<Arg> { meta.reportRead(this, "args"); return (this as any).$v.args; }
set args(value: ReadonlyArray<Arg>) { meta.writeField(this, BaseVariantSetting.$field_args, value); }
get attrs(): Readonly<Record<string, Expr>> { meta.reportRead(this, "attrs"); return (this as any).$v.attrs; }
set attrs(value: Readonly<Record<string, Expr>>) { meta.writeField(this, BaseVariantSetting.$field_attrs, value); }
get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseVariantSetting.$field_rs, value); }
get dataCond(): CustomCode | null | undefined | ObjectPath | null | undefined { meta.reportRead(this, "dataCond"); return (this as any).$v.dataCond; }
set dataCond(value: CustomCode | null | undefined | ObjectPath | null | undefined) { meta.writeField(this, BaseVariantSetting.$field_dataCond, value); }
get dataRep(): Rep | null | undefined { meta.reportRead(this, "dataRep"); return (this as any).$v.dataRep; }
set dataRep(value: Rep | null | undefined) { meta.writeField(this, BaseVariantSetting.$field_dataRep, value); }
get text(): RichText | null | undefined { meta.reportRead(this, "text"); return (this as any).$v.text; }
set text(value: RichText | null | undefined) { meta.writeField(this, BaseVariantSetting.$field_text, value); }
get columnsConfig(): ColumnsConfig | null | undefined { meta.reportRead(this, "columnsConfig"); return (this as any).$v.columnsConfig; }
set columnsConfig(value: ColumnsConfig | null | undefined) { meta.writeField(this, BaseVariantSetting.$field_columnsConfig, value); }
}
class ClsVariantSetting extends BaseVariantSetting {
  constructor(args: VariantSettingParams) {
    super();
    meta.initializers.VariantSetting(this, args);
  }
  get typeTag(): "VariantSetting" {
        return "VariantSetting";
      }
}
export const VariantSetting = ClsVariantSetting;

type KnownInteraction = ClsInteraction;
export function isKnownInteraction<T>(x: [Extract<T, Interaction>] extends [never] ? never : T): x is [Extract<T, Interaction>] extends [never]
  ? never
  : Interaction extends T // Needed when T is any
  ? Interaction
  : Extract<T, Interaction> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseInteraction;
}

    export function ensureKnownInteraction<T>(x: [Extract<T, Interaction>] extends [never] ? never : T): Interaction {
      assert(isKnownInteraction(x), () => mkUnexpectedTypeMsg([Interaction], x));
      return x;
    }
    export function ensureMaybeKnownInteraction<T>(x: [Extract<T, Interaction>] extends [never] ? never : T): Interaction | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownInteraction(x as any);
    }
    
export type Interaction = KnownInteraction;
export interface InteractionParams {
  interactionName: string /*  */;
actionName: string /*  */;
args: ReadonlyArray<NameArg> /*  */;
condExpr: CustomCode | null | undefined | ObjectPath | null | undefined /*  */;
conditionalMode: "always" | "never" | "expression" /*  */;
readonly uuid: string /* Const */;
parent: EventHandler /* WeakRef */;
}

abstract class BaseInteraction extends ModelInst {
  
    static isKnown(x: any): x is Interaction { return isKnownInteraction(x); }
    static getType(): Interaction { throw new Error(); }
    static modelTypeName = "Interaction";
    
  static readonly $fields = meta.allFields(meta.clsByName["Interaction"]);
  static readonly $field_interactionName: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "interactionName"),
    "Interaction has no field interactionName",
  );
static readonly $field_actionName: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "actionName"),
    "Interaction has no field actionName",
  );
static readonly $field_args: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "args"),
    "Interaction has no field args",
  );
static readonly $field_condExpr: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "condExpr"),
    "Interaction has no field condExpr",
  );
static readonly $field_conditionalMode: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "conditionalMode"),
    "Interaction has no field conditionalMode",
  );
static readonly $field_uuid: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "uuid"),
    "Interaction has no field uuid",
  );
static readonly $field_parent: Field = ensure(
    BaseInteraction.$fields.find((f) => f.name === "parent"),
    "Interaction has no field parent",
  );
  get interactionName(): string { meta.reportRead(this, "interactionName"); return (this as any).$v.interactionName; }
set interactionName(value: string) { meta.writeField(this, BaseInteraction.$field_interactionName, value); }
get actionName(): string { meta.reportRead(this, "actionName"); return (this as any).$v.actionName; }
set actionName(value: string) { meta.writeField(this, BaseInteraction.$field_actionName, value); }
get args(): ReadonlyArray<NameArg> { meta.reportRead(this, "args"); return (this as any).$v.args; }
set args(value: ReadonlyArray<NameArg>) { meta.writeField(this, BaseInteraction.$field_args, value); }
get condExpr(): CustomCode | null | undefined | ObjectPath | null | undefined { meta.reportRead(this, "condExpr"); return (this as any).$v.condExpr; }
set condExpr(value: CustomCode | null | undefined | ObjectPath | null | undefined) { meta.writeField(this, BaseInteraction.$field_condExpr, value); }
get conditionalMode(): "always" | "never" | "expression" { meta.reportRead(this, "conditionalMode"); return (this as any).$v.conditionalMode; }
set conditionalMode(value: "always" | "never" | "expression") { meta.writeField(this, BaseInteraction.$field_conditionalMode, value); }
/* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseInteraction.$field_uuid, value); }
/* WeakRef */
get parent(): EventHandler { meta.reportRead(this, "parent"); return (this as any).$v.parent; }
set parent(value: EventHandler) { meta.writeField(this, BaseInteraction.$field_parent, value); }
}
class ClsInteraction extends BaseInteraction {
  constructor(args: InteractionParams) {
    super();
    meta.initializers.Interaction(this, args);
  }
  get typeTag(): "Interaction" {
        return "Interaction";
      }
}
export const Interaction = ClsInteraction;

type KnownColumnsConfig = ClsColumnsConfig;
export function isKnownColumnsConfig<T>(x: [Extract<T, ColumnsConfig>] extends [never] ? never : T): x is [Extract<T, ColumnsConfig>] extends [never]
  ? never
  : ColumnsConfig extends T // Needed when T is any
  ? ColumnsConfig
  : Extract<T, ColumnsConfig> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseColumnsConfig;
}

    export function ensureKnownColumnsConfig<T>(x: [Extract<T, ColumnsConfig>] extends [never] ? never : T): ColumnsConfig {
      assert(isKnownColumnsConfig(x), () => mkUnexpectedTypeMsg([ColumnsConfig], x));
      return x;
    }
    export function ensureMaybeKnownColumnsConfig<T>(x: [Extract<T, ColumnsConfig>] extends [never] ? never : T): ColumnsConfig | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownColumnsConfig(x as any);
    }
    
export type ColumnsConfig = KnownColumnsConfig;
export interface ColumnsConfigParams {
  breakUpRows: boolean /*  */;
colsSizes: ReadonlyArray<number> /*  */;
}

abstract class BaseColumnsConfig extends ModelInst {
  
    static isKnown(x: any): x is ColumnsConfig { return isKnownColumnsConfig(x); }
    static getType(): ColumnsConfig { throw new Error(); }
    static modelTypeName = "ColumnsConfig";
    
  static readonly $fields = meta.allFields(meta.clsByName["ColumnsConfig"]);
  static readonly $field_breakUpRows: Field = ensure(
    BaseColumnsConfig.$fields.find((f) => f.name === "breakUpRows"),
    "ColumnsConfig has no field breakUpRows",
  );
static readonly $field_colsSizes: Field = ensure(
    BaseColumnsConfig.$fields.find((f) => f.name === "colsSizes"),
    "ColumnsConfig has no field colsSizes",
  );
  get breakUpRows(): boolean { meta.reportRead(this, "breakUpRows"); return (this as any).$v.breakUpRows; }
set breakUpRows(value: boolean) { meta.writeField(this, BaseColumnsConfig.$field_breakUpRows, value); }
get colsSizes(): ReadonlyArray<number> { meta.reportRead(this, "colsSizes"); return (this as any).$v.colsSizes; }
set colsSizes(value: ReadonlyArray<number>) { meta.writeField(this, BaseColumnsConfig.$field_colsSizes, value); }
}
class ClsColumnsConfig extends BaseColumnsConfig {
  constructor(args: ColumnsConfigParams) {
    super();
    meta.initializers.ColumnsConfig(this, args);
  }
  get typeTag(): "ColumnsConfig" {
        return "ColumnsConfig";
      }
}
export const ColumnsConfig = ClsColumnsConfig;

type KnownMarker = KnownStyleMarker | KnownNodeMarker;
export function isKnownMarker<T>(x: [Extract<T, Marker>] extends [never] ? never : T): x is [Extract<T, Marker>] extends [never]
  ? never
  : Marker extends T // Needed when T is any
  ? Marker
  : Extract<T, Marker> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseMarker;
}

    export function ensureKnownMarker<T>(x: [Extract<T, Marker>] extends [never] ? never : T): Marker {
      assert(isKnownMarker(x), () => mkUnexpectedTypeMsg([Marker], x));
      return x;
    }
    export function ensureMaybeKnownMarker<T>(x: [Extract<T, Marker>] extends [never] ? never : T): Marker | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownMarker(x as any);
    }
    
export type Marker = KnownMarker;
export interface MarkerParams {
  position: number /*  */;
length: number /*  */;
}

abstract class BaseMarker extends ModelInst {
  
    static isKnown(x: any): x is Marker { return isKnownMarker(x); }
    static getType(): Marker { throw new Error(); }
    static modelTypeName = "Marker";
    
  static readonly $fields = meta.allFields(meta.clsByName["Marker"]);
  static readonly $field_position: Field = ensure(
    BaseMarker.$fields.find((f) => f.name === "position"),
    "Marker has no field position",
  );
static readonly $field_length: Field = ensure(
    BaseMarker.$fields.find((f) => f.name === "length"),
    "Marker has no field length",
  );
  get position(): number { meta.reportRead(this, "position"); return (this as any).$v.position; }
set position(value: number) { meta.writeField(this, BaseMarker.$field_position, value); }
get length(): number { meta.reportRead(this, "length"); return (this as any).$v.length; }
set length(value: number) { meta.writeField(this, BaseMarker.$field_length, value); }
}

export const Marker = BaseMarker;

type KnownStyleMarker = ClsStyleMarker;
export function isKnownStyleMarker<T>(x: [Extract<T, StyleMarker>] extends [never] ? never : T): x is [Extract<T, StyleMarker>] extends [never]
  ? never
  : StyleMarker extends T // Needed when T is any
  ? StyleMarker
  : Extract<T, StyleMarker> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleMarker;
}

    export function ensureKnownStyleMarker<T>(x: [Extract<T, StyleMarker>] extends [never] ? never : T): StyleMarker {
      assert(isKnownStyleMarker(x), () => mkUnexpectedTypeMsg([StyleMarker], x));
      return x;
    }
    export function ensureMaybeKnownStyleMarker<T>(x: [Extract<T, StyleMarker>] extends [never] ? never : T): StyleMarker | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleMarker(x as any);
    }
    
export type StyleMarker = KnownStyleMarker;
export interface StyleMarkerParams {
  rs: RuleSet /*  */;
position: number /*  */;
length: number /*  */;
}

abstract class BaseStyleMarker extends BaseMarker {
  
    static isKnown(x: any): x is StyleMarker { return isKnownStyleMarker(x); }
    static getType(): StyleMarker { throw new Error(); }
    static modelTypeName = "StyleMarker";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleMarker"]);
  static readonly $field_rs: Field = ensure(
    BaseStyleMarker.$fields.find((f) => f.name === "rs"),
    "StyleMarker has no field rs",
  );
  get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseStyleMarker.$field_rs, value); }
}
class ClsStyleMarker extends BaseStyleMarker {
  constructor(args: StyleMarkerParams) {
    super();
    meta.initializers.StyleMarker(this, args);
  }
  get typeTag(): "StyleMarker" {
        return "StyleMarker";
      }
}
export const StyleMarker = ClsStyleMarker;

type KnownNodeMarker = ClsNodeMarker;
export function isKnownNodeMarker<T>(x: [Extract<T, NodeMarker>] extends [never] ? never : T): x is [Extract<T, NodeMarker>] extends [never]
  ? never
  : NodeMarker extends T // Needed when T is any
  ? NodeMarker
  : Extract<T, NodeMarker> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseNodeMarker;
}

    export function ensureKnownNodeMarker<T>(x: [Extract<T, NodeMarker>] extends [never] ? never : T): NodeMarker {
      assert(isKnownNodeMarker(x), () => mkUnexpectedTypeMsg([NodeMarker], x));
      return x;
    }
    export function ensureMaybeKnownNodeMarker<T>(x: [Extract<T, NodeMarker>] extends [never] ? never : T): NodeMarker | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownNodeMarker(x as any);
    }
    
export type NodeMarker = KnownNodeMarker;
export interface NodeMarkerParams {
  tpl: TplNode /* WeakRef */;
position: number /*  */;
length: number /*  */;
}

abstract class BaseNodeMarker extends BaseMarker {
  
    static isKnown(x: any): x is NodeMarker { return isKnownNodeMarker(x); }
    static getType(): NodeMarker { throw new Error(); }
    static modelTypeName = "NodeMarker";
    
  static readonly $fields = meta.allFields(meta.clsByName["NodeMarker"]);
  static readonly $field_tpl: Field = ensure(
    BaseNodeMarker.$fields.find((f) => f.name === "tpl"),
    "NodeMarker has no field tpl",
  );
  /* WeakRef */
get tpl(): TplNode { meta.reportRead(this, "tpl"); return (this as any).$v.tpl; }
set tpl(value: TplNode) { meta.writeField(this, BaseNodeMarker.$field_tpl, value); }
}
class ClsNodeMarker extends BaseNodeMarker {
  constructor(args: NodeMarkerParams) {
    super();
    meta.initializers.NodeMarker(this, args);
  }
  get typeTag(): "NodeMarker" {
        return "NodeMarker";
      }
}
export const NodeMarker = ClsNodeMarker;

type KnownRichText = KnownRawText | KnownExprText;
export function isKnownRichText<T>(x: [Extract<T, RichText>] extends [never] ? never : T): x is [Extract<T, RichText>] extends [never]
  ? never
  : RichText extends T // Needed when T is any
  ? RichText
  : Extract<T, RichText> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRichText;
}

    export function ensureKnownRichText<T>(x: [Extract<T, RichText>] extends [never] ? never : T): RichText {
      assert(isKnownRichText(x), () => mkUnexpectedTypeMsg([RichText], x));
      return x;
    }
    export function ensureMaybeKnownRichText<T>(x: [Extract<T, RichText>] extends [never] ? never : T): RichText | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRichText(x as any);
    }
    
export type RichText = KnownRichText;
export interface RichTextParams {
  
}

abstract class BaseRichText extends ModelInst {
  
    static isKnown(x: any): x is RichText { return isKnownRichText(x); }
    static getType(): RichText { throw new Error(); }
    static modelTypeName = "RichText";
    
  static readonly $fields = meta.allFields(meta.clsByName["RichText"]);
  
  
}

export const RichText = BaseRichText;

type KnownRawText = ClsRawText;
export function isKnownRawText<T>(x: [Extract<T, RawText>] extends [never] ? never : T): x is [Extract<T, RawText>] extends [never]
  ? never
  : RawText extends T // Needed when T is any
  ? RawText
  : Extract<T, RawText> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRawText;
}

    export function ensureKnownRawText<T>(x: [Extract<T, RawText>] extends [never] ? never : T): RawText {
      assert(isKnownRawText(x), () => mkUnexpectedTypeMsg([RawText], x));
      return x;
    }
    export function ensureMaybeKnownRawText<T>(x: [Extract<T, RawText>] extends [never] ? never : T): RawText | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRawText(x as any);
    }
    
export type RawText = KnownRawText;
export interface RawTextParams {
  markers: ReadonlyArray<Marker> /*  */;
text: string /*  */;
}

abstract class BaseRawText extends BaseRichText {
  
    static isKnown(x: any): x is RawText { return isKnownRawText(x); }
    static getType(): RawText { throw new Error(); }
    static modelTypeName = "RawText";
    
  static readonly $fields = meta.allFields(meta.clsByName["RawText"]);
  static readonly $field_markers: Field = ensure(
    BaseRawText.$fields.find((f) => f.name === "markers"),
    "RawText has no field markers",
  );
static readonly $field_text: Field = ensure(
    BaseRawText.$fields.find((f) => f.name === "text"),
    "RawText has no field text",
  );
  get markers(): ReadonlyArray<Marker> { meta.reportRead(this, "markers"); return (this as any).$v.markers; }
set markers(value: ReadonlyArray<Marker>) { meta.writeField(this, BaseRawText.$field_markers, value); }
get text(): string { meta.reportRead(this, "text"); return (this as any).$v.text; }
set text(value: string) { meta.writeField(this, BaseRawText.$field_text, value); }
}
class ClsRawText extends BaseRawText {
  constructor(args: RawTextParams) {
    super();
    meta.initializers.RawText(this, args);
  }
  get typeTag(): "RawText" {
        return "RawText";
      }
}
export const RawText = ClsRawText;

type KnownExprText = ClsExprText;
export function isKnownExprText<T>(x: [Extract<T, ExprText>] extends [never] ? never : T): x is [Extract<T, ExprText>] extends [never]
  ? never
  : ExprText extends T // Needed when T is any
  ? ExprText
  : Extract<T, ExprText> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseExprText;
}

    export function ensureKnownExprText<T>(x: [Extract<T, ExprText>] extends [never] ? never : T): ExprText {
      assert(isKnownExprText(x), () => mkUnexpectedTypeMsg([ExprText], x));
      return x;
    }
    export function ensureMaybeKnownExprText<T>(x: [Extract<T, ExprText>] extends [never] ? never : T): ExprText | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownExprText(x as any);
    }
    
export type ExprText = KnownExprText;
export interface ExprTextParams {
  expr: CustomCode | ObjectPath | TemplatedString /*  */;
html: boolean /*  */;
}

abstract class BaseExprText extends BaseRichText {
  
    static isKnown(x: any): x is ExprText { return isKnownExprText(x); }
    static getType(): ExprText { throw new Error(); }
    static modelTypeName = "ExprText";
    
  static readonly $fields = meta.allFields(meta.clsByName["ExprText"]);
  static readonly $field_expr: Field = ensure(
    BaseExprText.$fields.find((f) => f.name === "expr"),
    "ExprText has no field expr",
  );
static readonly $field_html: Field = ensure(
    BaseExprText.$fields.find((f) => f.name === "html"),
    "ExprText has no field html",
  );
  get expr(): CustomCode | ObjectPath | TemplatedString { meta.reportRead(this, "expr"); return (this as any).$v.expr; }
set expr(value: CustomCode | ObjectPath | TemplatedString) { meta.writeField(this, BaseExprText.$field_expr, value); }
get html(): boolean { meta.reportRead(this, "html"); return (this as any).$v.html; }
set html(value: boolean) { meta.writeField(this, BaseExprText.$field_html, value); }
}
class ClsExprText extends BaseExprText {
  constructor(args: ExprTextParams) {
    super();
    meta.initializers.ExprText(this, args);
  }
  get typeTag(): "ExprText" {
        return "ExprText";
      }
}
export const ExprText = ClsExprText;

type KnownVar = ClsVar;
export function isKnownVar<T>(x: [Extract<T, Var>] extends [never] ? never : T): x is [Extract<T, Var>] extends [never]
  ? never
  : Var extends T // Needed when T is any
  ? Var
  : Extract<T, Var> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVar;
}

    export function ensureKnownVar<T>(x: [Extract<T, Var>] extends [never] ? never : T): Var {
      assert(isKnownVar(x), () => mkUnexpectedTypeMsg([Var], x));
      return x;
    }
    export function ensureMaybeKnownVar<T>(x: [Extract<T, Var>] extends [never] ? never : T): Var | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVar(x as any);
    }
    
export type Var = KnownVar;
export interface VarParams {
  name: string /*  */;
readonly uuid: string /* Const */;
}

abstract class BaseVar extends ModelInst {
  
    static isKnown(x: any): x is Var { return isKnownVar(x); }
    static getType(): Var { throw new Error(); }
    static modelTypeName = "Var";
    
  static readonly $fields = meta.allFields(meta.clsByName["Var"]);
  static readonly $field_name: Field = ensure(
    BaseVar.$fields.find((f) => f.name === "name"),
    "Var has no field name",
  );
static readonly $field_uuid: Field = ensure(
    BaseVar.$fields.find((f) => f.name === "uuid"),
    "Var has no field uuid",
  );
  get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseVar.$field_name, value); }
/* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseVar.$field_uuid, value); }
}
class ClsVar extends BaseVar {
  constructor(args: VarParams) {
    super();
    meta.initializers.Var(this, args);
  }
  get typeTag(): "Var" {
        return "Var";
      }
}
export const Var = ClsVar;

type KnownBindingStruct = KnownRep | KnownParam | KnownArg;
export function isKnownBindingStruct<T>(x: [Extract<T, BindingStruct>] extends [never] ? never : T): x is [Extract<T, BindingStruct>] extends [never]
  ? never
  : BindingStruct extends T // Needed when T is any
  ? BindingStruct
  : Extract<T, BindingStruct> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseBindingStruct;
}

    export function ensureKnownBindingStruct<T>(x: [Extract<T, BindingStruct>] extends [never] ? never : T): BindingStruct {
      assert(isKnownBindingStruct(x), () => mkUnexpectedTypeMsg([BindingStruct], x));
      return x;
    }
    export function ensureMaybeKnownBindingStruct<T>(x: [Extract<T, BindingStruct>] extends [never] ? never : T): BindingStruct | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownBindingStruct(x as any);
    }
    
export type BindingStruct = KnownBindingStruct;
export interface BindingStructParams {
  
}

abstract class BaseBindingStruct extends ModelInst {
  
    static isKnown(x: any): x is BindingStruct { return isKnownBindingStruct(x); }
    static getType(): BindingStruct { throw new Error(); }
    static modelTypeName = "BindingStruct";
    
  static readonly $fields = meta.allFields(meta.clsByName["BindingStruct"]);
  
  
}

export const BindingStruct = BaseBindingStruct;

type KnownRep = ClsRep;
export function isKnownRep<T>(x: [Extract<T, Rep>] extends [never] ? never : T): x is [Extract<T, Rep>] extends [never]
  ? never
  : Rep extends T // Needed when T is any
  ? Rep
  : Extract<T, Rep> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRep;
}

    export function ensureKnownRep<T>(x: [Extract<T, Rep>] extends [never] ? never : T): Rep {
      assert(isKnownRep(x), () => mkUnexpectedTypeMsg([Rep], x));
      return x;
    }
    export function ensureMaybeKnownRep<T>(x: [Extract<T, Rep>] extends [never] ? never : T): Rep | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRep(x as any);
    }
    
export type Rep = KnownRep;
export interface RepParams {
  element: Var /*  */;
index: Var | null | undefined /*  */;
collection: CustomCode | ObjectPath /*  */;
}

abstract class BaseRep extends BaseBindingStruct {
  
    static isKnown(x: any): x is Rep { return isKnownRep(x); }
    static getType(): Rep { throw new Error(); }
    static modelTypeName = "Rep";
    
  static readonly $fields = meta.allFields(meta.clsByName["Rep"]);
  static readonly $field_element: Field = ensure(
    BaseRep.$fields.find((f) => f.name === "element"),
    "Rep has no field element",
  );
static readonly $field_index: Field = ensure(
    BaseRep.$fields.find((f) => f.name === "index"),
    "Rep has no field index",
  );
static readonly $field_collection: Field = ensure(
    BaseRep.$fields.find((f) => f.name === "collection"),
    "Rep has no field collection",
  );
  get element(): Var { meta.reportRead(this, "element"); return (this as any).$v.element; }
set element(value: Var) { meta.writeField(this, BaseRep.$field_element, value); }
get index(): Var | null | undefined { meta.reportRead(this, "index"); return (this as any).$v.index; }
set index(value: Var | null | undefined) { meta.writeField(this, BaseRep.$field_index, value); }
get collection(): CustomCode | ObjectPath { meta.reportRead(this, "collection"); return (this as any).$v.collection; }
set collection(value: CustomCode | ObjectPath) { meta.writeField(this, BaseRep.$field_collection, value); }
}
class ClsRep extends BaseRep {
  constructor(args: RepParams) {
    super();
    meta.initializers.Rep(this, args);
  }
  get typeTag(): "Rep" {
        return "Rep";
      }
}
export const Rep = ClsRep;

type KnownParam = KnownSlotParam | KnownStateParam | KnownGlobalVariantGroupParam | KnownStateChangeHandlerParam | KnownPropParam;
export function isKnownParam<T>(x: [Extract<T, Param>] extends [never] ? never : T): x is [Extract<T, Param>] extends [never]
  ? never
  : Param extends T // Needed when T is any
  ? Param
  : Extract<T, Param> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseParam;
}

    export function ensureKnownParam<T>(x: [Extract<T, Param>] extends [never] ? never : T): Param {
      assert(isKnownParam(x), () => mkUnexpectedTypeMsg([Param], x));
      return x;
    }
    export function ensureMaybeKnownParam<T>(x: [Extract<T, Param>] extends [never] ? never : T): Param | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownParam(x as any);
    }
    
export type Param = KnownParam;
export interface ParamParams {
  variable: Var /*  */;
readonly uuid: string /* Const */;
enumValues: ReadonlyArray<string | boolean | number> /*  */;
origin: string | number | null | undefined /*  */;
exportType: "Internal" | "External" | "ToolsOnly" /*  */;
defaultExpr: Expr | null | undefined /*  */;
previewExpr: Expr | null | undefined /*  */;
propEffect: string | null | undefined /*  */;
description: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
about: string | null | undefined /*  */;
isRepeated: boolean | null | undefined /*  */;
isMainContentSlot: boolean /*  */;
required: boolean /*  */;
mergeWithParent: boolean /*  */;
isLocalizable: boolean /*  */;
}

abstract class BaseParam extends BaseBindingStruct {
  
    static isKnown(x: any): x is Param { return isKnownParam(x); }
    static getType(): Param { throw new Error(); }
    static modelTypeName = "Param";
    
  static readonly $fields = meta.allFields(meta.clsByName["Param"]);
  static readonly $field_variable: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "variable"),
    "Param has no field variable",
  );
static readonly $field_uuid: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "uuid"),
    "Param has no field uuid",
  );
static readonly $field_enumValues: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "enumValues"),
    "Param has no field enumValues",
  );
static readonly $field_origin: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "origin"),
    "Param has no field origin",
  );
static readonly $field_exportType: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "exportType"),
    "Param has no field exportType",
  );
static readonly $field_defaultExpr: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "defaultExpr"),
    "Param has no field defaultExpr",
  );
static readonly $field_previewExpr: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "previewExpr"),
    "Param has no field previewExpr",
  );
static readonly $field_propEffect: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "propEffect"),
    "Param has no field propEffect",
  );
static readonly $field_description: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "description"),
    "Param has no field description",
  );
static readonly $field_displayName: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "displayName"),
    "Param has no field displayName",
  );
static readonly $field_about: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "about"),
    "Param has no field about",
  );
static readonly $field_isRepeated: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "isRepeated"),
    "Param has no field isRepeated",
  );
static readonly $field_isMainContentSlot: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "isMainContentSlot"),
    "Param has no field isMainContentSlot",
  );
static readonly $field_required: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "required"),
    "Param has no field required",
  );
static readonly $field_mergeWithParent: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "mergeWithParent"),
    "Param has no field mergeWithParent",
  );
static readonly $field_isLocalizable: Field = ensure(
    BaseParam.$fields.find((f) => f.name === "isLocalizable"),
    "Param has no field isLocalizable",
  );
  get variable(): Var { meta.reportRead(this, "variable"); return (this as any).$v.variable; }
set variable(value: Var) { meta.writeField(this, BaseParam.$field_variable, value); }
/* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseParam.$field_uuid, value); }
get enumValues(): ReadonlyArray<string | boolean | number> { meta.reportRead(this, "enumValues"); return (this as any).$v.enumValues; }
set enumValues(value: ReadonlyArray<string | boolean | number>) { meta.writeField(this, BaseParam.$field_enumValues, value); }
get origin(): string | number | null | undefined { meta.reportRead(this, "origin"); return (this as any).$v.origin; }
set origin(value: string | number | null | undefined) { meta.writeField(this, BaseParam.$field_origin, value); }
get exportType(): "Internal" | "External" | "ToolsOnly" { meta.reportRead(this, "exportType"); return (this as any).$v.exportType; }
set exportType(value: "Internal" | "External" | "ToolsOnly") { meta.writeField(this, BaseParam.$field_exportType, value); }
get defaultExpr(): Expr | null | undefined { meta.reportRead(this, "defaultExpr"); return (this as any).$v.defaultExpr; }
set defaultExpr(value: Expr | null | undefined) { meta.writeField(this, BaseParam.$field_defaultExpr, value); }
get previewExpr(): Expr | null | undefined { meta.reportRead(this, "previewExpr"); return (this as any).$v.previewExpr; }
set previewExpr(value: Expr | null | undefined) { meta.writeField(this, BaseParam.$field_previewExpr, value); }
get propEffect(): string | null | undefined { meta.reportRead(this, "propEffect"); return (this as any).$v.propEffect; }
set propEffect(value: string | null | undefined) { meta.writeField(this, BaseParam.$field_propEffect, value); }
get description(): string | null | undefined { meta.reportRead(this, "description"); return (this as any).$v.description; }
set description(value: string | null | undefined) { meta.writeField(this, BaseParam.$field_description, value); }
get displayName(): string | null | undefined { meta.reportRead(this, "displayName"); return (this as any).$v.displayName; }
set displayName(value: string | null | undefined) { meta.writeField(this, BaseParam.$field_displayName, value); }
get about(): string | null | undefined { meta.reportRead(this, "about"); return (this as any).$v.about; }
set about(value: string | null | undefined) { meta.writeField(this, BaseParam.$field_about, value); }
get isRepeated(): boolean | null | undefined { meta.reportRead(this, "isRepeated"); return (this as any).$v.isRepeated; }
set isRepeated(value: boolean | null | undefined) { meta.writeField(this, BaseParam.$field_isRepeated, value); }
get isMainContentSlot(): boolean { meta.reportRead(this, "isMainContentSlot"); return (this as any).$v.isMainContentSlot; }
set isMainContentSlot(value: boolean) { meta.writeField(this, BaseParam.$field_isMainContentSlot, value); }
get required(): boolean { meta.reportRead(this, "required"); return (this as any).$v.required; }
set required(value: boolean) { meta.writeField(this, BaseParam.$field_required, value); }
get mergeWithParent(): boolean { meta.reportRead(this, "mergeWithParent"); return (this as any).$v.mergeWithParent; }
set mergeWithParent(value: boolean) { meta.writeField(this, BaseParam.$field_mergeWithParent, value); }
get isLocalizable(): boolean { meta.reportRead(this, "isLocalizable"); return (this as any).$v.isLocalizable; }
set isLocalizable(value: boolean) { meta.writeField(this, BaseParam.$field_isLocalizable, value); }
}

export const Param = BaseParam;

type KnownSlotParam = ClsSlotParam;
export function isKnownSlotParam<T>(x: [Extract<T, SlotParam>] extends [never] ? never : T): x is [Extract<T, SlotParam>] extends [never]
  ? never
  : SlotParam extends T // Needed when T is any
  ? SlotParam
  : Extract<T, SlotParam> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSlotParam;
}

    export function ensureKnownSlotParam<T>(x: [Extract<T, SlotParam>] extends [never] ? never : T): SlotParam {
      assert(isKnownSlotParam(x), () => mkUnexpectedTypeMsg([SlotParam], x));
      return x;
    }
    export function ensureMaybeKnownSlotParam<T>(x: [Extract<T, SlotParam>] extends [never] ? never : T): SlotParam | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSlotParam(x as any);
    }
    
export type SlotParam = KnownSlotParam;
export interface SlotParamParams {
  type: RenderableType | RenderFuncType /*  */;
readonly tplSlot: TplSlot /* Const,WeakRef */;
variable: Var /*  */;
readonly uuid: string /* Const */;
enumValues: ReadonlyArray<string | boolean | number> /*  */;
origin: string | number | null | undefined /*  */;
exportType: "Internal" | "External" | "ToolsOnly" /*  */;
defaultExpr: Expr | null | undefined /*  */;
previewExpr: Expr | null | undefined /*  */;
propEffect: string | null | undefined /*  */;
description: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
about: string | null | undefined /*  */;
isRepeated: boolean | null | undefined /*  */;
isMainContentSlot: boolean /*  */;
required: boolean /*  */;
mergeWithParent: boolean /*  */;
isLocalizable: boolean /*  */;
}

abstract class BaseSlotParam extends BaseParam {
  
    static isKnown(x: any): x is SlotParam { return isKnownSlotParam(x); }
    static getType(): SlotParam { throw new Error(); }
    static modelTypeName = "SlotParam";
    
  static readonly $fields = meta.allFields(meta.clsByName["SlotParam"]);
  static readonly $field_type: Field = ensure(
    BaseSlotParam.$fields.find((f) => f.name === "type"),
    "SlotParam has no field type",
  );
static readonly $field_tplSlot: Field = ensure(
    BaseSlotParam.$fields.find((f) => f.name === "tplSlot"),
    "SlotParam has no field tplSlot",
  );
  get type(): RenderableType | RenderFuncType { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: RenderableType | RenderFuncType) { meta.writeField(this, BaseSlotParam.$field_type, value); }
/* Const,WeakRef */
get tplSlot(): TplSlot { meta.reportRead(this, "tplSlot"); return (this as any).$v.tplSlot; }
private set tplSlot(value: TplSlot) { meta.writeField(this, BaseSlotParam.$field_tplSlot, value); }
}
class ClsSlotParam extends BaseSlotParam {
  constructor(args: SlotParamParams) {
    super();
    meta.initializers.SlotParam(this, args);
  }
  get typeTag(): "SlotParam" {
        return "SlotParam";
      }
}
export const SlotParam = ClsSlotParam;

type KnownStateParam = ClsStateParam;
export function isKnownStateParam<T>(x: [Extract<T, StateParam>] extends [never] ? never : T): x is [Extract<T, StateParam>] extends [never]
  ? never
  : StateParam extends T // Needed when T is any
  ? StateParam
  : Extract<T, StateParam> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStateParam;
}

    export function ensureKnownStateParam<T>(x: [Extract<T, StateParam>] extends [never] ? never : T): StateParam {
      assert(isKnownStateParam(x), () => mkUnexpectedTypeMsg([StateParam], x));
      return x;
    }
    export function ensureMaybeKnownStateParam<T>(x: [Extract<T, StateParam>] extends [never] ? never : T): StateParam | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStateParam(x as any);
    }
    
export type StateParam = KnownStateParam;
export interface StateParamParams {
  type: PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | StylePropType /*  */;
readonly state: State /* Const,WeakRef */;
variable: Var /*  */;
readonly uuid: string /* Const */;
enumValues: ReadonlyArray<string | boolean | number> /*  */;
origin: string | number | null | undefined /*  */;
exportType: "Internal" | "External" | "ToolsOnly" /*  */;
defaultExpr: Expr | null | undefined /*  */;
previewExpr: Expr | null | undefined /*  */;
propEffect: string | null | undefined /*  */;
description: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
about: string | null | undefined /*  */;
isRepeated: boolean | null | undefined /*  */;
isMainContentSlot: boolean /*  */;
required: boolean /*  */;
mergeWithParent: boolean /*  */;
isLocalizable: boolean /*  */;
}

abstract class BaseStateParam extends BaseParam {
  
    static isKnown(x: any): x is StateParam { return isKnownStateParam(x); }
    static getType(): StateParam { throw new Error(); }
    static modelTypeName = "StateParam";
    
  static readonly $fields = meta.allFields(meta.clsByName["StateParam"]);
  static readonly $field_type: Field = ensure(
    BaseStateParam.$fields.find((f) => f.name === "type"),
    "StateParam has no field type",
  );
static readonly $field_state: Field = ensure(
    BaseStateParam.$fields.find((f) => f.name === "state"),
    "StateParam has no field state",
  );
  get type(): PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | StylePropType { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | StylePropType) { meta.writeField(this, BaseStateParam.$field_type, value); }
/* Const,WeakRef */
get state(): State { meta.reportRead(this, "state"); return (this as any).$v.state; }
private set state(value: State) { meta.writeField(this, BaseStateParam.$field_state, value); }
}
class ClsStateParam extends BaseStateParam {
  constructor(args: StateParamParams) {
    super();
    meta.initializers.StateParam(this, args);
  }
  get typeTag(): "StateParam" {
        return "StateParam";
      }
}
export const StateParam = ClsStateParam;

type KnownGlobalVariantGroupParam = ClsGlobalVariantGroupParam;
export function isKnownGlobalVariantGroupParam<T>(x: [Extract<T, GlobalVariantGroupParam>] extends [never] ? never : T): x is [Extract<T, GlobalVariantGroupParam>] extends [never]
  ? never
  : GlobalVariantGroupParam extends T // Needed when T is any
  ? GlobalVariantGroupParam
  : Extract<T, GlobalVariantGroupParam> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseGlobalVariantGroupParam;
}

    export function ensureKnownGlobalVariantGroupParam<T>(x: [Extract<T, GlobalVariantGroupParam>] extends [never] ? never : T): GlobalVariantGroupParam {
      assert(isKnownGlobalVariantGroupParam(x), () => mkUnexpectedTypeMsg([GlobalVariantGroupParam], x));
      return x;
    }
    export function ensureMaybeKnownGlobalVariantGroupParam<T>(x: [Extract<T, GlobalVariantGroupParam>] extends [never] ? never : T): GlobalVariantGroupParam | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownGlobalVariantGroupParam(x as any);
    }
    
export type GlobalVariantGroupParam = KnownGlobalVariantGroupParam;
export interface GlobalVariantGroupParamParams {
  type: Text | BoolType | Choice | AnyType /*  */;
variable: Var /*  */;
readonly uuid: string /* Const */;
enumValues: ReadonlyArray<string | boolean | number> /*  */;
origin: string | number | null | undefined /*  */;
exportType: "Internal" | "External" | "ToolsOnly" /*  */;
defaultExpr: Expr | null | undefined /*  */;
previewExpr: Expr | null | undefined /*  */;
propEffect: string | null | undefined /*  */;
description: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
about: string | null | undefined /*  */;
isRepeated: boolean | null | undefined /*  */;
isMainContentSlot: boolean /*  */;
required: boolean /*  */;
mergeWithParent: boolean /*  */;
isLocalizable: boolean /*  */;
}

abstract class BaseGlobalVariantGroupParam extends BaseParam {
  
    static isKnown(x: any): x is GlobalVariantGroupParam { return isKnownGlobalVariantGroupParam(x); }
    static getType(): GlobalVariantGroupParam { throw new Error(); }
    static modelTypeName = "GlobalVariantGroupParam";
    
  static readonly $fields = meta.allFields(meta.clsByName["GlobalVariantGroupParam"]);
  static readonly $field_type: Field = ensure(
    BaseGlobalVariantGroupParam.$fields.find((f) => f.name === "type"),
    "GlobalVariantGroupParam has no field type",
  );
  get type(): Text | BoolType | Choice | AnyType { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: Text | BoolType | Choice | AnyType) { meta.writeField(this, BaseGlobalVariantGroupParam.$field_type, value); }
}
class ClsGlobalVariantGroupParam extends BaseGlobalVariantGroupParam {
  constructor(args: GlobalVariantGroupParamParams) {
    super();
    meta.initializers.GlobalVariantGroupParam(this, args);
  }
  get typeTag(): "GlobalVariantGroupParam" {
        return "GlobalVariantGroupParam";
      }
}
export const GlobalVariantGroupParam = ClsGlobalVariantGroupParam;

type KnownStateChangeHandlerParam = ClsStateChangeHandlerParam;
export function isKnownStateChangeHandlerParam<T>(x: [Extract<T, StateChangeHandlerParam>] extends [never] ? never : T): x is [Extract<T, StateChangeHandlerParam>] extends [never]
  ? never
  : StateChangeHandlerParam extends T // Needed when T is any
  ? StateChangeHandlerParam
  : Extract<T, StateChangeHandlerParam> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStateChangeHandlerParam;
}

    export function ensureKnownStateChangeHandlerParam<T>(x: [Extract<T, StateChangeHandlerParam>] extends [never] ? never : T): StateChangeHandlerParam {
      assert(isKnownStateChangeHandlerParam(x), () => mkUnexpectedTypeMsg([StateChangeHandlerParam], x));
      return x;
    }
    export function ensureMaybeKnownStateChangeHandlerParam<T>(x: [Extract<T, StateChangeHandlerParam>] extends [never] ? never : T): StateChangeHandlerParam | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStateChangeHandlerParam(x as any);
    }
    
export type StateChangeHandlerParam = KnownStateChangeHandlerParam;
export interface StateChangeHandlerParamParams {
  type: FunctionType /*  */;
readonly state: State /* Const,WeakRef */;
variable: Var /*  */;
readonly uuid: string /* Const */;
enumValues: ReadonlyArray<string | boolean | number> /*  */;
origin: string | number | null | undefined /*  */;
exportType: "Internal" | "External" | "ToolsOnly" /*  */;
defaultExpr: Expr | null | undefined /*  */;
previewExpr: Expr | null | undefined /*  */;
propEffect: string | null | undefined /*  */;
description: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
about: string | null | undefined /*  */;
isRepeated: boolean | null | undefined /*  */;
isMainContentSlot: boolean /*  */;
required: boolean /*  */;
mergeWithParent: boolean /*  */;
isLocalizable: boolean /*  */;
}

abstract class BaseStateChangeHandlerParam extends BaseParam {
  
    static isKnown(x: any): x is StateChangeHandlerParam { return isKnownStateChangeHandlerParam(x); }
    static getType(): StateChangeHandlerParam { throw new Error(); }
    static modelTypeName = "StateChangeHandlerParam";
    
  static readonly $fields = meta.allFields(meta.clsByName["StateChangeHandlerParam"]);
  static readonly $field_type: Field = ensure(
    BaseStateChangeHandlerParam.$fields.find((f) => f.name === "type"),
    "StateChangeHandlerParam has no field type",
  );
static readonly $field_state: Field = ensure(
    BaseStateChangeHandlerParam.$fields.find((f) => f.name === "state"),
    "StateChangeHandlerParam has no field state",
  );
  get type(): FunctionType { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: FunctionType) { meta.writeField(this, BaseStateChangeHandlerParam.$field_type, value); }
/* Const,WeakRef */
get state(): State { meta.reportRead(this, "state"); return (this as any).$v.state; }
private set state(value: State) { meta.writeField(this, BaseStateChangeHandlerParam.$field_state, value); }
}
class ClsStateChangeHandlerParam extends BaseStateChangeHandlerParam {
  constructor(args: StateChangeHandlerParamParams) {
    super();
    meta.initializers.StateChangeHandlerParam(this, args);
  }
  get typeTag(): "StateChangeHandlerParam" {
        return "StateChangeHandlerParam";
      }
}
export const StateChangeHandlerParam = ClsStateChangeHandlerParam;

type KnownPropParam = ClsPropParam;
export function isKnownPropParam<T>(x: [Extract<T, PropParam>] extends [never] ? never : T): x is [Extract<T, PropParam>] extends [never]
  ? never
  : PropParam extends T // Needed when T is any
  ? PropParam
  : Extract<T, PropParam> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePropParam;
}

    export function ensureKnownPropParam<T>(x: [Extract<T, PropParam>] extends [never] ? never : T): PropParam {
      assert(isKnownPropParam(x), () => mkUnexpectedTypeMsg([PropParam], x));
      return x;
    }
    export function ensureMaybeKnownPropParam<T>(x: [Extract<T, PropParam>] extends [never] ? never : T): PropParam | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPropParam(x as any);
    }
    
export type PropParam = KnownPropParam;
export interface PropParamParams {
  type: PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | FunctionType | StylePropType | ColorPropType /*  */;
advanced: boolean /*  */;
variable: Var /*  */;
readonly uuid: string /* Const */;
enumValues: ReadonlyArray<string | boolean | number> /*  */;
origin: string | number | null | undefined /*  */;
exportType: "Internal" | "External" | "ToolsOnly" /*  */;
defaultExpr: Expr | null | undefined /*  */;
previewExpr: Expr | null | undefined /*  */;
propEffect: string | null | undefined /*  */;
description: string | null | undefined /*  */;
displayName: string | null | undefined /*  */;
about: string | null | undefined /*  */;
isRepeated: boolean | null | undefined /*  */;
isMainContentSlot: boolean /*  */;
required: boolean /*  */;
mergeWithParent: boolean /*  */;
isLocalizable: boolean /*  */;
}

abstract class BasePropParam extends BaseParam {
  
    static isKnown(x: any): x is PropParam { return isKnownPropParam(x); }
    static getType(): PropParam { throw new Error(); }
    static modelTypeName = "PropParam";
    
  static readonly $fields = meta.allFields(meta.clsByName["PropParam"]);
  static readonly $field_type: Field = ensure(
    BasePropParam.$fields.find((f) => f.name === "type"),
    "PropParam has no field type",
  );
static readonly $field_advanced: Field = ensure(
    BasePropParam.$fields.find((f) => f.name === "advanced"),
    "PropParam has no field advanced",
  );
  get type(): PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | FunctionType | StylePropType | ColorPropType { meta.reportRead(this, "type"); return (this as any).$v.type; }
set type(value: PrimitiveType | Img | HrefType | TargetType | DateString | DateRangeStrings | QueryData | FunctionType | StylePropType | ColorPropType) { meta.writeField(this, BasePropParam.$field_type, value); }
get advanced(): boolean { meta.reportRead(this, "advanced"); return (this as any).$v.advanced; }
set advanced(value: boolean) { meta.writeField(this, BasePropParam.$field_advanced, value); }
}
class ClsPropParam extends BasePropParam {
  constructor(args: PropParamParams) {
    super();
    meta.initializers.PropParam(this, args);
  }
  get typeTag(): "PropParam" {
        return "PropParam";
      }
}
export const PropParam = ClsPropParam;

type KnownArg = ClsArg;
export function isKnownArg<T>(x: [Extract<T, Arg>] extends [never] ? never : T): x is [Extract<T, Arg>] extends [never]
  ? never
  : Arg extends T // Needed when T is any
  ? Arg
  : Extract<T, Arg> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseArg;
}

    export function ensureKnownArg<T>(x: [Extract<T, Arg>] extends [never] ? never : T): Arg {
      assert(isKnownArg(x), () => mkUnexpectedTypeMsg([Arg], x));
      return x;
    }
    export function ensureMaybeKnownArg<T>(x: [Extract<T, Arg>] extends [never] ? never : T): Arg | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownArg(x as any);
    }
    
export type Arg = KnownArg;
export interface ArgParams {
  param: Param /* WeakRef */;
expr: Expr /*  */;
}

abstract class BaseArg extends BaseBindingStruct {
  
    static isKnown(x: any): x is Arg { return isKnownArg(x); }
    static getType(): Arg { throw new Error(); }
    static modelTypeName = "Arg";
    
  static readonly $fields = meta.allFields(meta.clsByName["Arg"]);
  static readonly $field_param: Field = ensure(
    BaseArg.$fields.find((f) => f.name === "param"),
    "Arg has no field param",
  );
static readonly $field_expr: Field = ensure(
    BaseArg.$fields.find((f) => f.name === "expr"),
    "Arg has no field expr",
  );
  /* WeakRef */
get param(): Param { meta.reportRead(this, "param"); return (this as any).$v.param; }
set param(value: Param) { meta.writeField(this, BaseArg.$field_param, value); }
get expr(): Expr { meta.reportRead(this, "expr"); return (this as any).$v.expr; }
set expr(value: Expr) { meta.writeField(this, BaseArg.$field_expr, value); }
}
class ClsArg extends BaseArg {
  constructor(args: ArgParams) {
    super();
    meta.initializers.Arg(this, args);
  }
  get typeTag(): "Arg" {
        return "Arg";
      }
}
export const Arg = ClsArg;

type KnownExpr = KnownRenderExpr | KnownCustomCode | KnownDataSourceOpExpr | KnownCustomFunctionExpr | KnownVarRef | KnownTplRef | KnownStyleTokenRef | KnownImageAssetRef | KnownPageHref | KnownVariantsRef | KnownObjectPath | KnownEventHandler | KnownFunctionArg | KnownCollectionExpr | KnownMapExpr | KnownStyleExpr | KnownTemplatedString | KnownFunctionExpr | KnownQueryInvalidationExpr | KnownCompositeExpr;
export function isKnownExpr<T>(x: [Extract<T, Expr>] extends [never] ? never : T): x is [Extract<T, Expr>] extends [never]
  ? never
  : Expr extends T // Needed when T is any
  ? Expr
  : Extract<T, Expr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseExpr;
}

    export function ensureKnownExpr<T>(x: [Extract<T, Expr>] extends [never] ? never : T): Expr {
      assert(isKnownExpr(x), () => mkUnexpectedTypeMsg([Expr], x));
      return x;
    }
    export function ensureMaybeKnownExpr<T>(x: [Extract<T, Expr>] extends [never] ? never : T): Expr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownExpr(x as any);
    }
    
export type Expr = KnownExpr;
export interface ExprParams {
  
}

abstract class BaseExpr extends ModelInst {
  
    static isKnown(x: any): x is Expr { return isKnownExpr(x); }
    static getType(): Expr { throw new Error(); }
    static modelTypeName = "Expr";
    
  static readonly $fields = meta.allFields(meta.clsByName["Expr"]);
  
  
}

export const Expr = BaseExpr;

type KnownRenderExpr = KnownVirtualRenderExpr | ClsRenderExpr;
export function isKnownRenderExpr<T>(x: [Extract<T, RenderExpr>] extends [never] ? never : T): x is [Extract<T, RenderExpr>] extends [never]
  ? never
  : RenderExpr extends T // Needed when T is any
  ? RenderExpr
  : Extract<T, RenderExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRenderExpr;
}

    export function ensureKnownRenderExpr<T>(x: [Extract<T, RenderExpr>] extends [never] ? never : T): RenderExpr {
      assert(isKnownRenderExpr(x), () => mkUnexpectedTypeMsg([RenderExpr], x));
      return x;
    }
    export function ensureMaybeKnownRenderExpr<T>(x: [Extract<T, RenderExpr>] extends [never] ? never : T): RenderExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRenderExpr(x as any);
    }
    
export type RenderExpr = KnownRenderExpr;
export interface RenderExprParams {
  tpl: ReadonlyArray<TplNode> /*  */;
}

abstract class BaseRenderExpr extends BaseExpr {
  
    static isKnown(x: any): x is RenderExpr { return isKnownRenderExpr(x); }
    static getType(): RenderExpr { throw new Error(); }
    static modelTypeName = "RenderExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["RenderExpr"]);
  static readonly $field_tpl: Field = ensure(
    BaseRenderExpr.$fields.find((f) => f.name === "tpl"),
    "RenderExpr has no field tpl",
  );
  get tpl(): ReadonlyArray<TplNode> { meta.reportRead(this, "tpl"); return (this as any).$v.tpl; }
set tpl(value: ReadonlyArray<TplNode>) { meta.writeField(this, BaseRenderExpr.$field_tpl, value); }
}
class ClsRenderExpr extends BaseRenderExpr {
  constructor(args: RenderExprParams) {
    super();
    meta.initializers.RenderExpr(this, args);
  }
  get typeTag(): "RenderExpr" {
        return "RenderExpr";
      }
}
export const RenderExpr = ClsRenderExpr;

type KnownVirtualRenderExpr = ClsVirtualRenderExpr;
export function isKnownVirtualRenderExpr<T>(x: [Extract<T, VirtualRenderExpr>] extends [never] ? never : T): x is [Extract<T, VirtualRenderExpr>] extends [never]
  ? never
  : VirtualRenderExpr extends T // Needed when T is any
  ? VirtualRenderExpr
  : Extract<T, VirtualRenderExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVirtualRenderExpr;
}

    export function ensureKnownVirtualRenderExpr<T>(x: [Extract<T, VirtualRenderExpr>] extends [never] ? never : T): VirtualRenderExpr {
      assert(isKnownVirtualRenderExpr(x), () => mkUnexpectedTypeMsg([VirtualRenderExpr], x));
      return x;
    }
    export function ensureMaybeKnownVirtualRenderExpr<T>(x: [Extract<T, VirtualRenderExpr>] extends [never] ? never : T): VirtualRenderExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVirtualRenderExpr(x as any);
    }
    
export type VirtualRenderExpr = KnownVirtualRenderExpr;
export interface VirtualRenderExprParams {
  tpl: ReadonlyArray<TplNode> /*  */;
}

abstract class BaseVirtualRenderExpr extends BaseRenderExpr {
  
    static isKnown(x: any): x is VirtualRenderExpr { return isKnownVirtualRenderExpr(x); }
    static getType(): VirtualRenderExpr { throw new Error(); }
    static modelTypeName = "VirtualRenderExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["VirtualRenderExpr"]);
  
  
}
class ClsVirtualRenderExpr extends BaseVirtualRenderExpr {
  constructor(args: VirtualRenderExprParams) {
    super();
    meta.initializers.VirtualRenderExpr(this, args);
  }
  get typeTag(): "VirtualRenderExpr" {
        return "VirtualRenderExpr";
      }
}
export const VirtualRenderExpr = ClsVirtualRenderExpr;

type KnownCustomCode = ClsCustomCode;
export function isKnownCustomCode<T>(x: [Extract<T, CustomCode>] extends [never] ? never : T): x is [Extract<T, CustomCode>] extends [never]
  ? never
  : CustomCode extends T // Needed when T is any
  ? CustomCode
  : Extract<T, CustomCode> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCustomCode;
}

    export function ensureKnownCustomCode<T>(x: [Extract<T, CustomCode>] extends [never] ? never : T): CustomCode {
      assert(isKnownCustomCode(x), () => mkUnexpectedTypeMsg([CustomCode], x));
      return x;
    }
    export function ensureMaybeKnownCustomCode<T>(x: [Extract<T, CustomCode>] extends [never] ? never : T): CustomCode | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCustomCode(x as any);
    }
    
export type CustomCode = KnownCustomCode;
export interface CustomCodeParams {
  code: string /*  */;
fallback: Expr | null | undefined /*  */;
}

abstract class BaseCustomCode extends BaseExpr {
  
    static isKnown(x: any): x is CustomCode { return isKnownCustomCode(x); }
    static getType(): CustomCode { throw new Error(); }
    static modelTypeName = "CustomCode";
    
  static readonly $fields = meta.allFields(meta.clsByName["CustomCode"]);
  static readonly $field_code: Field = ensure(
    BaseCustomCode.$fields.find((f) => f.name === "code"),
    "CustomCode has no field code",
  );
static readonly $field_fallback: Field = ensure(
    BaseCustomCode.$fields.find((f) => f.name === "fallback"),
    "CustomCode has no field fallback",
  );
  get code(): string { meta.reportRead(this, "code"); return (this as any).$v.code; }
set code(value: string) { meta.writeField(this, BaseCustomCode.$field_code, value); }
get fallback(): Expr | null | undefined { meta.reportRead(this, "fallback"); return (this as any).$v.fallback; }
set fallback(value: Expr | null | undefined) { meta.writeField(this, BaseCustomCode.$field_fallback, value); }
}
class ClsCustomCode extends BaseCustomCode {
  constructor(args: CustomCodeParams) {
    super();
    meta.initializers.CustomCode(this, args);
  }
  get typeTag(): "CustomCode" {
        return "CustomCode";
      }
}
export const CustomCode = ClsCustomCode;

type KnownDataSourceOpExpr = ClsDataSourceOpExpr;
export function isKnownDataSourceOpExpr<T>(x: [Extract<T, DataSourceOpExpr>] extends [never] ? never : T): x is [Extract<T, DataSourceOpExpr>] extends [never]
  ? never
  : DataSourceOpExpr extends T // Needed when T is any
  ? DataSourceOpExpr
  : Extract<T, DataSourceOpExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDataSourceOpExpr;
}

    export function ensureKnownDataSourceOpExpr<T>(x: [Extract<T, DataSourceOpExpr>] extends [never] ? never : T): DataSourceOpExpr {
      assert(isKnownDataSourceOpExpr(x), () => mkUnexpectedTypeMsg([DataSourceOpExpr], x));
      return x;
    }
    export function ensureMaybeKnownDataSourceOpExpr<T>(x: [Extract<T, DataSourceOpExpr>] extends [never] ? never : T): DataSourceOpExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDataSourceOpExpr(x as any);
    }
    
export type DataSourceOpExpr = KnownDataSourceOpExpr;
export interface DataSourceOpExprParams {
  parent: QueryRef | null | undefined /*  */;
sourceId: string /*  */;
opId: string /*  */;
opName: string /*  */;
templates: Readonly<Record<string, DataSourceTemplate>> /*  */;
cacheKey: TemplatedString | null | undefined /*  */;
queryInvalidation: QueryInvalidationExpr | null | undefined /*  */;
roleId: string | null | undefined /*  */;
}

abstract class BaseDataSourceOpExpr extends BaseExpr {
  
    static isKnown(x: any): x is DataSourceOpExpr { return isKnownDataSourceOpExpr(x); }
    static getType(): DataSourceOpExpr { throw new Error(); }
    static modelTypeName = "DataSourceOpExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["DataSourceOpExpr"]);
  static readonly $field_parent: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "parent"),
    "DataSourceOpExpr has no field parent",
  );
static readonly $field_sourceId: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "sourceId"),
    "DataSourceOpExpr has no field sourceId",
  );
static readonly $field_opId: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "opId"),
    "DataSourceOpExpr has no field opId",
  );
static readonly $field_opName: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "opName"),
    "DataSourceOpExpr has no field opName",
  );
static readonly $field_templates: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "templates"),
    "DataSourceOpExpr has no field templates",
  );
static readonly $field_cacheKey: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "cacheKey"),
    "DataSourceOpExpr has no field cacheKey",
  );
static readonly $field_queryInvalidation: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "queryInvalidation"),
    "DataSourceOpExpr has no field queryInvalidation",
  );
static readonly $field_roleId: Field = ensure(
    BaseDataSourceOpExpr.$fields.find((f) => f.name === "roleId"),
    "DataSourceOpExpr has no field roleId",
  );
  get parent(): QueryRef | null | undefined { meta.reportRead(this, "parent"); return (this as any).$v.parent; }
set parent(value: QueryRef | null | undefined) { meta.writeField(this, BaseDataSourceOpExpr.$field_parent, value); }
get sourceId(): string { meta.reportRead(this, "sourceId"); return (this as any).$v.sourceId; }
set sourceId(value: string) { meta.writeField(this, BaseDataSourceOpExpr.$field_sourceId, value); }
get opId(): string { meta.reportRead(this, "opId"); return (this as any).$v.opId; }
set opId(value: string) { meta.writeField(this, BaseDataSourceOpExpr.$field_opId, value); }
get opName(): string { meta.reportRead(this, "opName"); return (this as any).$v.opName; }
set opName(value: string) { meta.writeField(this, BaseDataSourceOpExpr.$field_opName, value); }
get templates(): Readonly<Record<string, DataSourceTemplate>> { meta.reportRead(this, "templates"); return (this as any).$v.templates; }
set templates(value: Readonly<Record<string, DataSourceTemplate>>) { meta.writeField(this, BaseDataSourceOpExpr.$field_templates, value); }
get cacheKey(): TemplatedString | null | undefined { meta.reportRead(this, "cacheKey"); return (this as any).$v.cacheKey; }
set cacheKey(value: TemplatedString | null | undefined) { meta.writeField(this, BaseDataSourceOpExpr.$field_cacheKey, value); }
get queryInvalidation(): QueryInvalidationExpr | null | undefined { meta.reportRead(this, "queryInvalidation"); return (this as any).$v.queryInvalidation; }
set queryInvalidation(value: QueryInvalidationExpr | null | undefined) { meta.writeField(this, BaseDataSourceOpExpr.$field_queryInvalidation, value); }
get roleId(): string | null | undefined { meta.reportRead(this, "roleId"); return (this as any).$v.roleId; }
set roleId(value: string | null | undefined) { meta.writeField(this, BaseDataSourceOpExpr.$field_roleId, value); }
}
class ClsDataSourceOpExpr extends BaseDataSourceOpExpr {
  constructor(args: DataSourceOpExprParams) {
    super();
    meta.initializers.DataSourceOpExpr(this, args);
  }
  get typeTag(): "DataSourceOpExpr" {
        return "DataSourceOpExpr";
      }
}
export const DataSourceOpExpr = ClsDataSourceOpExpr;

type KnownCustomFunctionExpr = ClsCustomFunctionExpr;
export function isKnownCustomFunctionExpr<T>(x: [Extract<T, CustomFunctionExpr>] extends [never] ? never : T): x is [Extract<T, CustomFunctionExpr>] extends [never]
  ? never
  : CustomFunctionExpr extends T // Needed when T is any
  ? CustomFunctionExpr
  : Extract<T, CustomFunctionExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCustomFunctionExpr;
}

    export function ensureKnownCustomFunctionExpr<T>(x: [Extract<T, CustomFunctionExpr>] extends [never] ? never : T): CustomFunctionExpr {
      assert(isKnownCustomFunctionExpr(x), () => mkUnexpectedTypeMsg([CustomFunctionExpr], x));
      return x;
    }
    export function ensureMaybeKnownCustomFunctionExpr<T>(x: [Extract<T, CustomFunctionExpr>] extends [never] ? never : T): CustomFunctionExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCustomFunctionExpr(x as any);
    }
    
export type CustomFunctionExpr = KnownCustomFunctionExpr;
export interface CustomFunctionExprParams {
  func: CustomFunction /* WeakRef */;
args: ReadonlyArray<FunctionArg> /*  */;
}

abstract class BaseCustomFunctionExpr extends BaseExpr {
  
    static isKnown(x: any): x is CustomFunctionExpr { return isKnownCustomFunctionExpr(x); }
    static getType(): CustomFunctionExpr { throw new Error(); }
    static modelTypeName = "CustomFunctionExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["CustomFunctionExpr"]);
  static readonly $field_func: Field = ensure(
    BaseCustomFunctionExpr.$fields.find((f) => f.name === "func"),
    "CustomFunctionExpr has no field func",
  );
static readonly $field_args: Field = ensure(
    BaseCustomFunctionExpr.$fields.find((f) => f.name === "args"),
    "CustomFunctionExpr has no field args",
  );
  /* WeakRef */
get func(): CustomFunction { meta.reportRead(this, "func"); return (this as any).$v.func; }
set func(value: CustomFunction) { meta.writeField(this, BaseCustomFunctionExpr.$field_func, value); }
get args(): ReadonlyArray<FunctionArg> { meta.reportRead(this, "args"); return (this as any).$v.args; }
set args(value: ReadonlyArray<FunctionArg>) { meta.writeField(this, BaseCustomFunctionExpr.$field_args, value); }
}
class ClsCustomFunctionExpr extends BaseCustomFunctionExpr {
  constructor(args: CustomFunctionExprParams) {
    super();
    meta.initializers.CustomFunctionExpr(this, args);
  }
  get typeTag(): "CustomFunctionExpr" {
        return "CustomFunctionExpr";
      }
}
export const CustomFunctionExpr = ClsCustomFunctionExpr;

type KnownVarRef = ClsVarRef;
export function isKnownVarRef<T>(x: [Extract<T, VarRef>] extends [never] ? never : T): x is [Extract<T, VarRef>] extends [never]
  ? never
  : VarRef extends T // Needed when T is any
  ? VarRef
  : Extract<T, VarRef> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVarRef;
}

    export function ensureKnownVarRef<T>(x: [Extract<T, VarRef>] extends [never] ? never : T): VarRef {
      assert(isKnownVarRef(x), () => mkUnexpectedTypeMsg([VarRef], x));
      return x;
    }
    export function ensureMaybeKnownVarRef<T>(x: [Extract<T, VarRef>] extends [never] ? never : T): VarRef | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVarRef(x as any);
    }
    
export type VarRef = KnownVarRef;
export interface VarRefParams {
  variable: Var /* WeakRef */;
}

abstract class BaseVarRef extends BaseExpr {
  
    static isKnown(x: any): x is VarRef { return isKnownVarRef(x); }
    static getType(): VarRef { throw new Error(); }
    static modelTypeName = "VarRef";
    
  static readonly $fields = meta.allFields(meta.clsByName["VarRef"]);
  static readonly $field_variable: Field = ensure(
    BaseVarRef.$fields.find((f) => f.name === "variable"),
    "VarRef has no field variable",
  );
  /* WeakRef */
get variable(): Var { meta.reportRead(this, "variable"); return (this as any).$v.variable; }
set variable(value: Var) { meta.writeField(this, BaseVarRef.$field_variable, value); }
}
class ClsVarRef extends BaseVarRef {
  constructor(args: VarRefParams) {
    super();
    meta.initializers.VarRef(this, args);
  }
  get typeTag(): "VarRef" {
        return "VarRef";
      }
}
export const VarRef = ClsVarRef;

type KnownTplRef = ClsTplRef;
export function isKnownTplRef<T>(x: [Extract<T, TplRef>] extends [never] ? never : T): x is [Extract<T, TplRef>] extends [never]
  ? never
  : TplRef extends T // Needed when T is any
  ? TplRef
  : Extract<T, TplRef> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTplRef;
}

    export function ensureKnownTplRef<T>(x: [Extract<T, TplRef>] extends [never] ? never : T): TplRef {
      assert(isKnownTplRef(x), () => mkUnexpectedTypeMsg([TplRef], x));
      return x;
    }
    export function ensureMaybeKnownTplRef<T>(x: [Extract<T, TplRef>] extends [never] ? never : T): TplRef | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTplRef(x as any);
    }
    
export type TplRef = KnownTplRef;
export interface TplRefParams {
  tpl: TplNode /* WeakRef */;
}

abstract class BaseTplRef extends BaseExpr {
  
    static isKnown(x: any): x is TplRef { return isKnownTplRef(x); }
    static getType(): TplRef { throw new Error(); }
    static modelTypeName = "TplRef";
    
  static readonly $fields = meta.allFields(meta.clsByName["TplRef"]);
  static readonly $field_tpl: Field = ensure(
    BaseTplRef.$fields.find((f) => f.name === "tpl"),
    "TplRef has no field tpl",
  );
  /* WeakRef */
get tpl(): TplNode { meta.reportRead(this, "tpl"); return (this as any).$v.tpl; }
set tpl(value: TplNode) { meta.writeField(this, BaseTplRef.$field_tpl, value); }
}
class ClsTplRef extends BaseTplRef {
  constructor(args: TplRefParams) {
    super();
    meta.initializers.TplRef(this, args);
  }
  get typeTag(): "TplRef" {
        return "TplRef";
      }
}
export const TplRef = ClsTplRef;

type KnownStyleTokenRef = ClsStyleTokenRef;
export function isKnownStyleTokenRef<T>(x: [Extract<T, StyleTokenRef>] extends [never] ? never : T): x is [Extract<T, StyleTokenRef>] extends [never]
  ? never
  : StyleTokenRef extends T // Needed when T is any
  ? StyleTokenRef
  : Extract<T, StyleTokenRef> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleTokenRef;
}

    export function ensureKnownStyleTokenRef<T>(x: [Extract<T, StyleTokenRef>] extends [never] ? never : T): StyleTokenRef {
      assert(isKnownStyleTokenRef(x), () => mkUnexpectedTypeMsg([StyleTokenRef], x));
      return x;
    }
    export function ensureMaybeKnownStyleTokenRef<T>(x: [Extract<T, StyleTokenRef>] extends [never] ? never : T): StyleTokenRef | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleTokenRef(x as any);
    }
    
export type StyleTokenRef = KnownStyleTokenRef;
export interface StyleTokenRefParams {
  token: StyleToken /* WeakRef */;
}

abstract class BaseStyleTokenRef extends BaseExpr {
  
    static isKnown(x: any): x is StyleTokenRef { return isKnownStyleTokenRef(x); }
    static getType(): StyleTokenRef { throw new Error(); }
    static modelTypeName = "StyleTokenRef";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleTokenRef"]);
  static readonly $field_token: Field = ensure(
    BaseStyleTokenRef.$fields.find((f) => f.name === "token"),
    "StyleTokenRef has no field token",
  );
  /* WeakRef */
get token(): StyleToken { meta.reportRead(this, "token"); return (this as any).$v.token; }
set token(value: StyleToken) { meta.writeField(this, BaseStyleTokenRef.$field_token, value); }
}
class ClsStyleTokenRef extends BaseStyleTokenRef {
  constructor(args: StyleTokenRefParams) {
    super();
    meta.initializers.StyleTokenRef(this, args);
  }
  get typeTag(): "StyleTokenRef" {
        return "StyleTokenRef";
      }
}
export const StyleTokenRef = ClsStyleTokenRef;

type KnownImageAssetRef = ClsImageAssetRef;
export function isKnownImageAssetRef<T>(x: [Extract<T, ImageAssetRef>] extends [never] ? never : T): x is [Extract<T, ImageAssetRef>] extends [never]
  ? never
  : ImageAssetRef extends T // Needed when T is any
  ? ImageAssetRef
  : Extract<T, ImageAssetRef> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseImageAssetRef;
}

    export function ensureKnownImageAssetRef<T>(x: [Extract<T, ImageAssetRef>] extends [never] ? never : T): ImageAssetRef {
      assert(isKnownImageAssetRef(x), () => mkUnexpectedTypeMsg([ImageAssetRef], x));
      return x;
    }
    export function ensureMaybeKnownImageAssetRef<T>(x: [Extract<T, ImageAssetRef>] extends [never] ? never : T): ImageAssetRef | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownImageAssetRef(x as any);
    }
    
export type ImageAssetRef = KnownImageAssetRef;
export interface ImageAssetRefParams {
  asset: ImageAsset /* WeakRef */;
}

abstract class BaseImageAssetRef extends BaseExpr {
  
    static isKnown(x: any): x is ImageAssetRef { return isKnownImageAssetRef(x); }
    static getType(): ImageAssetRef { throw new Error(); }
    static modelTypeName = "ImageAssetRef";
    
  static readonly $fields = meta.allFields(meta.clsByName["ImageAssetRef"]);
  static readonly $field_asset: Field = ensure(
    BaseImageAssetRef.$fields.find((f) => f.name === "asset"),
    "ImageAssetRef has no field asset",
  );
  /* WeakRef */
get asset(): ImageAsset { meta.reportRead(this, "asset"); return (this as any).$v.asset; }
set asset(value: ImageAsset) { meta.writeField(this, BaseImageAssetRef.$field_asset, value); }
}
class ClsImageAssetRef extends BaseImageAssetRef {
  constructor(args: ImageAssetRefParams) {
    super();
    meta.initializers.ImageAssetRef(this, args);
  }
  get typeTag(): "ImageAssetRef" {
        return "ImageAssetRef";
      }
}
export const ImageAssetRef = ClsImageAssetRef;

type KnownPageHref = ClsPageHref;
export function isKnownPageHref<T>(x: [Extract<T, PageHref>] extends [never] ? never : T): x is [Extract<T, PageHref>] extends [never]
  ? never
  : PageHref extends T // Needed when T is any
  ? PageHref
  : Extract<T, PageHref> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BasePageHref;
}

    export function ensureKnownPageHref<T>(x: [Extract<T, PageHref>] extends [never] ? never : T): PageHref {
      assert(isKnownPageHref(x), () => mkUnexpectedTypeMsg([PageHref], x));
      return x;
    }
    export function ensureMaybeKnownPageHref<T>(x: [Extract<T, PageHref>] extends [never] ? never : T): PageHref | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownPageHref(x as any);
    }
    
export type PageHref = KnownPageHref;
export interface PageHrefParams {
  page: Component /* WeakRef */;
params: Readonly<Record<string, TemplatedString | CustomCode | ObjectPath | VarRef>> /*  */;
query: Readonly<Record<string, TemplatedString | CustomCode | ObjectPath | VarRef>> /*  */;
fragment: TemplatedString | null | undefined | CustomCode | null | undefined | ObjectPath | null | undefined | VarRef | null | undefined /*  */;
encode: boolean /*  */;
}

abstract class BasePageHref extends BaseExpr {
  
    static isKnown(x: any): x is PageHref { return isKnownPageHref(x); }
    static getType(): PageHref { throw new Error(); }
    static modelTypeName = "PageHref";
    
  static readonly $fields = meta.allFields(meta.clsByName["PageHref"]);
  static readonly $field_page: Field = ensure(
    BasePageHref.$fields.find((f) => f.name === "page"),
    "PageHref has no field page",
  );
static readonly $field_params: Field = ensure(
    BasePageHref.$fields.find((f) => f.name === "params"),
    "PageHref has no field params",
  );
static readonly $field_query: Field = ensure(
    BasePageHref.$fields.find((f) => f.name === "query"),
    "PageHref has no field query",
  );
static readonly $field_fragment: Field = ensure(
    BasePageHref.$fields.find((f) => f.name === "fragment"),
    "PageHref has no field fragment",
  );
static readonly $field_encode: Field = ensure(
    BasePageHref.$fields.find((f) => f.name === "encode"),
    "PageHref has no field encode",
  );
  /* WeakRef */
get page(): Component { meta.reportRead(this, "page"); return (this as any).$v.page; }
set page(value: Component) { meta.writeField(this, BasePageHref.$field_page, value); }
get params(): Readonly<Record<string, TemplatedString | CustomCode | ObjectPath | VarRef>> { meta.reportRead(this, "params"); return (this as any).$v.params; }
set params(value: Readonly<Record<string, TemplatedString | CustomCode | ObjectPath | VarRef>>) { meta.writeField(this, BasePageHref.$field_params, value); }
get query(): Readonly<Record<string, TemplatedString | CustomCode | ObjectPath | VarRef>> { meta.reportRead(this, "query"); return (this as any).$v.query; }
set query(value: Readonly<Record<string, TemplatedString | CustomCode | ObjectPath | VarRef>>) { meta.writeField(this, BasePageHref.$field_query, value); }
get fragment(): TemplatedString | null | undefined | CustomCode | null | undefined | ObjectPath | null | undefined | VarRef | null | undefined { meta.reportRead(this, "fragment"); return (this as any).$v.fragment; }
set fragment(value: TemplatedString | null | undefined | CustomCode | null | undefined | ObjectPath | null | undefined | VarRef | null | undefined) { meta.writeField(this, BasePageHref.$field_fragment, value); }
get encode(): boolean { meta.reportRead(this, "encode"); return (this as any).$v.encode; }
set encode(value: boolean) { meta.writeField(this, BasePageHref.$field_encode, value); }
}
class ClsPageHref extends BasePageHref {
  constructor(args: PageHrefParams) {
    super();
    meta.initializers.PageHref(this, args);
  }
  get typeTag(): "PageHref" {
        return "PageHref";
      }
}
export const PageHref = ClsPageHref;

type KnownVariantsRef = ClsVariantsRef;
export function isKnownVariantsRef<T>(x: [Extract<T, VariantsRef>] extends [never] ? never : T): x is [Extract<T, VariantsRef>] extends [never]
  ? never
  : VariantsRef extends T // Needed when T is any
  ? VariantsRef
  : Extract<T, VariantsRef> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariantsRef;
}

    export function ensureKnownVariantsRef<T>(x: [Extract<T, VariantsRef>] extends [never] ? never : T): VariantsRef {
      assert(isKnownVariantsRef(x), () => mkUnexpectedTypeMsg([VariantsRef], x));
      return x;
    }
    export function ensureMaybeKnownVariantsRef<T>(x: [Extract<T, VariantsRef>] extends [never] ? never : T): VariantsRef | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariantsRef(x as any);
    }
    
export type VariantsRef = KnownVariantsRef;
export interface VariantsRefParams {
  variants: ReadonlyArray<Variant> /* WeakRef */;
}

abstract class BaseVariantsRef extends BaseExpr {
  
    static isKnown(x: any): x is VariantsRef { return isKnownVariantsRef(x); }
    static getType(): VariantsRef { throw new Error(); }
    static modelTypeName = "VariantsRef";
    
  static readonly $fields = meta.allFields(meta.clsByName["VariantsRef"]);
  static readonly $field_variants: Field = ensure(
    BaseVariantsRef.$fields.find((f) => f.name === "variants"),
    "VariantsRef has no field variants",
  );
  /* WeakRef */
get variants(): ReadonlyArray<Variant> { meta.reportRead(this, "variants"); return (this as any).$v.variants; }
set variants(value: ReadonlyArray<Variant>) { meta.writeField(this, BaseVariantsRef.$field_variants, value); }
}
class ClsVariantsRef extends BaseVariantsRef {
  constructor(args: VariantsRefParams) {
    super();
    meta.initializers.VariantsRef(this, args);
  }
  get typeTag(): "VariantsRef" {
        return "VariantsRef";
      }
}
export const VariantsRef = ClsVariantsRef;

type KnownObjectPath = ClsObjectPath;
export function isKnownObjectPath<T>(x: [Extract<T, ObjectPath>] extends [never] ? never : T): x is [Extract<T, ObjectPath>] extends [never]
  ? never
  : ObjectPath extends T // Needed when T is any
  ? ObjectPath
  : Extract<T, ObjectPath> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseObjectPath;
}

    export function ensureKnownObjectPath<T>(x: [Extract<T, ObjectPath>] extends [never] ? never : T): ObjectPath {
      assert(isKnownObjectPath(x), () => mkUnexpectedTypeMsg([ObjectPath], x));
      return x;
    }
    export function ensureMaybeKnownObjectPath<T>(x: [Extract<T, ObjectPath>] extends [never] ? never : T): ObjectPath | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownObjectPath(x as any);
    }
    
export type ObjectPath = KnownObjectPath;
export interface ObjectPathParams {
  path: ReadonlyArray<string | number> /*  */;
fallback: Expr | null | undefined /*  */;
}

abstract class BaseObjectPath extends BaseExpr {
  
    static isKnown(x: any): x is ObjectPath { return isKnownObjectPath(x); }
    static getType(): ObjectPath { throw new Error(); }
    static modelTypeName = "ObjectPath";
    
  static readonly $fields = meta.allFields(meta.clsByName["ObjectPath"]);
  static readonly $field_path: Field = ensure(
    BaseObjectPath.$fields.find((f) => f.name === "path"),
    "ObjectPath has no field path",
  );
static readonly $field_fallback: Field = ensure(
    BaseObjectPath.$fields.find((f) => f.name === "fallback"),
    "ObjectPath has no field fallback",
  );
  get path(): ReadonlyArray<string | number> { meta.reportRead(this, "path"); return (this as any).$v.path; }
set path(value: ReadonlyArray<string | number>) { meta.writeField(this, BaseObjectPath.$field_path, value); }
get fallback(): Expr | null | undefined { meta.reportRead(this, "fallback"); return (this as any).$v.fallback; }
set fallback(value: Expr | null | undefined) { meta.writeField(this, BaseObjectPath.$field_fallback, value); }
}
class ClsObjectPath extends BaseObjectPath {
  constructor(args: ObjectPathParams) {
    super();
    meta.initializers.ObjectPath(this, args);
  }
  get typeTag(): "ObjectPath" {
        return "ObjectPath";
      }
}
export const ObjectPath = ClsObjectPath;

type KnownEventHandler = KnownGenericEventHandler | ClsEventHandler;
export function isKnownEventHandler<T>(x: [Extract<T, EventHandler>] extends [never] ? never : T): x is [Extract<T, EventHandler>] extends [never]
  ? never
  : EventHandler extends T // Needed when T is any
  ? EventHandler
  : Extract<T, EventHandler> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseEventHandler;
}

    export function ensureKnownEventHandler<T>(x: [Extract<T, EventHandler>] extends [never] ? never : T): EventHandler {
      assert(isKnownEventHandler(x), () => mkUnexpectedTypeMsg([EventHandler], x));
      return x;
    }
    export function ensureMaybeKnownEventHandler<T>(x: [Extract<T, EventHandler>] extends [never] ? never : T): EventHandler | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownEventHandler(x as any);
    }
    
export type EventHandler = KnownEventHandler;
export interface EventHandlerParams {
  interactions: ReadonlyArray<Interaction> /*  */;
}

abstract class BaseEventHandler extends BaseExpr {
  
    static isKnown(x: any): x is EventHandler { return isKnownEventHandler(x); }
    static getType(): EventHandler { throw new Error(); }
    static modelTypeName = "EventHandler";
    
  static readonly $fields = meta.allFields(meta.clsByName["EventHandler"]);
  static readonly $field_interactions: Field = ensure(
    BaseEventHandler.$fields.find((f) => f.name === "interactions"),
    "EventHandler has no field interactions",
  );
  get interactions(): ReadonlyArray<Interaction> { meta.reportRead(this, "interactions"); return (this as any).$v.interactions; }
set interactions(value: ReadonlyArray<Interaction>) { meta.writeField(this, BaseEventHandler.$field_interactions, value); }
}
class ClsEventHandler extends BaseEventHandler {
  constructor(args: EventHandlerParams) {
    super();
    meta.initializers.EventHandler(this, args);
  }
  get typeTag(): "EventHandler" {
        return "EventHandler";
      }
}
export const EventHandler = ClsEventHandler;

type KnownGenericEventHandler = ClsGenericEventHandler;
export function isKnownGenericEventHandler<T>(x: [Extract<T, GenericEventHandler>] extends [never] ? never : T): x is [Extract<T, GenericEventHandler>] extends [never]
  ? never
  : GenericEventHandler extends T // Needed when T is any
  ? GenericEventHandler
  : Extract<T, GenericEventHandler> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseGenericEventHandler;
}

    export function ensureKnownGenericEventHandler<T>(x: [Extract<T, GenericEventHandler>] extends [never] ? never : T): GenericEventHandler {
      assert(isKnownGenericEventHandler(x), () => mkUnexpectedTypeMsg([GenericEventHandler], x));
      return x;
    }
    export function ensureMaybeKnownGenericEventHandler<T>(x: [Extract<T, GenericEventHandler>] extends [never] ? never : T): GenericEventHandler | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownGenericEventHandler(x as any);
    }
    
export type GenericEventHandler = KnownGenericEventHandler;
export interface GenericEventHandlerParams {
  handlerType: FunctionType /*  */;
interactions: ReadonlyArray<Interaction> /*  */;
}

abstract class BaseGenericEventHandler extends BaseEventHandler {
  
    static isKnown(x: any): x is GenericEventHandler { return isKnownGenericEventHandler(x); }
    static getType(): GenericEventHandler { throw new Error(); }
    static modelTypeName = "GenericEventHandler";
    
  static readonly $fields = meta.allFields(meta.clsByName["GenericEventHandler"]);
  static readonly $field_handlerType: Field = ensure(
    BaseGenericEventHandler.$fields.find((f) => f.name === "handlerType"),
    "GenericEventHandler has no field handlerType",
  );
  get handlerType(): FunctionType { meta.reportRead(this, "handlerType"); return (this as any).$v.handlerType; }
set handlerType(value: FunctionType) { meta.writeField(this, BaseGenericEventHandler.$field_handlerType, value); }
}
class ClsGenericEventHandler extends BaseGenericEventHandler {
  constructor(args: GenericEventHandlerParams) {
    super();
    meta.initializers.GenericEventHandler(this, args);
  }
  get typeTag(): "GenericEventHandler" {
        return "GenericEventHandler";
      }
}
export const GenericEventHandler = ClsGenericEventHandler;

type KnownFunctionArg = KnownStrongFunctionArg | ClsFunctionArg;
export function isKnownFunctionArg<T>(x: [Extract<T, FunctionArg>] extends [never] ? never : T): x is [Extract<T, FunctionArg>] extends [never]
  ? never
  : FunctionArg extends T // Needed when T is any
  ? FunctionArg
  : Extract<T, FunctionArg> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseFunctionArg;
}

    export function ensureKnownFunctionArg<T>(x: [Extract<T, FunctionArg>] extends [never] ? never : T): FunctionArg {
      assert(isKnownFunctionArg(x), () => mkUnexpectedTypeMsg([FunctionArg], x));
      return x;
    }
    export function ensureMaybeKnownFunctionArg<T>(x: [Extract<T, FunctionArg>] extends [never] ? never : T): FunctionArg | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownFunctionArg(x as any);
    }
    
export type FunctionArg = KnownFunctionArg;
export interface FunctionArgParams {
  readonly uuid: string /* Const */;
argType: ArgType /* WeakRef */;
expr: Expr /*  */;
}

abstract class BaseFunctionArg extends BaseExpr {
  
    static isKnown(x: any): x is FunctionArg { return isKnownFunctionArg(x); }
    static getType(): FunctionArg { throw new Error(); }
    static modelTypeName = "FunctionArg";
    
  static readonly $fields = meta.allFields(meta.clsByName["FunctionArg"]);
  static readonly $field_uuid: Field = ensure(
    BaseFunctionArg.$fields.find((f) => f.name === "uuid"),
    "FunctionArg has no field uuid",
  );
static readonly $field_argType: Field = ensure(
    BaseFunctionArg.$fields.find((f) => f.name === "argType"),
    "FunctionArg has no field argType",
  );
static readonly $field_expr: Field = ensure(
    BaseFunctionArg.$fields.find((f) => f.name === "expr"),
    "FunctionArg has no field expr",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseFunctionArg.$field_uuid, value); }
/* WeakRef */
get argType(): ArgType { meta.reportRead(this, "argType"); return (this as any).$v.argType; }
set argType(value: ArgType) { meta.writeField(this, BaseFunctionArg.$field_argType, value); }
get expr(): Expr { meta.reportRead(this, "expr"); return (this as any).$v.expr; }
set expr(value: Expr) { meta.writeField(this, BaseFunctionArg.$field_expr, value); }
}
class ClsFunctionArg extends BaseFunctionArg {
  constructor(args: FunctionArgParams) {
    super();
    meta.initializers.FunctionArg(this, args);
  }
  get typeTag(): "FunctionArg" {
        return "FunctionArg";
      }
}
export const FunctionArg = ClsFunctionArg;

type KnownStrongFunctionArg = ClsStrongFunctionArg;
export function isKnownStrongFunctionArg<T>(x: [Extract<T, StrongFunctionArg>] extends [never] ? never : T): x is [Extract<T, StrongFunctionArg>] extends [never]
  ? never
  : StrongFunctionArg extends T // Needed when T is any
  ? StrongFunctionArg
  : Extract<T, StrongFunctionArg> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStrongFunctionArg;
}

    export function ensureKnownStrongFunctionArg<T>(x: [Extract<T, StrongFunctionArg>] extends [never] ? never : T): StrongFunctionArg {
      assert(isKnownStrongFunctionArg(x), () => mkUnexpectedTypeMsg([StrongFunctionArg], x));
      return x;
    }
    export function ensureMaybeKnownStrongFunctionArg<T>(x: [Extract<T, StrongFunctionArg>] extends [never] ? never : T): StrongFunctionArg | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStrongFunctionArg(x as any);
    }
    
export type StrongFunctionArg = KnownStrongFunctionArg;
export interface StrongFunctionArgParams {
  argType: ArgType /*  */;
readonly uuid: string /* Const */;
expr: Expr /*  */;
}

abstract class BaseStrongFunctionArg extends BaseFunctionArg {
  
    static isKnown(x: any): x is StrongFunctionArg { return isKnownStrongFunctionArg(x); }
    static getType(): StrongFunctionArg { throw new Error(); }
    static modelTypeName = "StrongFunctionArg";
    
  static readonly $fields = meta.allFields(meta.clsByName["StrongFunctionArg"]);
  static readonly $field_argType: Field = ensure(
    BaseStrongFunctionArg.$fields.find((f) => f.name === "argType"),
    "StrongFunctionArg has no field argType",
  );
  get argType(): ArgType { meta.reportRead(this, "argType"); return (this as any).$v.argType; }
set argType(value: ArgType) { meta.writeField(this, BaseStrongFunctionArg.$field_argType, value); }
}
class ClsStrongFunctionArg extends BaseStrongFunctionArg {
  constructor(args: StrongFunctionArgParams) {
    super();
    meta.initializers.StrongFunctionArg(this, args);
  }
  get typeTag(): "StrongFunctionArg" {
        return "StrongFunctionArg";
      }
}
export const StrongFunctionArg = ClsStrongFunctionArg;

type KnownCollectionExpr = ClsCollectionExpr;
export function isKnownCollectionExpr<T>(x: [Extract<T, CollectionExpr>] extends [never] ? never : T): x is [Extract<T, CollectionExpr>] extends [never]
  ? never
  : CollectionExpr extends T // Needed when T is any
  ? CollectionExpr
  : Extract<T, CollectionExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCollectionExpr;
}

    export function ensureKnownCollectionExpr<T>(x: [Extract<T, CollectionExpr>] extends [never] ? never : T): CollectionExpr {
      assert(isKnownCollectionExpr(x), () => mkUnexpectedTypeMsg([CollectionExpr], x));
      return x;
    }
    export function ensureMaybeKnownCollectionExpr<T>(x: [Extract<T, CollectionExpr>] extends [never] ? never : T): CollectionExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCollectionExpr(x as any);
    }
    
export type CollectionExpr = KnownCollectionExpr;
export interface CollectionExprParams {
  exprs: ReadonlyArray<Expr | null | undefined> /*  */;
}

abstract class BaseCollectionExpr extends BaseExpr {
  
    static isKnown(x: any): x is CollectionExpr { return isKnownCollectionExpr(x); }
    static getType(): CollectionExpr { throw new Error(); }
    static modelTypeName = "CollectionExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["CollectionExpr"]);
  static readonly $field_exprs: Field = ensure(
    BaseCollectionExpr.$fields.find((f) => f.name === "exprs"),
    "CollectionExpr has no field exprs",
  );
  get exprs(): ReadonlyArray<Expr | null | undefined> { meta.reportRead(this, "exprs"); return (this as any).$v.exprs; }
set exprs(value: ReadonlyArray<Expr | null | undefined>) { meta.writeField(this, BaseCollectionExpr.$field_exprs, value); }
}
class ClsCollectionExpr extends BaseCollectionExpr {
  constructor(args: CollectionExprParams) {
    super();
    meta.initializers.CollectionExpr(this, args);
  }
  get typeTag(): "CollectionExpr" {
        return "CollectionExpr";
      }
}
export const CollectionExpr = ClsCollectionExpr;

type KnownMapExpr = ClsMapExpr;
export function isKnownMapExpr<T>(x: [Extract<T, MapExpr>] extends [never] ? never : T): x is [Extract<T, MapExpr>] extends [never]
  ? never
  : MapExpr extends T // Needed when T is any
  ? MapExpr
  : Extract<T, MapExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseMapExpr;
}

    export function ensureKnownMapExpr<T>(x: [Extract<T, MapExpr>] extends [never] ? never : T): MapExpr {
      assert(isKnownMapExpr(x), () => mkUnexpectedTypeMsg([MapExpr], x));
      return x;
    }
    export function ensureMaybeKnownMapExpr<T>(x: [Extract<T, MapExpr>] extends [never] ? never : T): MapExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownMapExpr(x as any);
    }
    
export type MapExpr = KnownMapExpr;
export interface MapExprParams {
  mapExpr: Readonly<Record<string, Expr>> /*  */;
}

abstract class BaseMapExpr extends BaseExpr {
  
    static isKnown(x: any): x is MapExpr { return isKnownMapExpr(x); }
    static getType(): MapExpr { throw new Error(); }
    static modelTypeName = "MapExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["MapExpr"]);
  static readonly $field_mapExpr: Field = ensure(
    BaseMapExpr.$fields.find((f) => f.name === "mapExpr"),
    "MapExpr has no field mapExpr",
  );
  get mapExpr(): Readonly<Record<string, Expr>> { meta.reportRead(this, "mapExpr"); return (this as any).$v.mapExpr; }
set mapExpr(value: Readonly<Record<string, Expr>>) { meta.writeField(this, BaseMapExpr.$field_mapExpr, value); }
}
class ClsMapExpr extends BaseMapExpr {
  constructor(args: MapExprParams) {
    super();
    meta.initializers.MapExpr(this, args);
  }
  get typeTag(): "MapExpr" {
        return "MapExpr";
      }
}
export const MapExpr = ClsMapExpr;

type KnownStyleExpr = ClsStyleExpr;
export function isKnownStyleExpr<T>(x: [Extract<T, StyleExpr>] extends [never] ? never : T): x is [Extract<T, StyleExpr>] extends [never]
  ? never
  : StyleExpr extends T // Needed when T is any
  ? StyleExpr
  : Extract<T, StyleExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseStyleExpr;
}

    export function ensureKnownStyleExpr<T>(x: [Extract<T, StyleExpr>] extends [never] ? never : T): StyleExpr {
      assert(isKnownStyleExpr(x), () => mkUnexpectedTypeMsg([StyleExpr], x));
      return x;
    }
    export function ensureMaybeKnownStyleExpr<T>(x: [Extract<T, StyleExpr>] extends [never] ? never : T): StyleExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownStyleExpr(x as any);
    }
    
export type StyleExpr = KnownStyleExpr;
export interface StyleExprParams {
  uuid: string /*  */;
styles: ReadonlyArray<SelectorRuleSet> /*  */;
}

abstract class BaseStyleExpr extends BaseExpr {
  
    static isKnown(x: any): x is StyleExpr { return isKnownStyleExpr(x); }
    static getType(): StyleExpr { throw new Error(); }
    static modelTypeName = "StyleExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["StyleExpr"]);
  static readonly $field_uuid: Field = ensure(
    BaseStyleExpr.$fields.find((f) => f.name === "uuid"),
    "StyleExpr has no field uuid",
  );
static readonly $field_styles: Field = ensure(
    BaseStyleExpr.$fields.find((f) => f.name === "styles"),
    "StyleExpr has no field styles",
  );
  get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
set uuid(value: string) { meta.writeField(this, BaseStyleExpr.$field_uuid, value); }
get styles(): ReadonlyArray<SelectorRuleSet> { meta.reportRead(this, "styles"); return (this as any).$v.styles; }
set styles(value: ReadonlyArray<SelectorRuleSet>) { meta.writeField(this, BaseStyleExpr.$field_styles, value); }
}
class ClsStyleExpr extends BaseStyleExpr {
  constructor(args: StyleExprParams) {
    super();
    meta.initializers.StyleExpr(this, args);
  }
  get typeTag(): "StyleExpr" {
        return "StyleExpr";
      }
}
export const StyleExpr = ClsStyleExpr;

type KnownTemplatedString = ClsTemplatedString;
export function isKnownTemplatedString<T>(x: [Extract<T, TemplatedString>] extends [never] ? never : T): x is [Extract<T, TemplatedString>] extends [never]
  ? never
  : TemplatedString extends T // Needed when T is any
  ? TemplatedString
  : Extract<T, TemplatedString> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseTemplatedString;
}

    export function ensureKnownTemplatedString<T>(x: [Extract<T, TemplatedString>] extends [never] ? never : T): TemplatedString {
      assert(isKnownTemplatedString(x), () => mkUnexpectedTypeMsg([TemplatedString], x));
      return x;
    }
    export function ensureMaybeKnownTemplatedString<T>(x: [Extract<T, TemplatedString>] extends [never] ? never : T): TemplatedString | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownTemplatedString(x as any);
    }
    
export type TemplatedString = KnownTemplatedString;
export interface TemplatedStringParams {
  text: ReadonlyArray<string | ObjectPath | CustomCode> /*  */;
}

abstract class BaseTemplatedString extends BaseExpr {
  
    static isKnown(x: any): x is TemplatedString { return isKnownTemplatedString(x); }
    static getType(): TemplatedString { throw new Error(); }
    static modelTypeName = "TemplatedString";
    
  static readonly $fields = meta.allFields(meta.clsByName["TemplatedString"]);
  static readonly $field_text: Field = ensure(
    BaseTemplatedString.$fields.find((f) => f.name === "text"),
    "TemplatedString has no field text",
  );
  get text(): ReadonlyArray<string | ObjectPath | CustomCode> { meta.reportRead(this, "text"); return (this as any).$v.text; }
set text(value: ReadonlyArray<string | ObjectPath | CustomCode>) { meta.writeField(this, BaseTemplatedString.$field_text, value); }
}
class ClsTemplatedString extends BaseTemplatedString {
  constructor(args: TemplatedStringParams) {
    super();
    meta.initializers.TemplatedString(this, args);
  }
  get typeTag(): "TemplatedString" {
        return "TemplatedString";
      }
}
export const TemplatedString = ClsTemplatedString;

type KnownFunctionExpr = ClsFunctionExpr;
export function isKnownFunctionExpr<T>(x: [Extract<T, FunctionExpr>] extends [never] ? never : T): x is [Extract<T, FunctionExpr>] extends [never]
  ? never
  : FunctionExpr extends T // Needed when T is any
  ? FunctionExpr
  : Extract<T, FunctionExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseFunctionExpr;
}

    export function ensureKnownFunctionExpr<T>(x: [Extract<T, FunctionExpr>] extends [never] ? never : T): FunctionExpr {
      assert(isKnownFunctionExpr(x), () => mkUnexpectedTypeMsg([FunctionExpr], x));
      return x;
    }
    export function ensureMaybeKnownFunctionExpr<T>(x: [Extract<T, FunctionExpr>] extends [never] ? never : T): FunctionExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownFunctionExpr(x as any);
    }
    
export type FunctionExpr = KnownFunctionExpr;
export interface FunctionExprParams {
  argNames: ReadonlyArray<string> /*  */;
bodyExpr: Expr /*  */;
}

abstract class BaseFunctionExpr extends BaseExpr {
  
    static isKnown(x: any): x is FunctionExpr { return isKnownFunctionExpr(x); }
    static getType(): FunctionExpr { throw new Error(); }
    static modelTypeName = "FunctionExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["FunctionExpr"]);
  static readonly $field_argNames: Field = ensure(
    BaseFunctionExpr.$fields.find((f) => f.name === "argNames"),
    "FunctionExpr has no field argNames",
  );
static readonly $field_bodyExpr: Field = ensure(
    BaseFunctionExpr.$fields.find((f) => f.name === "bodyExpr"),
    "FunctionExpr has no field bodyExpr",
  );
  get argNames(): ReadonlyArray<string> { meta.reportRead(this, "argNames"); return (this as any).$v.argNames; }
set argNames(value: ReadonlyArray<string>) { meta.writeField(this, BaseFunctionExpr.$field_argNames, value); }
get bodyExpr(): Expr { meta.reportRead(this, "bodyExpr"); return (this as any).$v.bodyExpr; }
set bodyExpr(value: Expr) { meta.writeField(this, BaseFunctionExpr.$field_bodyExpr, value); }
}
class ClsFunctionExpr extends BaseFunctionExpr {
  constructor(args: FunctionExprParams) {
    super();
    meta.initializers.FunctionExpr(this, args);
  }
  get typeTag(): "FunctionExpr" {
        return "FunctionExpr";
      }
}
export const FunctionExpr = ClsFunctionExpr;

type KnownQueryInvalidationExpr = ClsQueryInvalidationExpr;
export function isKnownQueryInvalidationExpr<T>(x: [Extract<T, QueryInvalidationExpr>] extends [never] ? never : T): x is [Extract<T, QueryInvalidationExpr>] extends [never]
  ? never
  : QueryInvalidationExpr extends T // Needed when T is any
  ? QueryInvalidationExpr
  : Extract<T, QueryInvalidationExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseQueryInvalidationExpr;
}

    export function ensureKnownQueryInvalidationExpr<T>(x: [Extract<T, QueryInvalidationExpr>] extends [never] ? never : T): QueryInvalidationExpr {
      assert(isKnownQueryInvalidationExpr(x), () => mkUnexpectedTypeMsg([QueryInvalidationExpr], x));
      return x;
    }
    export function ensureMaybeKnownQueryInvalidationExpr<T>(x: [Extract<T, QueryInvalidationExpr>] extends [never] ? never : T): QueryInvalidationExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownQueryInvalidationExpr(x as any);
    }
    
export type QueryInvalidationExpr = KnownQueryInvalidationExpr;
export interface QueryInvalidationExprParams {
  invalidationQueries: ReadonlyArray<QueryRef | string> /*  */;
invalidationKeys: CustomCode | null | undefined | ObjectPath | null | undefined /*  */;
}

abstract class BaseQueryInvalidationExpr extends BaseExpr {
  
    static isKnown(x: any): x is QueryInvalidationExpr { return isKnownQueryInvalidationExpr(x); }
    static getType(): QueryInvalidationExpr { throw new Error(); }
    static modelTypeName = "QueryInvalidationExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["QueryInvalidationExpr"]);
  static readonly $field_invalidationQueries: Field = ensure(
    BaseQueryInvalidationExpr.$fields.find((f) => f.name === "invalidationQueries"),
    "QueryInvalidationExpr has no field invalidationQueries",
  );
static readonly $field_invalidationKeys: Field = ensure(
    BaseQueryInvalidationExpr.$fields.find((f) => f.name === "invalidationKeys"),
    "QueryInvalidationExpr has no field invalidationKeys",
  );
  get invalidationQueries(): ReadonlyArray<QueryRef | string> { meta.reportRead(this, "invalidationQueries"); return (this as any).$v.invalidationQueries; }
set invalidationQueries(value: ReadonlyArray<QueryRef | string>) { meta.writeField(this, BaseQueryInvalidationExpr.$field_invalidationQueries, value); }
get invalidationKeys(): CustomCode | null | undefined | ObjectPath | null | undefined { meta.reportRead(this, "invalidationKeys"); return (this as any).$v.invalidationKeys; }
set invalidationKeys(value: CustomCode | null | undefined | ObjectPath | null | undefined) { meta.writeField(this, BaseQueryInvalidationExpr.$field_invalidationKeys, value); }
}
class ClsQueryInvalidationExpr extends BaseQueryInvalidationExpr {
  constructor(args: QueryInvalidationExprParams) {
    super();
    meta.initializers.QueryInvalidationExpr(this, args);
  }
  get typeTag(): "QueryInvalidationExpr" {
        return "QueryInvalidationExpr";
      }
}
export const QueryInvalidationExpr = ClsQueryInvalidationExpr;

type KnownCompositeExpr = ClsCompositeExpr;
export function isKnownCompositeExpr<T>(x: [Extract<T, CompositeExpr>] extends [never] ? never : T): x is [Extract<T, CompositeExpr>] extends [never]
  ? never
  : CompositeExpr extends T // Needed when T is any
  ? CompositeExpr
  : Extract<T, CompositeExpr> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseCompositeExpr;
}

    export function ensureKnownCompositeExpr<T>(x: [Extract<T, CompositeExpr>] extends [never] ? never : T): CompositeExpr {
      assert(isKnownCompositeExpr(x), () => mkUnexpectedTypeMsg([CompositeExpr], x));
      return x;
    }
    export function ensureMaybeKnownCompositeExpr<T>(x: [Extract<T, CompositeExpr>] extends [never] ? never : T): CompositeExpr | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownCompositeExpr(x as any);
    }
    
export type CompositeExpr = KnownCompositeExpr;
export interface CompositeExprParams {
  hostLiteral: string /*  */;
substitutions: Readonly<Record<string, Expr>> /*  */;
}

abstract class BaseCompositeExpr extends BaseExpr {
  
    static isKnown(x: any): x is CompositeExpr { return isKnownCompositeExpr(x); }
    static getType(): CompositeExpr { throw new Error(); }
    static modelTypeName = "CompositeExpr";
    
  static readonly $fields = meta.allFields(meta.clsByName["CompositeExpr"]);
  static readonly $field_hostLiteral: Field = ensure(
    BaseCompositeExpr.$fields.find((f) => f.name === "hostLiteral"),
    "CompositeExpr has no field hostLiteral",
  );
static readonly $field_substitutions: Field = ensure(
    BaseCompositeExpr.$fields.find((f) => f.name === "substitutions"),
    "CompositeExpr has no field substitutions",
  );
  get hostLiteral(): string { meta.reportRead(this, "hostLiteral"); return (this as any).$v.hostLiteral; }
set hostLiteral(value: string) { meta.writeField(this, BaseCompositeExpr.$field_hostLiteral, value); }
get substitutions(): Readonly<Record<string, Expr>> { meta.reportRead(this, "substitutions"); return (this as any).$v.substitutions; }
set substitutions(value: Readonly<Record<string, Expr>>) { meta.writeField(this, BaseCompositeExpr.$field_substitutions, value); }
}
class ClsCompositeExpr extends BaseCompositeExpr {
  constructor(args: CompositeExprParams) {
    super();
    meta.initializers.CompositeExpr(this, args);
  }
  get typeTag(): "CompositeExpr" {
        return "CompositeExpr";
      }
}
export const CompositeExpr = ClsCompositeExpr;

type KnownSelectorRuleSet = ClsSelectorRuleSet;
export function isKnownSelectorRuleSet<T>(x: [Extract<T, SelectorRuleSet>] extends [never] ? never : T): x is [Extract<T, SelectorRuleSet>] extends [never]
  ? never
  : SelectorRuleSet extends T // Needed when T is any
  ? SelectorRuleSet
  : Extract<T, SelectorRuleSet> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSelectorRuleSet;
}

    export function ensureKnownSelectorRuleSet<T>(x: [Extract<T, SelectorRuleSet>] extends [never] ? never : T): SelectorRuleSet {
      assert(isKnownSelectorRuleSet(x), () => mkUnexpectedTypeMsg([SelectorRuleSet], x));
      return x;
    }
    export function ensureMaybeKnownSelectorRuleSet<T>(x: [Extract<T, SelectorRuleSet>] extends [never] ? never : T): SelectorRuleSet | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSelectorRuleSet(x as any);
    }
    
export type SelectorRuleSet = KnownSelectorRuleSet;
export interface SelectorRuleSetParams {
  rs: RuleSet /*  */;
selector: string | null | undefined /*  */;
}

abstract class BaseSelectorRuleSet extends ModelInst {
  
    static isKnown(x: any): x is SelectorRuleSet { return isKnownSelectorRuleSet(x); }
    static getType(): SelectorRuleSet { throw new Error(); }
    static modelTypeName = "SelectorRuleSet";
    
  static readonly $fields = meta.allFields(meta.clsByName["SelectorRuleSet"]);
  static readonly $field_rs: Field = ensure(
    BaseSelectorRuleSet.$fields.find((f) => f.name === "rs"),
    "SelectorRuleSet has no field rs",
  );
static readonly $field_selector: Field = ensure(
    BaseSelectorRuleSet.$fields.find((f) => f.name === "selector"),
    "SelectorRuleSet has no field selector",
  );
  get rs(): RuleSet { meta.reportRead(this, "rs"); return (this as any).$v.rs; }
set rs(value: RuleSet) { meta.writeField(this, BaseSelectorRuleSet.$field_rs, value); }
get selector(): string | null | undefined { meta.reportRead(this, "selector"); return (this as any).$v.selector; }
set selector(value: string | null | undefined) { meta.writeField(this, BaseSelectorRuleSet.$field_selector, value); }
}
class ClsSelectorRuleSet extends BaseSelectorRuleSet {
  constructor(args: SelectorRuleSetParams) {
    super();
    meta.initializers.SelectorRuleSet(this, args);
  }
  get typeTag(): "SelectorRuleSet" {
        return "SelectorRuleSet";
      }
}
export const SelectorRuleSet = ClsSelectorRuleSet;

type KnownLabeledSelector = ClsLabeledSelector;
export function isKnownLabeledSelector<T>(x: [Extract<T, LabeledSelector>] extends [never] ? never : T): x is [Extract<T, LabeledSelector>] extends [never]
  ? never
  : LabeledSelector extends T // Needed when T is any
  ? LabeledSelector
  : Extract<T, LabeledSelector> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseLabeledSelector;
}

    export function ensureKnownLabeledSelector<T>(x: [Extract<T, LabeledSelector>] extends [never] ? never : T): LabeledSelector {
      assert(isKnownLabeledSelector(x), () => mkUnexpectedTypeMsg([LabeledSelector], x));
      return x;
    }
    export function ensureMaybeKnownLabeledSelector<T>(x: [Extract<T, LabeledSelector>] extends [never] ? never : T): LabeledSelector | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownLabeledSelector(x as any);
    }
    
export type LabeledSelector = KnownLabeledSelector;
export interface LabeledSelectorParams {
  selector: string /*  */;
label: string | null | undefined /*  */;
defaultStyles: Readonly<Record<string, string>> /*  */;
}

abstract class BaseLabeledSelector extends ModelInst {
  
    static isKnown(x: any): x is LabeledSelector { return isKnownLabeledSelector(x); }
    static getType(): LabeledSelector { throw new Error(); }
    static modelTypeName = "LabeledSelector";
    
  static readonly $fields = meta.allFields(meta.clsByName["LabeledSelector"]);
  static readonly $field_selector: Field = ensure(
    BaseLabeledSelector.$fields.find((f) => f.name === "selector"),
    "LabeledSelector has no field selector",
  );
static readonly $field_label: Field = ensure(
    BaseLabeledSelector.$fields.find((f) => f.name === "label"),
    "LabeledSelector has no field label",
  );
static readonly $field_defaultStyles: Field = ensure(
    BaseLabeledSelector.$fields.find((f) => f.name === "defaultStyles"),
    "LabeledSelector has no field defaultStyles",
  );
  get selector(): string { meta.reportRead(this, "selector"); return (this as any).$v.selector; }
set selector(value: string) { meta.writeField(this, BaseLabeledSelector.$field_selector, value); }
get label(): string | null | undefined { meta.reportRead(this, "label"); return (this as any).$v.label; }
set label(value: string | null | undefined) { meta.writeField(this, BaseLabeledSelector.$field_label, value); }
get defaultStyles(): Readonly<Record<string, string>> { meta.reportRead(this, "defaultStyles"); return (this as any).$v.defaultStyles; }
set defaultStyles(value: Readonly<Record<string, string>>) { meta.writeField(this, BaseLabeledSelector.$field_defaultStyles, value); }
}
class ClsLabeledSelector extends BaseLabeledSelector {
  constructor(args: LabeledSelectorParams) {
    super();
    meta.initializers.LabeledSelector(this, args);
  }
  get typeTag(): "LabeledSelector" {
        return "LabeledSelector";
      }
}
export const LabeledSelector = ClsLabeledSelector;

type KnownDataSourceTemplate = ClsDataSourceTemplate;
export function isKnownDataSourceTemplate<T>(x: [Extract<T, DataSourceTemplate>] extends [never] ? never : T): x is [Extract<T, DataSourceTemplate>] extends [never]
  ? never
  : DataSourceTemplate extends T // Needed when T is any
  ? DataSourceTemplate
  : Extract<T, DataSourceTemplate> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseDataSourceTemplate;
}

    export function ensureKnownDataSourceTemplate<T>(x: [Extract<T, DataSourceTemplate>] extends [never] ? never : T): DataSourceTemplate {
      assert(isKnownDataSourceTemplate(x), () => mkUnexpectedTypeMsg([DataSourceTemplate], x));
      return x;
    }
    export function ensureMaybeKnownDataSourceTemplate<T>(x: [Extract<T, DataSourceTemplate>] extends [never] ? never : T): DataSourceTemplate | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownDataSourceTemplate(x as any);
    }
    
export type DataSourceTemplate = KnownDataSourceTemplate;
export interface DataSourceTemplateParams {
  fieldType: string /*  */;
value: TemplatedString | string /*  */;
bindings: Readonly<Record<string, TemplatedString | CustomCode | ObjectPath>> | null | undefined /*  */;
}

abstract class BaseDataSourceTemplate extends ModelInst {
  
    static isKnown(x: any): x is DataSourceTemplate { return isKnownDataSourceTemplate(x); }
    static getType(): DataSourceTemplate { throw new Error(); }
    static modelTypeName = "DataSourceTemplate";
    
  static readonly $fields = meta.allFields(meta.clsByName["DataSourceTemplate"]);
  static readonly $field_fieldType: Field = ensure(
    BaseDataSourceTemplate.$fields.find((f) => f.name === "fieldType"),
    "DataSourceTemplate has no field fieldType",
  );
static readonly $field_value: Field = ensure(
    BaseDataSourceTemplate.$fields.find((f) => f.name === "value"),
    "DataSourceTemplate has no field value",
  );
static readonly $field_bindings: Field = ensure(
    BaseDataSourceTemplate.$fields.find((f) => f.name === "bindings"),
    "DataSourceTemplate has no field bindings",
  );
  get fieldType(): string { meta.reportRead(this, "fieldType"); return (this as any).$v.fieldType; }
set fieldType(value: string) { meta.writeField(this, BaseDataSourceTemplate.$field_fieldType, value); }
get value(): TemplatedString | string { meta.reportRead(this, "value"); return (this as any).$v.value; }
set value(value: TemplatedString | string) { meta.writeField(this, BaseDataSourceTemplate.$field_value, value); }
get bindings(): Readonly<Record<string, TemplatedString | CustomCode | ObjectPath>> | null | undefined { meta.reportRead(this, "bindings"); return (this as any).$v.bindings; }
set bindings(value: Readonly<Record<string, TemplatedString | CustomCode | ObjectPath>> | null | undefined) { meta.writeField(this, BaseDataSourceTemplate.$field_bindings, value); }
}
class ClsDataSourceTemplate extends BaseDataSourceTemplate {
  constructor(args: DataSourceTemplateParams) {
    super();
    meta.initializers.DataSourceTemplate(this, args);
  }
  get typeTag(): "DataSourceTemplate" {
        return "DataSourceTemplate";
      }
}
export const DataSourceTemplate = ClsDataSourceTemplate;

type KnownQueryRef = ClsQueryRef;
export function isKnownQueryRef<T>(x: [Extract<T, QueryRef>] extends [never] ? never : T): x is [Extract<T, QueryRef>] extends [never]
  ? never
  : QueryRef extends T // Needed when T is any
  ? QueryRef
  : Extract<T, QueryRef> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseQueryRef;
}

    export function ensureKnownQueryRef<T>(x: [Extract<T, QueryRef>] extends [never] ? never : T): QueryRef {
      assert(isKnownQueryRef(x), () => mkUnexpectedTypeMsg([QueryRef], x));
      return x;
    }
    export function ensureMaybeKnownQueryRef<T>(x: [Extract<T, QueryRef>] extends [never] ? never : T): QueryRef | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownQueryRef(x as any);
    }
    
export type QueryRef = KnownQueryRef;
export interface QueryRefParams {
  ref: TplNode | ComponentDataQuery | ComponentServerQuery /* WeakRef */;
}

abstract class BaseQueryRef extends ModelInst {
  
    static isKnown(x: any): x is QueryRef { return isKnownQueryRef(x); }
    static getType(): QueryRef { throw new Error(); }
    static modelTypeName = "QueryRef";
    
  static readonly $fields = meta.allFields(meta.clsByName["QueryRef"]);
  static readonly $field_ref: Field = ensure(
    BaseQueryRef.$fields.find((f) => f.name === "ref"),
    "QueryRef has no field ref",
  );
  /* WeakRef */
get ref(): TplNode | ComponentDataQuery | ComponentServerQuery { meta.reportRead(this, "ref"); return (this as any).$v.ref; }
set ref(value: TplNode | ComponentDataQuery | ComponentServerQuery) { meta.writeField(this, BaseQueryRef.$field_ref, value); }
}
class ClsQueryRef extends BaseQueryRef {
  constructor(args: QueryRefParams) {
    super();
    meta.initializers.QueryRef(this, args);
  }
  get typeTag(): "QueryRef" {
        return "QueryRef";
      }
}
export const QueryRef = ClsQueryRef;

type KnownState = KnownNamedState | KnownVariantGroupState | ClsState;
export function isKnownState<T>(x: [Extract<T, State>] extends [never] ? never : T): x is [Extract<T, State>] extends [never]
  ? never
  : State extends T // Needed when T is any
  ? State
  : Extract<T, State> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseState;
}

    export function ensureKnownState<T>(x: [Extract<T, State>] extends [never] ? never : T): State {
      assert(isKnownState(x), () => mkUnexpectedTypeMsg([State], x));
      return x;
    }
    export function ensureMaybeKnownState<T>(x: [Extract<T, State>] extends [never] ? never : T): State | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownState(x as any);
    }
    
export type State = KnownState;
export interface StateParams {
  readonly param: StateParam /* Const,WeakRef */;
accessType: "private" | "readonly" | "writable" /*  */;
variableType: "text" | "number" | "boolean" | "array" | "object" | "variant" | "dateString" | "dateRangeStrings" /*  */;
readonly onChangeParam: StateChangeHandlerParam | PropParam /* Const,WeakRef */;
tplNode: TplComponent | null | undefined | TplTag | null | undefined /* WeakRef */;
implicitState: State | null | undefined /* WeakRef */;
}

abstract class BaseState extends ModelInst {
  
    static isKnown(x: any): x is State { return isKnownState(x); }
    static getType(): State { throw new Error(); }
    static modelTypeName = "State";
    
  static readonly $fields = meta.allFields(meta.clsByName["State"]);
  static readonly $field_param: Field = ensure(
    BaseState.$fields.find((f) => f.name === "param"),
    "State has no field param",
  );
static readonly $field_accessType: Field = ensure(
    BaseState.$fields.find((f) => f.name === "accessType"),
    "State has no field accessType",
  );
static readonly $field_variableType: Field = ensure(
    BaseState.$fields.find((f) => f.name === "variableType"),
    "State has no field variableType",
  );
static readonly $field_onChangeParam: Field = ensure(
    BaseState.$fields.find((f) => f.name === "onChangeParam"),
    "State has no field onChangeParam",
  );
static readonly $field_tplNode: Field = ensure(
    BaseState.$fields.find((f) => f.name === "tplNode"),
    "State has no field tplNode",
  );
static readonly $field_implicitState: Field = ensure(
    BaseState.$fields.find((f) => f.name === "implicitState"),
    "State has no field implicitState",
  );
  /* Const,WeakRef */
get param(): StateParam { meta.reportRead(this, "param"); return (this as any).$v.param; }
private set param(value: StateParam) { meta.writeField(this, BaseState.$field_param, value); }
get accessType(): "private" | "readonly" | "writable" { meta.reportRead(this, "accessType"); return (this as any).$v.accessType; }
set accessType(value: "private" | "readonly" | "writable") { meta.writeField(this, BaseState.$field_accessType, value); }
get variableType(): "text" | "number" | "boolean" | "array" | "object" | "variant" | "dateString" | "dateRangeStrings" { meta.reportRead(this, "variableType"); return (this as any).$v.variableType; }
set variableType(value: "text" | "number" | "boolean" | "array" | "object" | "variant" | "dateString" | "dateRangeStrings") { meta.writeField(this, BaseState.$field_variableType, value); }
/* Const,WeakRef */
get onChangeParam(): StateChangeHandlerParam | PropParam { meta.reportRead(this, "onChangeParam"); return (this as any).$v.onChangeParam; }
private set onChangeParam(value: StateChangeHandlerParam | PropParam) { meta.writeField(this, BaseState.$field_onChangeParam, value); }
/* WeakRef */
get tplNode(): TplComponent | null | undefined | TplTag | null | undefined { meta.reportRead(this, "tplNode"); return (this as any).$v.tplNode; }
set tplNode(value: TplComponent | null | undefined | TplTag | null | undefined) { meta.writeField(this, BaseState.$field_tplNode, value); }
/* WeakRef */
get implicitState(): State | null | undefined { meta.reportRead(this, "implicitState"); return (this as any).$v.implicitState; }
set implicitState(value: State | null | undefined) { meta.writeField(this, BaseState.$field_implicitState, value); }
}
class ClsState extends BaseState {
  constructor(args: StateParams) {
    super();
    meta.initializers.State(this, args);
  }
  get typeTag(): "State" {
        return "State";
      }
}
export const State = ClsState;

type KnownNamedState = ClsNamedState;
export function isKnownNamedState<T>(x: [Extract<T, NamedState>] extends [never] ? never : T): x is [Extract<T, NamedState>] extends [never]
  ? never
  : NamedState extends T // Needed when T is any
  ? NamedState
  : Extract<T, NamedState> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseNamedState;
}

    export function ensureKnownNamedState<T>(x: [Extract<T, NamedState>] extends [never] ? never : T): NamedState {
      assert(isKnownNamedState(x), () => mkUnexpectedTypeMsg([NamedState], x));
      return x;
    }
    export function ensureMaybeKnownNamedState<T>(x: [Extract<T, NamedState>] extends [never] ? never : T): NamedState | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownNamedState(x as any);
    }
    
export type NamedState = KnownNamedState;
export interface NamedStateParams {
  variableType: "text" | "number" | "boolean" | "array" | "object" | "dateString" | "dateRangeStrings" /*  */;
name: string /*  */;
readonly param: StateParam /* Const,WeakRef */;
accessType: "private" | "readonly" | "writable" /*  */;
readonly onChangeParam: StateChangeHandlerParam | PropParam /* Const,WeakRef */;
tplNode: TplComponent | null | undefined | TplTag | null | undefined /* WeakRef */;
implicitState: State | null | undefined /* WeakRef */;
}

abstract class BaseNamedState extends BaseState {
  
    static isKnown(x: any): x is NamedState { return isKnownNamedState(x); }
    static getType(): NamedState { throw new Error(); }
    static modelTypeName = "NamedState";
    
  static readonly $fields = meta.allFields(meta.clsByName["NamedState"]);
  static readonly $field_variableType: Field = ensure(
    BaseNamedState.$fields.find((f) => f.name === "variableType"),
    "NamedState has no field variableType",
  );
static readonly $field_name: Field = ensure(
    BaseNamedState.$fields.find((f) => f.name === "name"),
    "NamedState has no field name",
  );
  get variableType(): "text" | "number" | "boolean" | "array" | "object" | "dateString" | "dateRangeStrings" { meta.reportRead(this, "variableType"); return (this as any).$v.variableType; }
set variableType(value: "text" | "number" | "boolean" | "array" | "object" | "dateString" | "dateRangeStrings") { meta.writeField(this, BaseNamedState.$field_variableType, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseNamedState.$field_name, value); }
}
class ClsNamedState extends BaseNamedState {
  constructor(args: NamedStateParams) {
    super();
    meta.initializers.NamedState(this, args);
  }
  get typeTag(): "NamedState" {
        return "NamedState";
      }
}
export const NamedState = ClsNamedState;

type KnownVariantGroupState = ClsVariantGroupState;
export function isKnownVariantGroupState<T>(x: [Extract<T, VariantGroupState>] extends [never] ? never : T): x is [Extract<T, VariantGroupState>] extends [never]
  ? never
  : VariantGroupState extends T // Needed when T is any
  ? VariantGroupState
  : Extract<T, VariantGroupState> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseVariantGroupState;
}

    export function ensureKnownVariantGroupState<T>(x: [Extract<T, VariantGroupState>] extends [never] ? never : T): VariantGroupState {
      assert(isKnownVariantGroupState(x), () => mkUnexpectedTypeMsg([VariantGroupState], x));
      return x;
    }
    export function ensureMaybeKnownVariantGroupState<T>(x: [Extract<T, VariantGroupState>] extends [never] ? never : T): VariantGroupState | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownVariantGroupState(x as any);
    }
    
export type VariantGroupState = KnownVariantGroupState;
export interface VariantGroupStateParams {
  readonly variantGroup: ComponentVariantGroup /* Const,WeakRef */;
readonly param: StateParam /* Const,WeakRef */;
accessType: "private" | "readonly" | "writable" /*  */;
variableType: "text" | "number" | "boolean" | "array" | "object" | "variant" | "dateString" | "dateRangeStrings" /*  */;
readonly onChangeParam: StateChangeHandlerParam | PropParam /* Const,WeakRef */;
tplNode: TplComponent | null | undefined | TplTag | null | undefined /* WeakRef */;
implicitState: State | null | undefined /* WeakRef */;
}

abstract class BaseVariantGroupState extends BaseState {
  
    static isKnown(x: any): x is VariantGroupState { return isKnownVariantGroupState(x); }
    static getType(): VariantGroupState { throw new Error(); }
    static modelTypeName = "VariantGroupState";
    
  static readonly $fields = meta.allFields(meta.clsByName["VariantGroupState"]);
  static readonly $field_variantGroup: Field = ensure(
    BaseVariantGroupState.$fields.find((f) => f.name === "variantGroup"),
    "VariantGroupState has no field variantGroup",
  );
  /* Const,WeakRef */
get variantGroup(): ComponentVariantGroup { meta.reportRead(this, "variantGroup"); return (this as any).$v.variantGroup; }
private set variantGroup(value: ComponentVariantGroup) { meta.writeField(this, BaseVariantGroupState.$field_variantGroup, value); }
}
class ClsVariantGroupState extends BaseVariantGroupState {
  constructor(args: VariantGroupStateParams) {
    super();
    meta.initializers.VariantGroupState(this, args);
  }
  get typeTag(): "VariantGroupState" {
        return "VariantGroupState";
      }
}
export const VariantGroupState = ClsVariantGroupState;

type KnownSplit = ClsSplit;
export function isKnownSplit<T>(x: [Extract<T, Split>] extends [never] ? never : T): x is [Extract<T, Split>] extends [never]
  ? never
  : Split extends T // Needed when T is any
  ? Split
  : Extract<T, Split> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSplit;
}

    export function ensureKnownSplit<T>(x: [Extract<T, Split>] extends [never] ? never : T): Split {
      assert(isKnownSplit(x), () => mkUnexpectedTypeMsg([Split], x));
      return x;
    }
    export function ensureMaybeKnownSplit<T>(x: [Extract<T, Split>] extends [never] ? never : T): Split | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSplit(x as any);
    }
    
export type Split = KnownSplit;
export interface SplitParams {
  readonly uuid: string /* Const */;
name: string /*  */;
splitType: "experiment" | "segment" | "schedule" /*  */;
slices: ReadonlyArray<SplitSlice> /*  */;
status: "new" | "running" | "stopped" /*  */;
targetEvents: ReadonlyArray<string> /*  */;
description: string | null | undefined /*  */;
externalId: string | null | undefined /*  */;
}

abstract class BaseSplit extends ModelInst {
  
    static isKnown(x: any): x is Split { return isKnownSplit(x); }
    static getType(): Split { throw new Error(); }
    static modelTypeName = "Split";
    
  static readonly $fields = meta.allFields(meta.clsByName["Split"]);
  static readonly $field_uuid: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "uuid"),
    "Split has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "name"),
    "Split has no field name",
  );
static readonly $field_splitType: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "splitType"),
    "Split has no field splitType",
  );
static readonly $field_slices: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "slices"),
    "Split has no field slices",
  );
static readonly $field_status: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "status"),
    "Split has no field status",
  );
static readonly $field_targetEvents: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "targetEvents"),
    "Split has no field targetEvents",
  );
static readonly $field_description: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "description"),
    "Split has no field description",
  );
static readonly $field_externalId: Field = ensure(
    BaseSplit.$fields.find((f) => f.name === "externalId"),
    "Split has no field externalId",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseSplit.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseSplit.$field_name, value); }
get splitType(): "experiment" | "segment" | "schedule" { meta.reportRead(this, "splitType"); return (this as any).$v.splitType; }
set splitType(value: "experiment" | "segment" | "schedule") { meta.writeField(this, BaseSplit.$field_splitType, value); }
get slices(): ReadonlyArray<SplitSlice> { meta.reportRead(this, "slices"); return (this as any).$v.slices; }
set slices(value: ReadonlyArray<SplitSlice>) { meta.writeField(this, BaseSplit.$field_slices, value); }
get status(): "new" | "running" | "stopped" { meta.reportRead(this, "status"); return (this as any).$v.status; }
set status(value: "new" | "running" | "stopped") { meta.writeField(this, BaseSplit.$field_status, value); }
get targetEvents(): ReadonlyArray<string> { meta.reportRead(this, "targetEvents"); return (this as any).$v.targetEvents; }
set targetEvents(value: ReadonlyArray<string>) { meta.writeField(this, BaseSplit.$field_targetEvents, value); }
get description(): string | null | undefined { meta.reportRead(this, "description"); return (this as any).$v.description; }
set description(value: string | null | undefined) { meta.writeField(this, BaseSplit.$field_description, value); }
get externalId(): string | null | undefined { meta.reportRead(this, "externalId"); return (this as any).$v.externalId; }
set externalId(value: string | null | undefined) { meta.writeField(this, BaseSplit.$field_externalId, value); }
}
class ClsSplit extends BaseSplit {
  constructor(args: SplitParams) {
    super();
    meta.initializers.Split(this, args);
  }
  get typeTag(): "Split" {
        return "Split";
      }
}
export const Split = ClsSplit;

type KnownSplitSlice = KnownRandomSplitSlice | KnownSegmentSplitSlice;
export function isKnownSplitSlice<T>(x: [Extract<T, SplitSlice>] extends [never] ? never : T): x is [Extract<T, SplitSlice>] extends [never]
  ? never
  : SplitSlice extends T // Needed when T is any
  ? SplitSlice
  : Extract<T, SplitSlice> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSplitSlice;
}

    export function ensureKnownSplitSlice<T>(x: [Extract<T, SplitSlice>] extends [never] ? never : T): SplitSlice {
      assert(isKnownSplitSlice(x), () => mkUnexpectedTypeMsg([SplitSlice], x));
      return x;
    }
    export function ensureMaybeKnownSplitSlice<T>(x: [Extract<T, SplitSlice>] extends [never] ? never : T): SplitSlice | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSplitSlice(x as any);
    }
    
export type SplitSlice = KnownSplitSlice;
export interface SplitSliceParams {
  readonly uuid: string /* Const */;
name: string /*  */;
externalId: string | null | undefined /*  */;
contents: ReadonlyArray<SplitContent> /*  */;
}

abstract class BaseSplitSlice extends ModelInst {
  
    static isKnown(x: any): x is SplitSlice { return isKnownSplitSlice(x); }
    static getType(): SplitSlice { throw new Error(); }
    static modelTypeName = "SplitSlice";
    
  static readonly $fields = meta.allFields(meta.clsByName["SplitSlice"]);
  static readonly $field_uuid: Field = ensure(
    BaseSplitSlice.$fields.find((f) => f.name === "uuid"),
    "SplitSlice has no field uuid",
  );
static readonly $field_name: Field = ensure(
    BaseSplitSlice.$fields.find((f) => f.name === "name"),
    "SplitSlice has no field name",
  );
static readonly $field_externalId: Field = ensure(
    BaseSplitSlice.$fields.find((f) => f.name === "externalId"),
    "SplitSlice has no field externalId",
  );
static readonly $field_contents: Field = ensure(
    BaseSplitSlice.$fields.find((f) => f.name === "contents"),
    "SplitSlice has no field contents",
  );
  /* Const */
get uuid(): string { meta.reportRead(this, "uuid"); return (this as any).$v.uuid; }
private set uuid(value: string) { meta.writeField(this, BaseSplitSlice.$field_uuid, value); }
get name(): string { meta.reportRead(this, "name"); return (this as any).$v.name; }
set name(value: string) { meta.writeField(this, BaseSplitSlice.$field_name, value); }
get externalId(): string | null | undefined { meta.reportRead(this, "externalId"); return (this as any).$v.externalId; }
set externalId(value: string | null | undefined) { meta.writeField(this, BaseSplitSlice.$field_externalId, value); }
get contents(): ReadonlyArray<SplitContent> { meta.reportRead(this, "contents"); return (this as any).$v.contents; }
set contents(value: ReadonlyArray<SplitContent>) { meta.writeField(this, BaseSplitSlice.$field_contents, value); }
}

export const SplitSlice = BaseSplitSlice;

type KnownRandomSplitSlice = ClsRandomSplitSlice;
export function isKnownRandomSplitSlice<T>(x: [Extract<T, RandomSplitSlice>] extends [never] ? never : T): x is [Extract<T, RandomSplitSlice>] extends [never]
  ? never
  : RandomSplitSlice extends T // Needed when T is any
  ? RandomSplitSlice
  : Extract<T, RandomSplitSlice> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseRandomSplitSlice;
}

    export function ensureKnownRandomSplitSlice<T>(x: [Extract<T, RandomSplitSlice>] extends [never] ? never : T): RandomSplitSlice {
      assert(isKnownRandomSplitSlice(x), () => mkUnexpectedTypeMsg([RandomSplitSlice], x));
      return x;
    }
    export function ensureMaybeKnownRandomSplitSlice<T>(x: [Extract<T, RandomSplitSlice>] extends [never] ? never : T): RandomSplitSlice | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownRandomSplitSlice(x as any);
    }
    
export type RandomSplitSlice = KnownRandomSplitSlice;
export interface RandomSplitSliceParams {
  prob: number /*  */;
readonly uuid: string /* Const */;
name: string /*  */;
externalId: string | null | undefined /*  */;
contents: ReadonlyArray<SplitContent> /*  */;
}

abstract class BaseRandomSplitSlice extends BaseSplitSlice {
  
    static isKnown(x: any): x is RandomSplitSlice { return isKnownRandomSplitSlice(x); }
    static getType(): RandomSplitSlice { throw new Error(); }
    static modelTypeName = "RandomSplitSlice";
    
  static readonly $fields = meta.allFields(meta.clsByName["RandomSplitSlice"]);
  static readonly $field_prob: Field = ensure(
    BaseRandomSplitSlice.$fields.find((f) => f.name === "prob"),
    "RandomSplitSlice has no field prob",
  );
  get prob(): number { meta.reportRead(this, "prob"); return (this as any).$v.prob; }
set prob(value: number) { meta.writeField(this, BaseRandomSplitSlice.$field_prob, value); }
}
class ClsRandomSplitSlice extends BaseRandomSplitSlice {
  constructor(args: RandomSplitSliceParams) {
    super();
    meta.initializers.RandomSplitSlice(this, args);
  }
  get typeTag(): "RandomSplitSlice" {
        return "RandomSplitSlice";
      }
}
export const RandomSplitSlice = ClsRandomSplitSlice;

type KnownSegmentSplitSlice = ClsSegmentSplitSlice;
export function isKnownSegmentSplitSlice<T>(x: [Extract<T, SegmentSplitSlice>] extends [never] ? never : T): x is [Extract<T, SegmentSplitSlice>] extends [never]
  ? never
  : SegmentSplitSlice extends T // Needed when T is any
  ? SegmentSplitSlice
  : Extract<T, SegmentSplitSlice> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSegmentSplitSlice;
}

    export function ensureKnownSegmentSplitSlice<T>(x: [Extract<T, SegmentSplitSlice>] extends [never] ? never : T): SegmentSplitSlice {
      assert(isKnownSegmentSplitSlice(x), () => mkUnexpectedTypeMsg([SegmentSplitSlice], x));
      return x;
    }
    export function ensureMaybeKnownSegmentSplitSlice<T>(x: [Extract<T, SegmentSplitSlice>] extends [never] ? never : T): SegmentSplitSlice | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSegmentSplitSlice(x as any);
    }
    
export type SegmentSplitSlice = KnownSegmentSplitSlice;
export interface SegmentSplitSliceParams {
  cond: string /*  */;
readonly uuid: string /* Const */;
name: string /*  */;
externalId: string | null | undefined /*  */;
contents: ReadonlyArray<SplitContent> /*  */;
}

abstract class BaseSegmentSplitSlice extends BaseSplitSlice {
  
    static isKnown(x: any): x is SegmentSplitSlice { return isKnownSegmentSplitSlice(x); }
    static getType(): SegmentSplitSlice { throw new Error(); }
    static modelTypeName = "SegmentSplitSlice";
    
  static readonly $fields = meta.allFields(meta.clsByName["SegmentSplitSlice"]);
  static readonly $field_cond: Field = ensure(
    BaseSegmentSplitSlice.$fields.find((f) => f.name === "cond"),
    "SegmentSplitSlice has no field cond",
  );
  get cond(): string { meta.reportRead(this, "cond"); return (this as any).$v.cond; }
set cond(value: string) { meta.writeField(this, BaseSegmentSplitSlice.$field_cond, value); }
}
class ClsSegmentSplitSlice extends BaseSegmentSplitSlice {
  constructor(args: SegmentSplitSliceParams) {
    super();
    meta.initializers.SegmentSplitSlice(this, args);
  }
  get typeTag(): "SegmentSplitSlice" {
        return "SegmentSplitSlice";
      }
}
export const SegmentSplitSlice = ClsSegmentSplitSlice;

type KnownSplitContent = KnownGlobalVariantSplitContent | KnownComponentVariantSplitContent | KnownComponentSwapSplitContent;
export function isKnownSplitContent<T>(x: [Extract<T, SplitContent>] extends [never] ? never : T): x is [Extract<T, SplitContent>] extends [never]
  ? never
  : SplitContent extends T // Needed when T is any
  ? SplitContent
  : Extract<T, SplitContent> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseSplitContent;
}

    export function ensureKnownSplitContent<T>(x: [Extract<T, SplitContent>] extends [never] ? never : T): SplitContent {
      assert(isKnownSplitContent(x), () => mkUnexpectedTypeMsg([SplitContent], x));
      return x;
    }
    export function ensureMaybeKnownSplitContent<T>(x: [Extract<T, SplitContent>] extends [never] ? never : T): SplitContent | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownSplitContent(x as any);
    }
    
export type SplitContent = KnownSplitContent;
export interface SplitContentParams {
  
}

abstract class BaseSplitContent extends ModelInst {
  
    static isKnown(x: any): x is SplitContent { return isKnownSplitContent(x); }
    static getType(): SplitContent { throw new Error(); }
    static modelTypeName = "SplitContent";
    
  static readonly $fields = meta.allFields(meta.clsByName["SplitContent"]);
  
  
}

export const SplitContent = BaseSplitContent;

type KnownGlobalVariantSplitContent = ClsGlobalVariantSplitContent;
export function isKnownGlobalVariantSplitContent<T>(x: [Extract<T, GlobalVariantSplitContent>] extends [never] ? never : T): x is [Extract<T, GlobalVariantSplitContent>] extends [never]
  ? never
  : GlobalVariantSplitContent extends T // Needed when T is any
  ? GlobalVariantSplitContent
  : Extract<T, GlobalVariantSplitContent> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseGlobalVariantSplitContent;
}

    export function ensureKnownGlobalVariantSplitContent<T>(x: [Extract<T, GlobalVariantSplitContent>] extends [never] ? never : T): GlobalVariantSplitContent {
      assert(isKnownGlobalVariantSplitContent(x), () => mkUnexpectedTypeMsg([GlobalVariantSplitContent], x));
      return x;
    }
    export function ensureMaybeKnownGlobalVariantSplitContent<T>(x: [Extract<T, GlobalVariantSplitContent>] extends [never] ? never : T): GlobalVariantSplitContent | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownGlobalVariantSplitContent(x as any);
    }
    
export type GlobalVariantSplitContent = KnownGlobalVariantSplitContent;
export interface GlobalVariantSplitContentParams {
  group: GlobalVariantGroup /* WeakRef */;
variant: Variant /* WeakRef */;
}

abstract class BaseGlobalVariantSplitContent extends BaseSplitContent {
  
    static isKnown(x: any): x is GlobalVariantSplitContent { return isKnownGlobalVariantSplitContent(x); }
    static getType(): GlobalVariantSplitContent { throw new Error(); }
    static modelTypeName = "GlobalVariantSplitContent";
    
  static readonly $fields = meta.allFields(meta.clsByName["GlobalVariantSplitContent"]);
  static readonly $field_group: Field = ensure(
    BaseGlobalVariantSplitContent.$fields.find((f) => f.name === "group"),
    "GlobalVariantSplitContent has no field group",
  );
static readonly $field_variant: Field = ensure(
    BaseGlobalVariantSplitContent.$fields.find((f) => f.name === "variant"),
    "GlobalVariantSplitContent has no field variant",
  );
  /* WeakRef */
get group(): GlobalVariantGroup { meta.reportRead(this, "group"); return (this as any).$v.group; }
set group(value: GlobalVariantGroup) { meta.writeField(this, BaseGlobalVariantSplitContent.$field_group, value); }
/* WeakRef */
get variant(): Variant { meta.reportRead(this, "variant"); return (this as any).$v.variant; }
set variant(value: Variant) { meta.writeField(this, BaseGlobalVariantSplitContent.$field_variant, value); }
}
class ClsGlobalVariantSplitContent extends BaseGlobalVariantSplitContent {
  constructor(args: GlobalVariantSplitContentParams) {
    super();
    meta.initializers.GlobalVariantSplitContent(this, args);
  }
  get typeTag(): "GlobalVariantSplitContent" {
        return "GlobalVariantSplitContent";
      }
}
export const GlobalVariantSplitContent = ClsGlobalVariantSplitContent;

type KnownComponentVariantSplitContent = ClsComponentVariantSplitContent;
export function isKnownComponentVariantSplitContent<T>(x: [Extract<T, ComponentVariantSplitContent>] extends [never] ? never : T): x is [Extract<T, ComponentVariantSplitContent>] extends [never]
  ? never
  : ComponentVariantSplitContent extends T // Needed when T is any
  ? ComponentVariantSplitContent
  : Extract<T, ComponentVariantSplitContent> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentVariantSplitContent;
}

    export function ensureKnownComponentVariantSplitContent<T>(x: [Extract<T, ComponentVariantSplitContent>] extends [never] ? never : T): ComponentVariantSplitContent {
      assert(isKnownComponentVariantSplitContent(x), () => mkUnexpectedTypeMsg([ComponentVariantSplitContent], x));
      return x;
    }
    export function ensureMaybeKnownComponentVariantSplitContent<T>(x: [Extract<T, ComponentVariantSplitContent>] extends [never] ? never : T): ComponentVariantSplitContent | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentVariantSplitContent(x as any);
    }
    
export type ComponentVariantSplitContent = KnownComponentVariantSplitContent;
export interface ComponentVariantSplitContentParams {
  component: Component /* WeakRef */;
group: ComponentVariantGroup /* WeakRef */;
variant: Variant /* WeakRef */;
}

abstract class BaseComponentVariantSplitContent extends BaseSplitContent {
  
    static isKnown(x: any): x is ComponentVariantSplitContent { return isKnownComponentVariantSplitContent(x); }
    static getType(): ComponentVariantSplitContent { throw new Error(); }
    static modelTypeName = "ComponentVariantSplitContent";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentVariantSplitContent"]);
  static readonly $field_component: Field = ensure(
    BaseComponentVariantSplitContent.$fields.find((f) => f.name === "component"),
    "ComponentVariantSplitContent has no field component",
  );
static readonly $field_group: Field = ensure(
    BaseComponentVariantSplitContent.$fields.find((f) => f.name === "group"),
    "ComponentVariantSplitContent has no field group",
  );
static readonly $field_variant: Field = ensure(
    BaseComponentVariantSplitContent.$fields.find((f) => f.name === "variant"),
    "ComponentVariantSplitContent has no field variant",
  );
  /* WeakRef */
get component(): Component { meta.reportRead(this, "component"); return (this as any).$v.component; }
set component(value: Component) { meta.writeField(this, BaseComponentVariantSplitContent.$field_component, value); }
/* WeakRef */
get group(): ComponentVariantGroup { meta.reportRead(this, "group"); return (this as any).$v.group; }
set group(value: ComponentVariantGroup) { meta.writeField(this, BaseComponentVariantSplitContent.$field_group, value); }
/* WeakRef */
get variant(): Variant { meta.reportRead(this, "variant"); return (this as any).$v.variant; }
set variant(value: Variant) { meta.writeField(this, BaseComponentVariantSplitContent.$field_variant, value); }
}
class ClsComponentVariantSplitContent extends BaseComponentVariantSplitContent {
  constructor(args: ComponentVariantSplitContentParams) {
    super();
    meta.initializers.ComponentVariantSplitContent(this, args);
  }
  get typeTag(): "ComponentVariantSplitContent" {
        return "ComponentVariantSplitContent";
      }
}
export const ComponentVariantSplitContent = ClsComponentVariantSplitContent;

type KnownComponentSwapSplitContent = ClsComponentSwapSplitContent;
export function isKnownComponentSwapSplitContent<T>(x: [Extract<T, ComponentSwapSplitContent>] extends [never] ? never : T): x is [Extract<T, ComponentSwapSplitContent>] extends [never]
  ? never
  : ComponentSwapSplitContent extends T // Needed when T is any
  ? ComponentSwapSplitContent
  : Extract<T, ComponentSwapSplitContent> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseComponentSwapSplitContent;
}

    export function ensureKnownComponentSwapSplitContent<T>(x: [Extract<T, ComponentSwapSplitContent>] extends [never] ? never : T): ComponentSwapSplitContent {
      assert(isKnownComponentSwapSplitContent(x), () => mkUnexpectedTypeMsg([ComponentSwapSplitContent], x));
      return x;
    }
    export function ensureMaybeKnownComponentSwapSplitContent<T>(x: [Extract<T, ComponentSwapSplitContent>] extends [never] ? never : T): ComponentSwapSplitContent | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownComponentSwapSplitContent(x as any);
    }
    
export type ComponentSwapSplitContent = KnownComponentSwapSplitContent;
export interface ComponentSwapSplitContentParams {
  fromComponent: Component /* WeakRef */;
toComponent: Component /* WeakRef */;
}

abstract class BaseComponentSwapSplitContent extends BaseSplitContent {
  
    static isKnown(x: any): x is ComponentSwapSplitContent { return isKnownComponentSwapSplitContent(x); }
    static getType(): ComponentSwapSplitContent { throw new Error(); }
    static modelTypeName = "ComponentSwapSplitContent";
    
  static readonly $fields = meta.allFields(meta.clsByName["ComponentSwapSplitContent"]);
  static readonly $field_fromComponent: Field = ensure(
    BaseComponentSwapSplitContent.$fields.find((f) => f.name === "fromComponent"),
    "ComponentSwapSplitContent has no field fromComponent",
  );
static readonly $field_toComponent: Field = ensure(
    BaseComponentSwapSplitContent.$fields.find((f) => f.name === "toComponent"),
    "ComponentSwapSplitContent has no field toComponent",
  );
  /* WeakRef */
get fromComponent(): Component { meta.reportRead(this, "fromComponent"); return (this as any).$v.fromComponent; }
set fromComponent(value: Component) { meta.writeField(this, BaseComponentSwapSplitContent.$field_fromComponent, value); }
/* WeakRef */
get toComponent(): Component { meta.reportRead(this, "toComponent"); return (this as any).$v.toComponent; }
set toComponent(value: Component) { meta.writeField(this, BaseComponentSwapSplitContent.$field_toComponent, value); }
}
class ClsComponentSwapSplitContent extends BaseComponentSwapSplitContent {
  constructor(args: ComponentSwapSplitContentParams) {
    super();
    meta.initializers.ComponentSwapSplitContent(this, args);
  }
  get typeTag(): "ComponentSwapSplitContent" {
        return "ComponentSwapSplitContent";
      }
}
export const ComponentSwapSplitContent = ClsComponentSwapSplitContent;

type KnownFigmaComponentMapping = ClsFigmaComponentMapping;
export function isKnownFigmaComponentMapping<T>(x: [Extract<T, FigmaComponentMapping>] extends [never] ? never : T): x is [Extract<T, FigmaComponentMapping>] extends [never]
  ? never
  : FigmaComponentMapping extends T // Needed when T is any
  ? FigmaComponentMapping
  : Extract<T, FigmaComponentMapping> {
  // eslint-disable-next-line no-restricted-syntax
  return x instanceof BaseFigmaComponentMapping;
}

    export function ensureKnownFigmaComponentMapping<T>(x: [Extract<T, FigmaComponentMapping>] extends [never] ? never : T): FigmaComponentMapping {
      assert(isKnownFigmaComponentMapping(x), () => mkUnexpectedTypeMsg([FigmaComponentMapping], x));
      return x;
    }
    export function ensureMaybeKnownFigmaComponentMapping<T>(x: [Extract<T, FigmaComponentMapping>] extends [never] ? never : T): FigmaComponentMapping | undefined | null {
      if (x===undefined) {
        return undefined;
      }
      if (x===null) {
        return null;
      }
      return ensureKnownFigmaComponentMapping(x as any);
    }
    
export type FigmaComponentMapping = KnownFigmaComponentMapping;
export interface FigmaComponentMappingParams {
  figmaComponentName: string /*  */;
}

abstract class BaseFigmaComponentMapping extends ModelInst {
  
    static isKnown(x: any): x is FigmaComponentMapping { return isKnownFigmaComponentMapping(x); }
    static getType(): FigmaComponentMapping { throw new Error(); }
    static modelTypeName = "FigmaComponentMapping";
    
  static readonly $fields = meta.allFields(meta.clsByName["FigmaComponentMapping"]);
  static readonly $field_figmaComponentName: Field = ensure(
    BaseFigmaComponentMapping.$fields.find((f) => f.name === "figmaComponentName"),
    "FigmaComponentMapping has no field figmaComponentName",
  );
  get figmaComponentName(): string { meta.reportRead(this, "figmaComponentName"); return (this as any).$v.figmaComponentName; }
set figmaComponentName(value: string) { meta.writeField(this, BaseFigmaComponentMapping.$field_figmaComponentName, value); }
}
class ClsFigmaComponentMapping extends BaseFigmaComponentMapping {
  constructor(args: FigmaComponentMappingParams) {
    super();
    meta.initializers.FigmaComponentMapping(this, args);
  }
  get typeTag(): "FigmaComponentMapping" {
        return "FigmaComponentMapping";
      }
}
export const FigmaComponentMapping = ClsFigmaComponentMapping;
export type Val = string | number | {};
export const justClasses = { Type, PrimitiveType, Scalar, Num, Text, BoolType, AnyType, Choice, MultiChoice, Img, ComponentInstance, PlumeInstance, RenderableType, HrefType, TargetType, RenderFuncType, QueryData, DateString, DateRangeStrings, FunctionType, ArgType, StylePropType, ClassNamePropType, StyleScopeClassNamePropType, DefaultStylesClassNamePropType, DefaultStylesPropType, ColorPropType, VariantedValue, StyleTokenOverride, Token, StyleToken, DataToken, HostLessPackageInfo, Site, ArenaFrameGrid, ArenaFrameRow, ArenaFrameCell, ComponentArena, PageArena, Arena, ArenaChild, ArenaFrame, CustomFunction, CodeLibrary, StyleNode, RuleSet, Rule, VariantedRuleSet, Mixin, KeyFrame, AnimationSequence, Animation, Theme, ThemeStyle, ThemeLayoutSettings, ProjectDependency, ImageAsset, TplNode, TplTag, TplComponent, TplSlot, ColumnsSetting, PageMeta, ComponentDataQuery, ComponentServerQuery, CodeComponentHelper, CodeComponentVariantMeta, CodeComponentMeta, Component, NameArg, PlumeInfo, ComponentTemplateInfo, Variant, VariantGroup, GlobalVariantGroup, ComponentVariantGroup, VariantSetting, Interaction, ColumnsConfig, Marker, StyleMarker, NodeMarker, RichText, RawText, ExprText, Var, BindingStruct, Rep, Param, SlotParam, StateParam, GlobalVariantGroupParam, StateChangeHandlerParam, PropParam, Arg, Expr, RenderExpr, VirtualRenderExpr, CustomCode, DataSourceOpExpr, CustomFunctionExpr, VarRef, TplRef, StyleTokenRef, ImageAssetRef, PageHref, VariantsRef, ObjectPath, EventHandler, GenericEventHandler, FunctionArg, StrongFunctionArg, CollectionExpr, MapExpr, StyleExpr, TemplatedString, FunctionExpr, QueryInvalidationExpr, CompositeExpr, SelectorRuleSet, LabeledSelector, DataSourceTemplate, QueryRef, State, NamedState, VariantGroupState, Split, SplitSlice, RandomSplitSlice, SegmentSplitSlice, SplitContent, GlobalVariantSplitContent, ComponentVariantSplitContent, ComponentSwapSplitContent, FigmaComponentMapping } as const;
export type ObjInst = Type | PrimitiveType | Scalar | Num | Text | BoolType | AnyType | Choice | MultiChoice | Img | ComponentInstance | PlumeInstance | RenderableType | HrefType | TargetType | RenderFuncType | QueryData | DateString | DateRangeStrings | FunctionType | ArgType | StylePropType | ClassNamePropType | StyleScopeClassNamePropType | DefaultStylesClassNamePropType | DefaultStylesPropType | ColorPropType | VariantedValue | StyleTokenOverride | Token | StyleToken | DataToken | HostLessPackageInfo | Site | ArenaFrameGrid | ArenaFrameRow | ArenaFrameCell | ComponentArena | PageArena | Arena | ArenaChild | ArenaFrame | CustomFunction | CodeLibrary | StyleNode | RuleSet | Rule | VariantedRuleSet | Mixin | KeyFrame | AnimationSequence | Animation | Theme | ThemeStyle | ThemeLayoutSettings | ProjectDependency | ImageAsset | TplNode | TplTag | TplComponent | TplSlot | ColumnsSetting | PageMeta | ComponentDataQuery | ComponentServerQuery | CodeComponentHelper | CodeComponentVariantMeta | CodeComponentMeta | Component | NameArg | PlumeInfo | ComponentTemplateInfo | Variant | VariantGroup | GlobalVariantGroup | ComponentVariantGroup | VariantSetting | Interaction | ColumnsConfig | Marker | StyleMarker | NodeMarker | RichText | RawText | ExprText | Var | BindingStruct | Rep | Param | SlotParam | StateParam | GlobalVariantGroupParam | StateChangeHandlerParam | PropParam | Arg | Expr | RenderExpr | VirtualRenderExpr | CustomCode | DataSourceOpExpr | CustomFunctionExpr | VarRef | TplRef | StyleTokenRef | ImageAssetRef | PageHref | VariantsRef | ObjectPath | EventHandler | GenericEventHandler | FunctionArg | StrongFunctionArg | CollectionExpr | MapExpr | StyleExpr | TemplatedString | FunctionExpr | QueryInvalidationExpr | CompositeExpr | SelectorRuleSet | LabeledSelector | DataSourceTemplate | QueryRef | State | NamedState | VariantGroupState | Split | SplitSlice | RandomSplitSlice | SegmentSplitSlice | SplitContent | GlobalVariantSplitContent | ComponentVariantSplitContent | ComponentSwapSplitContent | FigmaComponentMapping;
